import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { ArrowLeft, Sparkles, Edit3, Package, X, Search, Loader2, Wand2, Upload, Image as ImageIcon, Layers, Gem, Scissors, FileImage, Plus, Trash2, Shirt, Maximize2, RotateCcw, Grid3x3, Palette, Check, ChevronLeft, ChevronRight, Scale, Film, Play, FileText, Copy, ZoomIn, ZoomOut } from 'lucide-react';
import PatternResizerModal from './PatternResizerModal';
import ReapplyFabricReviewModal from './ReapplyFabricReviewModal';
import { useBackButton } from '../hooks/useBackButton';
import { getKits, createKit, magicEdit as magicEditApi, magicEditV2 as magicEditV2Api, resizeFabricPattern as resizeFabricPatternApi, enhanceFabricTexture as enhanceFabricTextureApi, applyPartialDesignInspiration as applyPartialDesignInspirationApi, applyPartialSleeveInspiration as applyPartialSleeveInspirationApi, applyPartialSilhouetteInspiration as applyPartialSilhouetteInspirationApi, generateBackSideView as generateBackSideViewApi, API_BASE_URL } from '../api';
import { getProducts, semanticSearchProducts, getProductById } from '../api/product.api';
import { updateKit as updateKitApi } from '../api/kit.api';
import { applyFabricToGarment, generateVeoVideo } from '../api/realtime.api';
import { getInspirationalDesignById } from '../api/inspirationalDesign.api';
import { uploadImageWithFallback } from '../utils/s3Upload';
import { toThumbnailUrl } from '../utils/imageThumbnailUrl';
import FashDeckStudioTour from './FashDeckStudioTour';
import { getCustomizationForType, getReferenceAsset } from './MultiProductTypeSelector';
import { toAbsolutePublicAssetUrl } from '../utils/assetUrl';
import { appendCappedUrls } from '../utils/objectUrl';
import TechPackPanel from './techPack/TechPackPanel';
import FashdeckPlanWatermark from './FashdeckPlanWatermark';
import { isPaidPlanActive } from '../constants/runningPlans';

// Design option labels for prompt building
const DESIGN_LABELS = {
  neckline: {
    'v-neck': 'V-Neck',
    square: 'Square neckline',
    sweetheart: 'Sweetheart neckline',
    'semi-sweetheart': 'Semi-Sweetheart neckline',
    'high-neck': 'High-neck',
    scoop: 'Scoop neckline',
    'straight-across': 'Straight across neckline',
    'off-shoulder': 'Off-shoulder neckline',
    halter: 'Halter neckline',
    'halter-strap': 'Halter strap neckline',
    'spaghetti-strap': 'Spaghetti strap neckline',
    jewel: 'Jewel neckline',
    keyhole: 'Keyhole neckline',
    'queen-anne': 'Queen Anne neckline',
    sabrina: 'Sabrina neckline',
    grecian: 'Grecian neckline',
    illusion: 'Illusion neckline',
    'single-shoulder-asymmetric': 'Single shoulder asymmetric neckline'
  },
  sleeve: {
    cap: 'Cap sleeves',
    puff: 'Puff sleeves',
    full: 'Full sleeves',
    sleeveless: 'Sleeveless'
  },
  silhouette: {
    'a-line': 'A-line silhouette',
    straight: 'Straight silhouette',
    flared: 'Flared silhouette'
  },
  garmentLength: {
    'slightly-shorter': 'a slightly shorter hemline and overall garment length (hem raised a little)',
    'much-shorter': 'a noticeably shorter hemline and overall garment length (hem raised significantly)',
    'slightly-longer': 'a slightly longer hemline and overall garment length (hem extended a little)',
    'much-longer': 'a noticeably longer hemline and overall garment length (hem extended significantly)'
  }
};

const GARMENT_LENGTH_DISPLAY = {
  'slightly-shorter': 'Slightly shorter',
  'much-shorter': 'Much shorter',
  'slightly-longer': 'Slightly longer',
  'much-longer': 'Much longer'
};

/** Legacy Studio neckline tab (id + icon path) — shared with magic-edit reference lookup. */
const STUDIO_LEGACY_NECKLINE_OPTIONS = [
  { id: 'v-neck', label: 'V-Neck', icon: '/assets/neckicons/v-neck.png' },
  { id: 'square', label: 'Square', icon: '/assets/neckicons/square.png' },
  { id: 'sweetheart', label: 'Sweetheart', icon: '/assets/neckicons/sweetheart.png' },
  { id: 'semi-sweetheart', label: 'Semi-Sweetheart', icon: '/assets/neckicons/Semi-sweetheart.png' },
  { id: 'high-neck', label: 'High Neck', icon: '/assets/neckicons/high neck.png' },
  { id: 'scoop', label: 'Scoop', icon: '/assets/neckicons/scoop.png' },
  { id: 'straight-across', label: 'Straight Across', icon: '/assets/neckicons/Straight across.png' },
  { id: 'off-shoulder', label: 'Off-Shoulder', icon: '/assets/neckicons/Off-shoulder.png' },
  { id: 'halter', label: 'Halter', icon: '/assets/neckicons/halter.png' },
  { id: 'halter-strap', label: 'Halter Strap', icon: '/assets/neckicons/halter strap.png' },
  { id: 'spaghetti-strap', label: 'Spaghetti Strap', icon: '/assets/neckicons/sphagetti strap.png' },
  { id: 'jewel', label: 'Jewel', icon: '/assets/neckicons/jewel.png' },
  { id: 'keyhole', label: 'Keyhole', icon: '/assets/neckicons/keyhole.png' },
  { id: 'queen-anne', label: 'Queen Anne', icon: '/assets/neckicons/queen anne.png' },
  { id: 'sabrina', label: 'Sabrina', icon: '/assets/neckicons/sabrina.png' },
  { id: 'grecian', label: 'Grecian', icon: '/assets/neckicons/grecian.png' },
  { id: 'illusion', label: 'Illusion', icon: '/assets/neckicons/illusion.png' },
  { id: 'single-shoulder-asymmetric', label: 'Single Shoulder Asymmetric', icon: '/assets/neckicons/single shoulder asymmetric.png' },
];

// Build a simple prompt for the specific style option that was clicked
const buildMagicEditPrompt = ({ lastClickedStyle, designSettings, productType = '', productCategory = '' }) => {
  if (!lastClickedStyle || !lastClickedStyle.type || !lastClickedStyle.value) {
    return 'Improve the garment design aesthetics while keeping it fashion-forward and high-quality.';
  }

  const { type, value } = lastClickedStyle;
  
  // Check if there's a custom prompt for this type
  const customPromptKey = `${type}Prompt`;
  if (designSettings && designSettings[customPromptKey] && designSettings[customPromptKey].trim()) {
    return designSettings[customPromptKey].trim();
  }

  // Fall back to auto-generated prompt
  let styleLabel = '';

  if (type === 'neckline' && DESIGN_LABELS.neckline[value]) {
    styleLabel = DESIGN_LABELS.neckline[value];
  } else if (type === 'sleeve' && DESIGN_LABELS.sleeve[value]) {
    styleLabel = DESIGN_LABELS.sleeve[value];
  } else if (type === 'silhouette' && DESIGN_LABELS.silhouette[value]) {
    styleLabel = DESIGN_LABELS.silhouette[value];
  } else if (type === 'garmentLength' && DESIGN_LABELS.garmentLength[value]) {
    styleLabel = DESIGN_LABELS.garmentLength[value];
  }

  // Studio "Generate page-like" mens customisations store user-facing labels as `value`
  // (e.g. neck: "Chinese collar", sleeve: "Cuffed sleeve", frontPanel: "Angrakha style").
  if (!styleLabel && typeof value === 'string' && value.trim()) {
    styleLabel = value.trim();
  }

  if (!styleLabel) {
    return 'Improve the garment design aesthetics while keeping it fashion-forward and high-quality.';
  }

  if (type === 'garmentLength') {
    return `Adjust the garment to have ${styleLabel}. Preserve fabric, print, colors, neckline, sleeves, and embellishments; change only the garment's vertical length and hem proportion in a natural, realistic way.`;
  }

  // Keep prompts intentionally simple for studio UX.
  const partLabel =
    type === 'neck' ? 'neckline' :
    type === 'frontPanel' ? 'front panel' :
    type === 'collar' ? 'collar' :
    type === 'outerShell' ? 'outer shell' :
    type === 'placket' ? 'placket' :
    type === 'cuff' ? 'cuffs' :
    type === 'yoke' ? 'yoke' :
    type === 'neckline' ? 'neckline' :
    type === 'sleeve' ? 'sleeve' :
    type === 'silhouette' ? 'silhouette' :
    type === 'traditional' ? 'traditional waistcoat' :
    type === 'nehruJacket' ? 'Nehru jacket waistcoat' :
    type === 'westernTuxedo' ? 'western waistcoat' :
    type === 'waist' ? (productCategory === 'men-clothing' ? 'waistband' : 'waist') :
    type === 'front' ? 'front construction' :
    type === 'legStructure' ? 'leg silhouette' :
    type === 'rise' ? 'rise' :
    type === 'lengthPants' ? 'length (pants)' :
    type === 'lengthShorts' ? 'length (shorts)' :
    type === 'lengthBoyShort' ? 'length (boy short)' :
    type === 'pocketFront' ? 'front pocket style' :
    type === 'pocketBack' ? 'back pocket style' :
    type === 'necklineFront' ? 'front neckline' :
    type === 'necklineBack' ? 'back neckline' :
    type === 'bodice' ? 'bodice' :
    type === 'bodyCutOut' ? 'body cut-out' :
    type === 'waistAttachment' ? 'waist attachment' :
    type === 'waistPlacement' ? 'waist placement' :
    type === 'shoulderDetail' ? 'shoulder detail' :
    type === 'surfaceVolume' ? 'surface and volume' :
    type === 'slitVariationLength' ? 'slit length' :
    type === 'slitVariationTypes' ? 'slit type' :
    type === 'strap' ? 'strap' :
    type === 'skirt' ? 'skirt' :
    type === 'fit' ? 'fit' :
    type === 'hem' ? 'hem' :
    type === 'closure' ? 'closure' :
    type === 'back' ? 'back style' :
    type;

  const cleanStyle = String(styleLabel).trim().toLowerCase();
  return `Apply the ${cleanStyle} ${partLabel}.`;
};

function rgbTupleToHex(rgb) {
  if (!Array.isArray(rgb) || rgb.length < 3) return null;
  const [r, g, b] = rgb.map((n) => Math.max(0, Math.min(255, Math.round(Number(n) || 0))));
  return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
}

function normalizeHex(h) {
  if (h == null || typeof h !== 'string') return null;
  let s = h.trim();
  if (!s) return null;
  if (!s.startsWith('#')) s = `#${s}`;
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toLowerCase();
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`.toLowerCase();
  }
  return null;
}

function normalizeImageUrlForProductMatch(url) {
  if (!url || typeof url !== 'string') return '';
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    return url.split('?')[0];
  }
}

/** Resolve catalogue fabric product id — same signals as moodboard / kit export. */
function resolveStudioProductIdFromKit(kit) {
  if (!kit) return null;
  const candidates = [
    kit.sourceProductId,
    kit.meta?.linkedTo,
    kit.meta?.id,
    kit.productDetails?._id,
    kit.productId,
  ].filter(Boolean);
  for (const c of candidates) {
    const s = typeof c === 'object' && c != null && typeof c.toString === 'function' ? c.toString() : String(c);
    if (/^[a-fA-F0-9]{24}$/.test(s)) return s;
  }
  return null;
}

function resolveFabricImageUrlForApply(fabric) {
  return (
    fabric?.image ||
    fabric?.imageUrl ||
    fabric?.images?.original?.s3Url ||
    fabric?.images?.original?.imgbbUrl ||
    fabric?.images?.original?.url ||
    ''
  );
}

/** Body-cut / garment reference for apply-fabric (Image 1 in inspiration flow). */
async function resolveStudioGarmentUrlForApplyFabric(kit) {
  const fromKit = kit?.productDetails?.inspirationalDesignImage;
  if (fromKit && String(fromKit).trim()) return String(fromKit).trim();

  const designId = kit?.sourceInspirationalDesignId;
  if (designId) {
    try {
      const design = await getInspirationalDesignById(designId);
      if (design?.imageUrl && String(design.imageUrl).trim()) {
        return String(design.imageUrl).trim();
      }
    } catch (err) {
      console.warn('Studio: could not load inspirational design for apply-fabric', err);
    }
  }

  const fallback =
    kit?.generatedImageUrl ||
    kit?.productDetails?.image ||
    kit?.generatedContent?.['Image Posts (Social Media)']?.[0]?.image ||
    '';
  return fallback && String(fallback).trim() ? String(fallback).trim() : '';
}

async function resolveStudioProductIdByImageMatch(kit) {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token || !kit) return null;

  const kitUrls = [
    kit.sourceFabricImage,
    kit.productDetails?.image,
    kit.productDetails?.originalImage,
    kit.sourceImage,
    kit.originalImageUrl,
    kit.fabricImage,
    kit.inputImage,
  ]
    .filter(Boolean)
    .map(normalizeImageUrlForProductMatch)
    .filter(Boolean);

  if (!kitUrls.length) return null;

  try {
    const res = await getProducts(1, 250, '');
    const products = res?.products || res?.items || [];
    for (const p of products) {
      const pId = p?._id || p?.id;
      if (!pId || !/^[a-fA-F0-9]{24}$/.test(String(pId))) continue;
      const productUrls = [
        p?.image,
        p?.images?.original?.s3Url,
        p?.images?.original?.url,
        p?.images?.original?.imgbbUrl,
      ]
        .filter(Boolean)
        .map(normalizeImageUrlForProductMatch);
      const matched = productUrls.some((u) => kitUrls.includes(u));
      if (matched) return String(pId);
    }
  } catch (err) {
    console.warn('Studio: fabric lookup by image match failed', err);
  }
  return null;
}

/** Fabric image, label, and size for studio tools (preview overlay, pattern resizer). */
/** First non-empty string URL, skipping blob: previews (temporary, non-shareable). */
function firstUsableFabricUrl(...values) {
  for (const value of values) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (trimmed && !trimmed.startsWith('blob:')) return trimmed;
  }
  return '';
}

function resolveStudioFabricDisplay(kit, fabricProduct, extras = {}) {
  const selectedProduct = kit?.productDetails?.selectedProduct;
  // Base (non-edited) kits store the source image the garment was generated from
  // at `productDetails.image` — this IS the source fabric — but never a dedicated
  // fabric field and only sometimes a linked product id. So fall back to it
  // directly, guarding against the two cases where it is NOT a fabric:
  //   • magic-edit kits, where productDetails.image is the pre-edit garment (their
  //     real fabric, if any, lives in magicEditData.fabricImageUrl, checked above);
  //   • when it merely duplicates the generated garment / thumbnail image.
  const generatedGarmentUrl = kit?.generatedImageUrl || kit?.thumbnail || '';
  const isMagicEditKit = Boolean(kit?.magicEditData);
  const productSourceImage =
    !isMagicEditKit &&
    kit?.productDetails?.image &&
    kit.productDetails.image !== generatedGarmentUrl
      ? kit.productDetails.image
      : '';
  const fabricSrc = firstUsableFabricUrl(
    // 0. Canonical source fabric persisted on the kit at creation. Every kit
    //    should carry this (base kits from the fabric they were generated from,
    //    derived/magic-edit kits inherited from their parent), so it is the most
    //    reliable signal and takes priority over everything below.
    kit?.sourceFabricImage,
    // 1. Fabric product loaded for this kit (by linked id or image match).
    fabricProduct?.image,
    fabricProduct?.imageUrl,
    fabricProduct?.images?.original?.s3Url,
    fabricProduct?.images?.original?.imgbbUrl,
    fabricProduct?.images?.original?.url,
    // 2. In-session fabric references (annotation / multi-fabric flows).
    extras.annotationFabricImageUrl,
    extras.multiFabricImageUrl,
    // 3. Fabric persisted directly on the kit.
    kit?.fabricImage,
    kit?.fabricImageUrl,
    // 4. Fabric reference stored when the kit was made via magic-edit /
    //    apply-fabric / pattern-resize — this is where most edited kits keep it.
    kit?.magicEditData?.fabricImageUrl,
    kit?.productDetails?.fabricImage,
    kit?.productDetails?.fabricImageUrl,
    // 5. A fabric product embedded on the kit's productDetails.
    selectedProduct?.image,
    selectedProduct?.imageUrl,
    selectedProduct?.images?.original?.s3Url,
    selectedProduct?.images?.original?.imgbbUrl,
    selectedProduct?.images?.original?.url,
    selectedProduct?.fabricImage,
    // 6. Last resort: the source image the kit was generated from.
    productSourceImage
  );

  const fabricName =
    fabricProduct?.name ||
    selectedProduct?.name ||
    'Fabric';

  const fabricSize =
    fabricProduct?.specifications?.dimensions ||
    kit?.productDetails?.selectedProduct?.specifications?.dimensions ||
    fabricProduct?.specifications?.weight ||
    '';

  return {
    fabricSrc: typeof fabricSrc === 'string' ? fabricSrc : '',
    fabricName,
    fabricSize: typeof fabricSize === 'string' ? fabricSize.trim() : '',
  };
}

/**
 * Shade-card swatches: fabric `colorVariants` and variants saved on the kit only (no fabric-analysis text colors).
 */
function getStudioFabricColorSwatches(kit, fabricProduct) {
  const fabric = fabricProduct && typeof fabricProduct === 'object' ? fabricProduct : {};
  const seen = new Set();
  const out = [];

  const pushNamed = (entry) => {
    const hexKey = entry.hex ? entry.hex.toLowerCase() : '';
    const nameKey = (entry.colorName || '').toLowerCase().trim();
    const key = hexKey || nameKey;
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ ...entry, id: `${key}-${out.length}` });
  };

  const tryPushVariantObject = (v, sourceTag) => {
    if (!v || typeof v !== 'object') return;
    const raw = v.hex ?? v.color;
    const strRaw = raw == null ? '' : String(raw).trim();
    const hex = normalizeHex(strRaw) || rgbTupleToHex(v.rgb);
    const name =
      (v.colorName && String(v.colorName).trim()) ||
      (v.name && String(v.name).trim()) ||
      (hex ? `Color ${hex}` : '');
    if (!name && !hex) return;
    pushNamed({
      colorName: name || `Variant ${out.length + 1}`,
      hex,
      rgb: Array.isArray(v.rgb) ? v.rgb : null,
      source: sourceTag,
    });
  };

  const variantLayers = [
    [fabric.colorVariants, 'fabric'],
    [kit?.productDetails?.colorVariants, 'kit-product'],
    [kit?.colorVariants, 'kit'],
  ];
  variantLayers.forEach(([arr, tag]) => {
    if (!Array.isArray(arr)) return;
    arr.forEach((v) => tryPushVariantObject(v, tag));
  });

  return out;
}

function buildRecolorPrompt(swatch) {
  const hex = swatch.hex;
  const name = swatch.colorName || 'target color';
  const rgbStr =
    Array.isArray(swatch.rgb) && swatch.rgb.length >= 3
      ? `RGB(${Math.round(swatch.rgb[0])}, ${Math.round(swatch.rgb[1])}, ${Math.round(swatch.rgb[2])})`
      : null;
  const colorSpec = hex
    ? `exact hex ${hex}${rgbStr ? ` (${rgbStr})` : ''}`
    : rgbStr
      ? rgbStr
      : `the color described as "${name}"`;
  return `Recolor the garment to match ${colorSpec} (${name}). Preserve fabric pattern, print layout, weave, embroidery, sequins, silhouette, and overall design; only shift the garment's dominant fabric color toward this shade in a realistic, photographic way. Do not change the garment type or add new motifs.`;
}

function normalizeStudioHex(value) {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  const short = withHash.match(/^#([0-9a-fA-F]{3})$/);
  if (short) {
    const [r, g, b] = short[1].split('');
    return `#${r}${r}${g}${g}${b}${b}`.toUpperCase();
  }
  const full = withHash.match(/^#([0-9a-fA-F]{6})$/);
  return full ? `#${full[1].toUpperCase()}` : null;
}

function hexToRgbChannels(hex) {
  const normalized = normalizeStudioHex(hex);
  if (!normalized) return { r: 124, g: 58, b: 237 };
  return {
    r: parseInt(normalized.slice(1, 3), 16),
    g: parseInt(normalized.slice(3, 5), 16),
    b: parseInt(normalized.slice(5, 7), 16),
  };
}

function rgbChannelsToHex(r, g, b) {
  const clamp = (n) => Math.max(0, Math.min(255, Math.round(Number(n) || 0)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((n) => n.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

function rgbToHsv(r, g, b) {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  const s = max === 0 ? 0 : d / max;
  return { h, s, v: max };
}

function hsvToRgb(h, s, v) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let rp = 0;
  let gp = 0;
  let bp = 0;
  if (h < 60) [rp, gp, bp] = [c, x, 0];
  else if (h < 120) [rp, gp, bp] = [x, c, 0];
  else if (h < 180) [rp, gp, bp] = [0, c, x];
  else if (h < 240) [rp, gp, bp] = [0, x, c];
  else if (h < 300) [rp, gp, bp] = [x, 0, c];
  else [rp, gp, bp] = [c, 0, x];
  return {
    r: Math.round((rp + m) * 255),
    g: Math.round((gp + m) * 255),
    b: Math.round((bp + m) * 255),
  };
}

const STUDIO_COLOR_PRESETS = [
  '#FFFFFF',
  '#F5F5F4',
  '#000000',
  '#7C3AED',
  '#DB2777',
  '#DC2626',
  '#EA580C',
  '#CA8A04',
  '#16A34A',
  '#0891B2',
  '#2563EB',
  '#7C2D12',
];

function StudioAdvancedColorPicker({
  hex,
  hexInput,
  onHexChange,
  onHexInputChange,
  onApply,
  applying = false,
  disabled = false,
}) {
  const svRef = useRef(null);
  const hueRef = useRef(null);
  const draggingRef = useRef(null);
  const rgb = hexToRgbChannels(hex);
  const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
  const pureHue = hsvToRgb(hsv.h, 1, 1);
  const pureHueHex = rgbChannelsToHex(pureHue.r, pureHue.g, pureHue.b);

  const commitHsv = useCallback(
    (nextHsv) => {
      const nextRgb = hsvToRgb(nextHsv.h, nextHsv.s, nextHsv.v);
      const nextHex = rgbChannelsToHex(nextRgb.r, nextRgb.g, nextRgb.b);
      onHexChange(nextHex);
    },
    [onHexChange]
  );

  const updateFromPointer = useCallback(
    (clientX, clientY, mode) => {
      if (mode === 'sv' && svRef.current) {
        const rect = svRef.current.getBoundingClientRect();
        const s = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        const v = Math.max(0, Math.min(1, 1 - (clientY - rect.top) / rect.height));
        commitHsv({ h: hsv.h, s, v });
      } else if (mode === 'hue' && hueRef.current) {
        const rect = hueRef.current.getBoundingClientRect();
        const h = Math.max(0, Math.min(360, ((clientX - rect.left) / rect.width) * 360));
        commitHsv({ h, s: hsv.s, v: hsv.v });
      }
    },
    [commitHsv, hsv.h, hsv.s, hsv.v]
  );

  useEffect(() => {
    const onMove = (e) => {
      if (!draggingRef.current) return;
      const point = e.touches?.[0] || e;
      updateFromPointer(point.clientX, point.clientY, draggingRef.current);
    };
    const onUp = () => {
      draggingRef.current = null;
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, [updateFromPointer]);

  const startDrag = (mode) => (e) => {
    if (disabled) return;
    e.preventDefault();
    draggingRef.current = mode;
    const point = e.touches?.[0] || e;
    updateFromPointer(point.clientX, point.clientY, mode);
  };

  const setRgbChannel = (channel, value) => {
    const next = { ...rgb, [channel]: Number(value) };
    onHexChange(rgbChannelsToHex(next.r, next.g, next.b));
  };

  return (
    <div className="rounded-xl border border-violet-200/70 bg-white p-3 sm:p-4 shadow-sm">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-1">Custom color</h3>
          <p className="text-xs text-gray-600">
            Pick any shade with the advanced picker. Pattern, print, and silhouette stay locked.
          </p>
        </div>
        <div
          className="h-12 w-12 shrink-0 rounded-xl border border-gray-200 shadow-inner ring-1 ring-black/5"
          style={{ backgroundColor: normalizeStudioHex(hex) || '#7C3AED' }}
          title={normalizeStudioHex(hex) || hex}
          aria-hidden
        />
      </div>

      <div
        ref={svRef}
        role="slider"
        aria-label="Saturation and brightness"
        tabIndex={disabled ? -1 : 0}
        onMouseDown={startDrag('sv')}
        onTouchStart={startDrag('sv')}
        className={`relative mb-3 h-40 w-full touch-none select-none overflow-hidden rounded-xl border border-gray-200 ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-crosshair'
        }`}
        style={{
          background: `
            linear-gradient(to top, #000, transparent),
            linear-gradient(to right, #fff, ${pureHueHex})
          `,
        }}
      >
        <span
          className="pointer-events-none absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-md ring-1 ring-black/20"
          style={{
            left: `${hsv.s * 100}%`,
            top: `${(1 - hsv.v) * 100}%`,
            backgroundColor: normalizeStudioHex(hex) || '#7C3AED',
          }}
        />
      </div>

      <div
        ref={hueRef}
        role="slider"
        aria-label="Hue"
        tabIndex={disabled ? -1 : 0}
        onMouseDown={startDrag('hue')}
        onTouchStart={startDrag('hue')}
        className={`relative mb-4 h-3.5 w-full touch-none select-none rounded-full border border-gray-200 ${
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-ew-resize'
        }`}
        style={{
          background:
            'linear-gradient(to right, #ff0000 0%, #ffff00 17%, #00ff00 33%, #00ffff 50%, #0000ff 67%, #ff00ff 83%, #ff0000 100%)',
        }}
      >
        <span
          className="pointer-events-none absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow ring-1 ring-black/20"
          style={{
            left: `${(hsv.h / 360) * 100}%`,
            backgroundColor: pureHueHex,
          }}
        />
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
        <label className="col-span-2 flex flex-col gap-1 sm:col-span-1">
          <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Hex</span>
          <input
            type="text"
            value={hexInput}
            disabled={disabled}
            onChange={(e) => onHexInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onApply();
              }
            }}
            className="rounded-lg border border-gray-300 px-2.5 py-2 font-mono text-sm uppercase tracking-wide text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
            placeholder="#7C3AED"
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </label>
        {['r', 'g', 'b'].map((channel) => (
          <label key={channel} className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              {channel}
            </span>
            <input
              type="number"
              min={0}
              max={255}
              value={rgb[channel]}
              disabled={disabled}
              onChange={(e) => setRgbChannel(channel, e.target.value)}
              className="rounded-lg border border-gray-300 px-2.5 py-2 font-mono text-sm text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-50"
            />
          </label>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap gap-1.5">
        {STUDIO_COLOR_PRESETS.map((preset) => {
          const active = (normalizeStudioHex(hex) || '').toUpperCase() === preset;
          return (
            <button
              key={preset}
              type="button"
              disabled={disabled}
              title={preset}
              onClick={() => onHexChange(preset)}
              className={`h-7 w-7 rounded-full border shadow-sm transition ${
                active ? 'ring-2 ring-violet-500 ring-offset-1' : 'border-gray-200 hover:scale-105'
              } disabled:opacity-50`}
              style={{ backgroundColor: preset }}
              aria-label={`Use ${preset}`}
            />
          );
        })}
      </div>

      <button
        type="button"
        disabled={disabled}
        onClick={onApply}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-violet-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
      >
        {applying ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Applying…
          </>
        ) : (
          'Apply color'
        )}
      </button>
    </div>
  );
}

// Fabric color chart + recolor (catalogue + kit colorVariants + custom picker)
const FabricColorStudioSection = ({
  kit,
  fabricProduct,
  fabricProductLoading = false,
  showNotification,
  magicEditImages,
  selectedImageIndex,
  setMagicEditImages,
  setSelectedImageIndex,
  setIsMagicEditing,
  setIsMagicEditPreviewLoading,
  embedded = false,
  onMobileApplyStart,
}) => {
  const shadeCardSwatches = useMemo(
    () => getStudioFabricColorSwatches(kit, fabricProduct).filter((s) => Boolean(s.hex)),
    [kit, fabricProduct]
  );
  const [applyingId, setApplyingId] = useState(null);
  const [customHex, setCustomHex] = useState('#7C3AED');
  const [customHexInput, setCustomHexInput] = useState('#7C3AED');
  const shellClass = embedded
    ? 'max-h-[420px] overflow-y-auto'
    : 'p-4 sm:p-6 max-h-[500px] overflow-y-auto';

  const applySwatch = async (swatch) => {
    if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
      showNotification?.('Import a kit with an image first', 'error');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please log in to apply color', 'error');
      return;
    }

    onMobileApplyStart?.();

    setApplyingId(swatch.id);
    setIsMagicEditing?.(true);
    setIsMagicEditPreviewLoading?.(true);

    try {
      const currentMainImage =
        selectedImageIndex === -1
          ? kit?.generatedImageUrl || kit?.productDetails?.image
          : magicEditImages?.[selectedImageIndex] || kit?.generatedImageUrl || kit?.productDetails?.image;

      const response = await magicEditV2Api(
        {
          imageUrl: currentMainImage,
          originalImageUrl: kit?.productDetails?.image || currentMainImage,
          prompt: buildRecolorPrompt(swatch),
          originalKitId: kit._id,
          createNewKit: true,
          aspectRatio: 'match_input_image',
        },
        token
      );

      if (response.data?.success) {
        const editedUrl = response.data.data?.editedImageUrl;
        if (editedUrl) {
          setMagicEditImages?.((prev) => {
            const next = [...(prev || []), editedUrl];
            setSelectedImageIndex?.(next.length - 1);
            return next;
          });
          showNotification?.(`Applied color: ${swatch.colorName}`, 'success');
        } else {
          showNotification?.('Edit finished but no image was returned', 'warning');
        }
      } else {
        showNotification?.(response.data?.message || 'Could not apply color', 'error');
      }
    } catch (err) {
      console.error('Studio recolor error:', err);
      showNotification?.(err.response?.data?.message || 'Could not apply color', 'error');
    } finally {
      setApplyingId(null);
      setIsMagicEditing?.(false);
      setIsMagicEditPreviewLoading?.(false);
    }
  };

  const setCustomColor = (nextHex) => {
    const normalized = normalizeStudioHex(nextHex) || nextHex;
    setCustomHex(normalized);
    setCustomHexInput(normalized);
  };

  const applyCustomColor = () => {
    const hex = normalizeStudioHex(customHexInput) || normalizeStudioHex(customHex);
    if (!hex) {
      showNotification?.('Enter a valid hex color (e.g. #7C3AED)', 'error');
      return;
    }
    setCustomColor(hex);
    applySwatch({
      id: `custom-picker-${hex}`,
      hex,
      colorName: hex,
    });
  };

  const customPickerBusy = applyingId === `custom-picker-${normalizeStudioHex(customHex) || customHex}`;

  return (
    <div className={shellClass}>
      <div className="mb-4">
        <h3 className="mb-1 text-sm font-semibold text-gray-800">Fabric colors</h3>
        <p className="text-xs text-gray-600">
          {shadeCardSwatches.length > 0
            ? 'Pick any custom color, or tap a shade-card swatch to recolor the garment. Pattern and layout stay locked.'
            : 'Pick any custom color to recolor the garment. Pattern and layout stay locked.'}
        </p>
        {fabricProductLoading && (
          <p className="mt-2 flex items-center gap-2 text-xs text-violet-600">
            <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin" />
            Loading full fabric palette…
          </p>
        )}
      </div>

      <div className={shadeCardSwatches.length > 0 ? 'mb-4' : undefined}>
        <StudioAdvancedColorPicker
          hex={customHex}
          hexInput={customHexInput}
          onHexChange={setCustomColor}
          onHexInputChange={(raw) => {
            setCustomHexInput(raw);
            const normalized = normalizeStudioHex(raw);
            if (normalized) setCustomHex(normalized);
          }}
          onApply={applyCustomColor}
          applying={customPickerBusy}
          disabled={Boolean(applyingId)}
        />
      </div>

      {shadeCardSwatches.length > 0 ? (
        <div className="rounded-xl border border-violet-200/50 bg-gradient-to-br from-white via-violet-50/40 to-fuchsia-50/25 p-3 sm:p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2 sm:gap-3">
            <span className="h-px max-w-[2rem] flex-1 bg-gradient-to-r from-transparent to-violet-200/80 sm:max-w-[3rem]" aria-hidden />
            <p className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.14em] text-violet-700 sm:text-[11px] sm:tracking-[0.18em]">
              Extracted palette
            </p>
            <span className="h-px max-w-[2rem] flex-1 bg-gradient-to-l from-transparent to-violet-200/80 sm:max-w-[3rem]" aria-hidden />
          </div>
          <div className="grid gap-2 sm:gap-2.5 [grid-template-columns:repeat(auto-fill,minmax(6.75rem,1fr))] sm:[grid-template-columns:repeat(auto-fill,minmax(7.25rem,1fr))]">
            {shadeCardSwatches.map((swatch) => {
              const bg = swatch.hex;
              const busy = applyingId === swatch.id;
              return (
                <button
                  key={swatch.id}
                  type="button"
                  disabled={Boolean(applyingId)}
                  onClick={() => applySwatch(swatch)}
                  className={`group flex flex-col items-stretch overflow-hidden rounded-xl border border-gray-200/90 bg-white/90 text-left shadow-sm ring-1 ring-black/[0.03] backdrop-blur-[2px] transition-all duration-200 ease-out hover:border-violet-300 hover:shadow-md hover:shadow-violet-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-1 active:scale-[0.98] ${
                    applyingId && !busy ? 'pointer-events-none opacity-40' : ''
                  }`}
                >
                  <div className="relative h-[3.25rem] w-full shrink-0 sm:h-14" style={{ background: bg }}>
                    <span
                      className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-white/15 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                      aria-hidden
                    />
                    {busy && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[1px]">
                        <Loader2 className="h-5 w-5 animate-spin text-white drop-shadow-md sm:h-6 sm:w-6" />
                      </div>
                    )}
                  </div>
                  <div className="flex min-h-0 flex-col gap-0.5 border-t border-violet-100/80 bg-gradient-to-b from-white to-violet-50/30 px-2 py-1.5 sm:px-2.5 sm:py-2">
                    <p
                      className="line-clamp-2 text-[10px] font-semibold leading-tight text-gray-900 [overflow-wrap:anywhere] sm:text-xs"
                      title={swatch.colorName}
                    >
                      {swatch.colorName}
                    </p>
                    <p className="truncate font-mono text-[9px] uppercase tabular-nums tracking-wide text-violet-700/85 select-all sm:text-[10px]">
                      {swatch.hex}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

// Embroidery Motifs Section Component
const EmbroiderySection = ({ items, onAdd, onRemove }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      const newItem = {
        id: Date.now(),
        name: file.name,
        imageUrl: previewUrl,
        file: file,
        position: { x: 50, y: 50 },
        scale: 100,
        opacity: 100
      };
      onAdd(newItem);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Embroidery Motifs</h3>
        <p className="text-xs text-gray-600 mb-4">
          Upload embroidery pattern images or motifs to add to your design
        </p>
      </div>

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-sm font-medium text-purple-700 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            Upload Embroidery Motif
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {items.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-700">Added Motifs ({items.length})</p>
          <div className="grid grid-cols-2 gap-3">
            {items.map((item, index) => (
              <div key={item.id} className="relative group border border-gray-200 rounded-lg overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-full h-24 object-cover"
                />
                <button
                  onClick={() => onRemove(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="p-2 bg-white">
                  <p className="text-xs text-gray-700 truncate">{item.name}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Sequins, Beads, Stones Section Component
const SequinsSection = ({ items, onAdd, onRemove }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [sequinType, setSequinType] = useState('sequins');
  const [color, setColor] = useState('#FFD700');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      const newItem = {
        id: Date.now(),
        name: file.name,
        imageUrl: previewUrl,
        file: file,
        type: sequinType,
        color: color,
        density: 50,
        position: { x: 50, y: 50 }
      };
      onAdd(newItem);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Sequins, Beads & Stones</h3>
        <p className="text-xs text-gray-600 mb-4">
          Add decorative sequins, beads, or stones to enhance your design
        </p>
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">Type</label>
          <select
            value={sequinType}
            onChange={(e) => setSequinType(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="sequins">Sequins</option>
            <option value="beads">Beads</option>
            <option value="stones">Stones</option>
            <option value="mixed">Mixed</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-medium text-gray-700 mb-1 block">Color</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="w-12 h-10 border border-gray-300 rounded-lg cursor-pointer"
            />
            <input
              type="text"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
              placeholder="#FFD700"
            />
          </div>
        </div>
      </div>

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-sm font-medium text-purple-700 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            Upload {sequinType.charAt(0).toUpperCase() + sequinType.slice(1)} Pattern
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {items.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-700">Added Items ({items.length})</p>
          <div className="grid grid-cols-2 gap-3">
            {items.map((item, index) => (
              <div key={item.id} className="relative group border border-gray-200 rounded-lg overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-full h-24 object-cover"
                />
                <button
                  onClick={() => onRemove(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="p-2 bg-white">
                  <p className="text-xs text-gray-700 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Lace and Borders Section Component
const LaceSection = ({ items, onAdd, onRemove }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [laceType, setLaceType] = useState('border');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      const newItem = {
        id: Date.now(),
        name: file.name,
        imageUrl: previewUrl,
        file: file,
        type: laceType,
        position: 'bottom',
        width: 100
      };
      onAdd(newItem);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Lace & Borders</h3>
        <p className="text-xs text-gray-600 mb-4">
          Add lace patterns or decorative borders to your design
        </p>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-700 mb-1 block">Type</label>
        <select
          value={laceType}
          onChange={(e) => setLaceType(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="border">Border</option>
          <option value="lace">Lace Pattern</option>
          <option value="trim">Trim</option>
          <option value="edging">Edging</option>
        </select>
      </div>

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-sm font-medium text-purple-700 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            Upload {laceType.charAt(0).toUpperCase() + laceType.slice(1)} Pattern
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {items.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-700">Added Patterns ({items.length})</p>
          <div className="grid grid-cols-2 gap-3">
            {items.map((item, index) => (
              <div key={item.id} className="relative group border border-gray-200 rounded-lg overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-full h-24 object-cover"
                />
                <button
                  onClick={() => onRemove(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="p-2 bg-white">
                  <p className="text-xs text-gray-700 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Product types that are purely bottom-wear — neckline and sleeves are irrelevant
const BOTTOM_WEAR_TYPES = ['trouser', 'trousers', 'jeans', 'shorts', 'skirt', 'dhoti', 'kurta-dhoti', 'pajama'];
// Product types that are sleeveless by construction — only sleeves are irrelevant
const SLEEVELESS_TYPES = ['waistcoat'];

// Men clothing customisation assets (matches Generate page step 2)
const STUDIO_MENS_TSHIRT_ASSET_MAP = {
  neckline: {
    'Crew Neck': '/assets/Mens-tshirt/Neck/Crew Neck.png',
    'V Neck': '/assets/Mens-tshirt/Neck/V Neck.png',
    'Polo Collar': '/assets/Mens-tshirt/Neck/Polo Collar.png',
    'Henley Collar': '/assets/Mens-tshirt/Neck/Henley Collar.png',
    'Scoop Neck': '/assets/Mens-tshirt/Neck/Scoop Neck.png',
    'Turtle Neck': '/assets/Mens-tshirt/Neck/Turtle Neck.png',
  },
  sleeve: {
    'Regular Half': '/assets/Mens-tshirt/Sleevees/Regular Half.png',
    'Long Sleeve': '/assets/Mens-tshirt/Sleevees/Long Sleeve.png',
    'Cap Sleeve': '/assets/Mens-tshirt/Sleevees/Cap Sleeve.png',
    'Sleeveless or Tank': '/assets/Mens-tshirt/Sleevees/Sleeveless or Tank.png',
    Raglan: '/assets/Mens-tshirt/Sleevees/Raglan.png',
    'Raglan Yoke Sleeve': '/assets/Mens-tshirt/Sleevees/Raglan Yoke Sleeve.png',
    Ringer: '/assets/Mens-tshirt/Sleevees/Ringer.png',
  },
};

const STUDIO_MENS_KURTA_ASSET_MAP = {
  neck: {
    'Chinese collar': '/assets/Men kurta/neck/Chinese collar.jpg',
    'Band collar': '/assets/Men kurta/neck/Band collar_.jpg',
    'Round neck': '/assets/Men kurta/neck/Round neck.jpg',
    'V neck': '/assets/Men kurta/neck/V neck.jpg',
    'Boat neck': '/assets/Men kurta/neck/Boat neck.jpg',
    'Notch neck': '/assets/Men kurta/neck/Notch neck.jpg',
    'Keyhole neck': '/assets/Men kurta/neck/Keyhole neck.jpg',
    'Asymmetric neck': '/assets/Men kurta/neck/Asymmetric neck.jpg',
    'Collar placket': '/assets/Men kurta/neck/Collar placket.jpg',
    'Collar less placket': '/assets/Men kurta/neck/Collar less placket.jpg',
  },
  sleeve: {
    'Half sleeve': '/assets/Men kurta/Sleevees/Half sleeve_.jpg',
    'Full sleeve': '/assets/Men kurta/Sleevees/Full sleeve.jpg',
    'Three quarter sleeve': '/assets/Men kurta/Sleevees/Three quarter sleeve_.jpg',
    'Rolled up sleeve': '/assets/Men kurta/Sleevees/Rolled up sleeve.jpg',
    'Cuffed sleeve': '/assets/Men kurta/Sleevees/Cuffed sleeve_.jpg',
    'Button tab sleeve': '/assets/Men kurta/Sleevees/Button tab sleeve_.jpg',
    'Convertible sleeve': '/assets/Men kurta/Sleevees/Convertible sleeve.jpg',
    'Piped sleeve': '/assets/Men kurta/Sleevees/Piped sleeve_.jpg',
    'Slit cut sleeve': '/assets/Men kurta/Sleevees/Slit cut sleeve_.jpg',
    'Gusset sleeve': '/assets/Men kurta/Sleevees/Gusset sleeve_.jpg',
    'Elbow patch': '/assets/Men kurta/Sleevees/Elbow patch_.jpg',
    'Angrakha style sleeve': '/assets/Men kurta/Sleevees/Angrakha style_.jpg',
  },
  frontPanel: {
    'Classic straight': '/assets/Men kurta/front panel/Classic straight_.jpg',
    'Slim fit': '/assets/Men kurta/front panel/Slim fit_.jpg',
    'Contrast side panel': '/assets/Men kurta/front panel/Contrast side panel.jpg',
    'Side gusset style': '/assets/Men kurta/front panel/Side gusset style_.jpg',
    'Asymmetric style': '/assets/Men kurta/front panel/Asymmetric style_.jpg',
    'Flaired style': '/assets/Men kurta/front panel/Flaired style_.jpg',
    'Angrakha style': '/assets/Men kurta/front panel/Angrakha style_.jpg',
  },
};

// Dhoti — icons under public/assets/Dhoti
const STUDIO_MDH = '/assets/Dhoti';
const STUDIO_MENS_DHOTI_ASSET_MAP = {
  style: {
    'South Indian': `${STUDIO_MDH}/South Indiandhoti_.jpg`,
    Casual: `${STUDIO_MDH}/Casual dhoti_.jpg`,
    'Panch Kacha': `${STUDIO_MDH}/Panch Kacha Dhoti.jpg`,
    Classic: `${STUDIO_MDH}/Classic Dhoti_.jpg`,
    Karnataka: `${STUDIO_MDH}/Karnataka dhoti_.jpg`,
    Gujarati: `${STUDIO_MDH}/Gujarati dhoti_.jpg`,
    Bengali: `${STUDIO_MDH}/Bengali dhoti_.jpg`,
    Maharashtrian: `${STUDIO_MDH}/Maharashtrian dhoti_.jpg`,
    Kerala: `${STUDIO_MDH}/Kerala dhoti_.jpg`,
  },
};

// Pajama — icons under public/assets/Pajama
const STUDIO_MPJ = '/assets/Pajama';
const STUDIO_MENS_PAJAMA_ASSET_MAP = {
  style: {
    Straight: `${STUDIO_MPJ}/Straight pajama_.jpg`,
    Salwar: `${STUDIO_MPJ}/Salwar pajama_.jpg`,
    Cigarette: `${STUDIO_MPJ}/Cigarette pajama_.jpg`,
    Tapered: `${STUDIO_MPJ}/Tapered pajama_.jpg`,
    Pathani: `${STUDIO_MPJ}/Pathani pajama.jpg`,
    Aligarh: `${STUDIO_MPJ}/Aligarh  pajama.jpg`,
    'Ankle length': `${STUDIO_MPJ}/Ankle length pajama_.jpg`,
    Drawstring: `${STUDIO_MPJ}/Drawstring pajama_.jpg`,
    Churidar: `${STUDIO_MPJ}/Churidar_.jpg`,
  },
};

// Men's pants / bottoms — icons under public/assets/Men pants (several subfolders have trailing spaces)
const STUDIO_MP = '/assets/Men pants';
const STUDIO_MP_LEG = `${STUDIO_MP}/Leg structure variation `;
const STUDIO_MP_LEN = `${STUDIO_MP}/Length variation `;
const STUDIO_MP_LEN_PANTS = `${STUDIO_MP_LEN}/Pants `;
const STUDIO_MP_LEN_SHORTS = `${STUDIO_MP_LEN}/Shorts `;
const STUDIO_MP_LEN_BOY = `${STUDIO_MP_LEN}/Boy short `;
const STUDIO_MP_RISE = `${STUDIO_MP}/Rise variation `;
const STUDIO_MP_POCKET = `${STUDIO_MP}/Pocket `;
const STUDIO_MP_POCKET_F = `${STUDIO_MP_POCKET}/Front `;
const STUDIO_MP_POCKET_B = `${STUDIO_MP_POCKET}/Back `;

const STUDIO_MENS_PANTS_ASSET_MAP = {
  waist: {
    'No waistband': `${STUDIO_MP}/Waist area/No waistband_.jpg`,
    'Side adjuster': `${STUDIO_MP}/Waist area/Side adjuster_.jpg`,
    'Half elastic waist': `${STUDIO_MP}/Waist area/Half elastic waist_.jpg`,
    'Extended waist': `${STUDIO_MP}/Waist area/Extended waist_.jpg`,
    'Tab waist': `${STUDIO_MP}/Waist area/Tab waist_.jpg`,
    'Belt loop waist': `${STUDIO_MP}/Waist area/Belt loop waist_.jpg`,
    'Paper bag waist': `${STUDIO_MP}/Waist area/Paper bag waist_.jpg`,
    'Draw string wast': `${STUDIO_MP}/Waist area/Draw string wast_.jpg`,
    'Full elastic waist': `${STUDIO_MP}/Waist area/Full elastic waist_.jpg`,
    'Normal waist': `${STUDIO_MP}/Waist area/Normal waist_.jpg`,
  },
  front: {
    'Plain front': `${STUDIO_MP}/Front construction/Plain front.jpg`,
    'Pleated front': `${STUDIO_MP}/Front construction/Pleated front_.jpg`,
    'Double pleated': `${STUDIO_MP}/Front construction/Double pleated_.jpg`,
    'Front dart': `${STUDIO_MP}/Front construction/Front dart_.jpg`,
    'Patch pocket front': `${STUDIO_MP}/Front construction/Patch pocket front_.jpg`,
    'Cargo pocket front': `${STUDIO_MP}/Front construction/Cargo pocket front.jpg`,
    'Button fly': `${STUDIO_MP}/Front construction/Button fly_.jpg`,
    'Zip fly with top button closure': `${STUDIO_MP}/Front construction/Zip fly with top button closure.jpg`,
    'Double button closure': `${STUDIO_MP}/Front construction/Double button closure_.jpg`,
    'Concealed hook & Bar closure': `${STUDIO_MP}/Front construction/Concealed hook & Bar closure_.jpg`,
  },
  legStructure: {
    'Slim fit': `${STUDIO_MP_LEG}/Slim fit_.jpg`,
    'Straight leg': `${STUDIO_MP_LEG}/Straight leg_.jpg`,
    'Relaxed leg': `${STUDIO_MP_LEG}/Relaxed leg_.jpg`,
    'Wide leg': `${STUDIO_MP_LEG}/Wide leg_.jpg`,
    'Flared leg': `${STUDIO_MP_LEG}/Flared leg.jpg`,
    'Boot fit': `${STUDIO_MP_LEG}/Boot fit_.jpg`,
    'Baggy leg': `${STUDIO_MP_LEG}/Baggy leg_.jpg`,
  },
  rise: {
    'Low rise': `${STUDIO_MP_RISE}/Low rise_.jpg`,
    'Mid rise': `${STUDIO_MP_RISE}/Mid rise_.jpg`,
    'High rise': `${STUDIO_MP_RISE}/High rise_.jpg`,
  },
  lengthPants: {
    'Extra short': `${STUDIO_MP_LEN_PANTS}/Extra short_.jpg`,
    Short: `${STUDIO_MP_LEN_PANTS}/Short_.jpg`,
    Regular: `${STUDIO_MP_LEN_PANTS}/Regular.jpg`,
    Long: `${STUDIO_MP_LEN_PANTS}/Long_.jpg`,
    'Extra long': `${STUDIO_MP_LEN_PANTS}/Extra long_.jpg`,
  },
  lengthShorts: {
    Short: `${STUDIO_MP_LEN_SHORTS}/Short_.jpg`,
    Regular: `${STUDIO_MP_LEN_SHORTS}/Regular.jpg`,
    Long: `${STUDIO_MP_LEN_SHORTS}/Long_.jpg`,
  },
  lengthBoyShort: {
    Short: `${STUDIO_MP_LEN_BOY}/Short.jpg`,
    Regular: `${STUDIO_MP_LEN_BOY}/Regular_.jpg`,
    Long: `${STUDIO_MP_LEN_BOY}/Long_.jpg`,
  },
  pocketFront: {
    'Coin pocket': `${STUDIO_MP_POCKET_F}/Coin pocket_.jpg`,
    'Curved pocket': `${STUDIO_MP_POCKET_F}/Curved pocket_.jpg`,
    'Patch pocket': `${STUDIO_MP_POCKET_F}/Patch pocket_.jpg`,
    'Welt pocket': `${STUDIO_MP_POCKET_F}/Welt pocket_.jpg`,
    'Zip pocket': `${STUDIO_MP_POCKET_F}/Zip pocket_.jpg`,
    'Utility pocket': `${STUDIO_MP_POCKET_F}/Utility pocket.jpg`,
  },
  pocketBack: {
    'No pocket': `${STUDIO_MP_POCKET_B}/No pocket_.jpg`,
    'Patch pocket': `${STUDIO_MP_POCKET_B}/Patch pocket_.jpg`,
    'Welt pocket': `${STUDIO_MP_POCKET_B}/Welt pocket_.jpg`,
    'Flap pocket': `${STUDIO_MP_POCKET_B}/Flap pocket_.jpg`,
    'Zip pocket': `${STUDIO_MP_POCKET_B}/Zip pocket_.jpg`,
    'Button welt pocket': `${STUDIO_MP_POCKET_B}/Button welt  pocket.jpg`,
    'Cargo pocket': `${STUDIO_MP_POCKET_B}/Cargo pocket_.jpg`,
    'Carpenter pocket': `${STUDIO_MP_POCKET_B}/Carpenter pocket.jpg`,
    'Hidden pocket': `${STUDIO_MP_POCKET_B}/Hidden pocket_.jpg`,
    'Phone pocket': `${STUDIO_MP_POCKET_B}/Phone pocket_.jpg`,
    'Yoke pocket': `${STUDIO_MP_POCKET_B}/Yoke pocket_.jpg`,
    'Hammer loop and pocket': `${STUDIO_MP_POCKET_B}/Hammer loop and pocket_.jpg`,
    'Utility pocket': `${STUDIO_MP_POCKET_B}/Utility pocket_.jpg`,
  },
};

// Men's blazer — icons under public/assets/Mens Blazer
const STUDIO_MBZ = '/assets/Mens Blazer';
const STUDIO_MENS_BLAZER_ASSET_MAP = {
  collar: {
    'Standard blazer collar': `${STUDIO_MBZ}/collars/Standard blazer collar.png`,
    'Shawl collar': `${STUDIO_MBZ}/collars/Shawl collar.png`,
    'Mandarin collar': `${STUDIO_MBZ}/collars/Mandarin collar.png`,
  },
  buttons: {
    'Single button': `${STUDIO_MBZ}/Bottons/Single button.png`,
    'Two button': `${STUDIO_MBZ}/Bottons/Two button.png`,
    'Three button': `${STUDIO_MBZ}/Bottons/Three button.png`,
    'Double breasted': `${STUDIO_MBZ}/Bottons/Double breasted.png`,
  },
  vents: {
    'No vent': `${STUDIO_MBZ}/Vents/No vent.png`,
    'Single vent': `${STUDIO_MBZ}/Vents/Single vent.png`,
    'Double vent': `${STUDIO_MBZ}/Vents/Double vent.png`,
  },
  hem: {
    'Staright hem': `${STUDIO_MBZ}/hems/staright hem.png`,
    'Rounded front hem': `${STUDIO_MBZ}/hems/Rounded front hem.png`,
  },
  pockets: {
    'Flap pocket': `${STUDIO_MBZ}/pockets/Flap pocket.png`,
    'Patch pocket': `${STUDIO_MBZ}/pockets/Patch pocket.png`,
    'Welt pocket': `${STUDIO_MBZ}/pockets/Welt pocket.png`,
    'Ticket pocket': `${STUDIO_MBZ}/pockets/Ticket pocket.png`,
    'Chest welt pocket': `${STUDIO_MBZ}/pockets/Chest welt pocket.png`,
  },
};

// Men's waistcoat — icons under public/assets/Men Waistcoat (note trailing spaces on "western tuxedo  " folder)
const STUDIO_MW = '/assets/Men Waistcoat';
const STUDIO_MW_TR = `${STUDIO_MW}/Traditional waistcoat`;
const STUDIO_MW_NJ = `${STUDIO_MW}/Nehru jacket`;
const STUDIO_MW_WT = `${STUDIO_MW}/western tuxedo  `;
const STUDIO_MENS_WAISTCOAT_ASSET_MAP = {
  traditional: {
    Layered: `${STUDIO_MW_TR}/layered waistcoat.png`,
    Longline: `${STUDIO_MW_TR}/longline waistcoat.png`,
    Asymmetric: `${STUDIO_MW_TR}/Asymetric waistcoat.png`,
    'Double breasted': `${STUDIO_MW_TR}/Double breasted waistcoat.png`,
    Overlap: `${STUDIO_MW_TR}/overlap waistcoat.png`,
    'Open front': `${STUDIO_MW_TR}/Open front waistcoat.png`,
    Angarkha: `${STUDIO_MW_TR}/Aangarkha style waistcoat.png`,
    Bandhgala: `${STUDIO_MW_TR}/Bandhgala waistcoat.png`,
    'Single breasted': `${STUDIO_MW_TR}/single breasted waistcoat.png`,
    'High-low hem': `${STUDIO_MW_TR}/high low hem waistcoat.png`,
  },
  nehruJacket: {
    Asymmetric: `${STUDIO_MW_NJ}/Asymetric nehru jacket.png`,
    Angarkha: `${STUDIO_MW_NJ}/Angrkha nehru jacket.png`,
    Long: `${STUDIO_MW_NJ}/long nehru jacket.png`,
    Short: `${STUDIO_MW_NJ}/short nehru jacket.png`,
    Bandhgala: `${STUDIO_MW_NJ}/bandhgala nehru jacket.png`,
  },
  westernTuxedo: {
    'V neck': `${STUDIO_MW_WT}/V neck waist coat.jpg`,
    Tuxedo: `${STUDIO_MW_WT}/Tuxdo waistcote.jpg`,
    Safari: `${STUDIO_MW_WT}/Safari waistcoat.jpg`,
    Victorian: `${STUDIO_MW_WT}/Vectorian waistcoat.jpg`,
    'U neck': `${STUDIO_MW_WT}/U neck waistcoat.jpg`,
    'Double busted': `${STUDIO_MW_WT}/double busted waist coat.jpg`,
    Cowboy: `${STUDIO_MW_WT}/western cowboy waistcoat.jpg`,
    Steampunk: `${STUDIO_MW_WT}/Steampunk waistcoat.jpg`,
    'Single busted': `${STUDIO_MW_WT}/single busted waist coat.jpg`,
  },
};

const STUDIO_MJ = '/assets/Men-Jacket';
const STUDIO_MENS_JACKET_ASSET_MAP = {
  collar: {
    'Asymmetric stand collar': `${STUDIO_MJ}/collar/Asymmetric stand collar.jpg`,
    'Attached hood': `${STUDIO_MJ}/collar/Attached hood_.jpg`,
    'Corduroy collar': `${STUDIO_MJ}/collar/Corduroy collar_.jpg`,
    'Detachable collar': `${STUDIO_MJ}/collar/Detachable collar_.jpg`,
    'Detached hood': `${STUDIO_MJ}/collar/Detached hood_.jpg`,
    'Funnel neck': `${STUDIO_MJ}/collar/Funnel neck_.jpg`,
    'High neck (Gaiter)': `${STUDIO_MJ}/collar/High neck (Gaiter).jpg`,
    'Hood with high neck': `${STUDIO_MJ}/collar/Hood with high neck_.jpg`,
    'Ribbed stand collar': `${STUDIO_MJ}/collar/Ribbed stand collar_.jpg`,
    'Shawl collar': `${STUDIO_MJ}/collar/Shawl collar_.jpg`,
    'Sherpa collar': `${STUDIO_MJ}/collar/Sherpa collar_.jpg`,
    'Shirt collar': `${STUDIO_MJ}/collar/Shirt collar_.jpg`,
    'Spread collar': `${STUDIO_MJ}/collar/Spread collar_.jpg`,
    'Stand collar with snap': `${STUDIO_MJ}/collar/Stand collar with snap.jpg`,
    'Stand collar': `${STUDIO_MJ}/collar/Stand collar_.jpg`,
  },
  sleeve: {
    'Adjustable cuff sleeve': `${STUDIO_MJ}/Sleeve option /Adjustable cuff sleeve_.jpg`,
    'Cuffed sleeve': `${STUDIO_MJ}/Sleeve option /Cuffed sleeve_.jpg`,
    'Drop shoulder': `${STUDIO_MJ}/Sleeve option /Drop shoulder_.jpg`,
    'Elbow patch sleeve': `${STUDIO_MJ}/Sleeve option /Elbow patch sleeve_.jpg`,
    'Panelled sleeve': `${STUDIO_MJ}/Sleeve option /Panelled sleeve_.jpg`,
    'Princess sleeve': `${STUDIO_MJ}/Sleeve option /Princess sleeve_.jpg`,
    'Quilted sleeve': `${STUDIO_MJ}/Sleeve option /Quilted sleeve_.jpg`,
    'Raglan sleeve': `${STUDIO_MJ}/Sleeve option /Raglan sleeve_.jpg`,
    'Set in sleeve': `${STUDIO_MJ}/Sleeve option /Set in sleeve_.jpg`,
    'Two-piece sleeve': `${STUDIO_MJ}/Sleeve option /Two-piece sleeve_.jpg`,
  },
  outerShell: {
    'Barn jacket': `${STUDIO_MJ}/Outer shell /Barn jacket_.jpg`,
    'Bomber jacket': `${STUDIO_MJ}/Outer shell /Bomber jacket_.jpg`,
    'Pea coat': `${STUDIO_MJ}/Outer shell /Pea coat_.jpg`,
    'Puffer jacket': `${STUDIO_MJ}/Outer shell /Puffer jacket_.jpg`,
    'Rain jacket': `${STUDIO_MJ}/Outer shell /Rain jacket_.jpg`,
    'Safety jacket': `${STUDIO_MJ}/Outer shell /Safety jacket_.jpg`,
    'Soft shell jacket': `${STUDIO_MJ}/Outer shell /Soft shell jacket.jpg`,
    'Sport jacket': `${STUDIO_MJ}/Outer shell /Sport jacket_.jpg`,
    'Teddy jacket': `${STUDIO_MJ}/Outer shell /Teddy jacket_.jpg`,
    'Windbreaker jacket': `${STUDIO_MJ}/Outer shell /Windbreaker jacket_.jpg`,
  },
};

const STUDIO_MS = '/assets/Mens Shirt';
const STUDIO_MENS_SHIRT_ASSET_MAP = {
  collar: {
    'Pointed collar': `${STUDIO_MS}/collars/pointed collar.png`,
    'Spread collar': `${STUDIO_MS}/collars/Spread collar.png`,
    'Cutway collar': `${STUDIO_MS}/collars/Cutway collar.png`,
    'Button down collar': `${STUDIO_MS}/collars/Button down collar.png`,
    'Club (round) collar': `${STUDIO_MS}/collars/Club(round) collar.png`,
    'Mandarin collar': `${STUDIO_MS}/collars/Mandarin collar (1).png`,
    'Band collar': `${STUDIO_MS}/collars/Band collar (1).png`,
  },
  placket: {
    'Standard placket': `${STUDIO_MS}/plackets/standard plackets.png`,
    'French placket': `${STUDIO_MS}/plackets/French plackets.png`,
    'Half placket': `${STUDIO_MS}/plackets/Half plackets.png`,
    'Concealed hidden placket': `${STUDIO_MS}/plackets/Concealed_ hidden plackets.png`,
  },
  cuff: {
    'Convertible cuff': `${STUDIO_MS}/Cuffs/convertable cuff.png`,
    'Barrel cuff (1 button)': `${STUDIO_MS}/Cuffs/Barrel cuff (1 Botton).png`,
    'Barrel cuff (2 button)': `${STUDIO_MS}/Cuffs/Barrel cuff (2 Botton).png`,
    'French cuff': `${STUDIO_MS}/Cuffs/French cuff.png`,
    'Rounded cuff': `${STUDIO_MS}/Cuffs/Rounded cuff.png`,
  },
  sleeve: {
    'Full sleeve': `${STUDIO_MS}/sleeves/Full sleeve.png`,
    'Half sleeves': `${STUDIO_MS}/sleeves/Half sleeves.png`,
    'Roll-up sleeve with tab': `${STUDIO_MS}/sleeves/Roll-up sleeve with tab.png`,
  },
  fit: {
    'Slim fit': `${STUDIO_MS}/Fit/Slim fit.png`,
    'Regular fit': `${STUDIO_MS}/Fit/Regular fit.png`,
    'Relaxed fit': `${STUDIO_MS}/Fit/Relaxed fit.png`,
    'Tailored fit': `${STUDIO_MS}/Fit/Tailored  fit.png`,
    'Oversized fit': `${STUDIO_MS}/Fit/Oversized fit.png`,
  },
  hem: {
    'Straight hem': `${STUDIO_MS}/Hems/straight hem.png`,
    'Curved hem': `${STUDIO_MS}/Hems/Curved hem.png`,
    'High low hem': `${STUDIO_MS}/Hems/High low hem.png`,
    'Long hem': `${STUDIO_MS}/Hems/long hem.png`,
  },
  backPleat: {
    'No pleat (flat)': `${STUDIO_MS}/back pleats/No pleat (flat).png`,
    'Box pleat': `${STUDIO_MS}/back pleats/box pleat.png`,
    'Side pleat': `${STUDIO_MS}/back pleats/Side pleat.png`,
    'Back darts': `${STUDIO_MS}/back pleats/back darts.png`,
  },
  pocket: {
    'Single pocket': `${STUDIO_MS}/pockets illustration/Single pocket.png`,
    'Double pocket': `${STUDIO_MS}/pockets illustration/Double pocket.png`,
    'Flap pocket': `${STUDIO_MS}/pockets illustration/Flap pocket.png`,
    'Welt pocket': `${STUDIO_MS}/pockets illustration/Welt pocket.png`,
    'Utility pocket': `${STUDIO_MS}/pockets illustration/Utility pocket.png`,
  },
};

function getStudioMensCustomisationSchema(productCategory, productType) {
  if (productCategory !== 'men-clothing') return null;
  if (productType === 'tshirt') {
    return [
      { key: 'neckline', label: 'Neckline', opts: Object.keys(STUDIO_MENS_TSHIRT_ASSET_MAP.neckline) },
      { key: 'sleeve', label: 'Sleeve', opts: Object.keys(STUDIO_MENS_TSHIRT_ASSET_MAP.sleeve) },
    ];
  }
  if (productType === 'kurta') {
    return [
      { key: 'neck', label: 'Neck', opts: Object.keys(STUDIO_MENS_KURTA_ASSET_MAP.neck) },
      { key: 'sleeve', label: 'Sleeve', opts: Object.keys(STUDIO_MENS_KURTA_ASSET_MAP.sleeve) },
      { key: 'frontPanel', label: 'Front panel', opts: Object.keys(STUDIO_MENS_KURTA_ASSET_MAP.frontPanel) },
    ];
  }
  if (productType === 'kurta-dhoti') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(STUDIO_MENS_DHOTI_ASSET_MAP.style) },
    ];
  }
  if (productType === 'pajama') {
    return [
      { key: 'style', label: 'Style', opts: Object.keys(STUDIO_MENS_PAJAMA_ASSET_MAP.style) },
    ];
  }
  if (productType === 'trouser') {
    return [
      { key: 'waist', label: 'Waist area', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.waist) },
      { key: 'front', label: 'Front construction', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.front) },
      { key: 'legStructure', label: 'Leg structure', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.legStructure) },
      { key: 'rise', label: 'Rise', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.rise) },
      { key: 'lengthPants', label: 'Length (pants)', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.lengthPants) },
      { key: 'lengthShorts', label: 'Length (shorts)', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.lengthShorts) },
      { key: 'lengthBoyShort', label: 'Length (boy short)', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.lengthBoyShort) },
      { key: 'pocketFront', label: 'Front pockets', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.pocketFront) },
      { key: 'pocketBack', label: 'Back pockets', opts: Object.keys(STUDIO_MENS_PANTS_ASSET_MAP.pocketBack) },
    ];
  }
  if (productType === 'blazer') {
    return [
      { key: 'collar', label: 'Collars', opts: Object.keys(STUDIO_MENS_BLAZER_ASSET_MAP.collar) },
      { key: 'buttons', label: 'Buttons', opts: Object.keys(STUDIO_MENS_BLAZER_ASSET_MAP.buttons) },
      { key: 'vents', label: 'Vents', opts: Object.keys(STUDIO_MENS_BLAZER_ASSET_MAP.vents) },
      { key: 'pockets', label: 'Pockets', opts: Object.keys(STUDIO_MENS_BLAZER_ASSET_MAP.pockets) },
      { key: 'hem', label: 'Hems', opts: Object.keys(STUDIO_MENS_BLAZER_ASSET_MAP.hem) },
    ];
  }
  if (productType === 'waistcoat') {
    return [
      { key: 'traditional', label: 'Traditional waistcoat', opts: Object.keys(STUDIO_MENS_WAISTCOAT_ASSET_MAP.traditional) },
      { key: 'nehruJacket', label: 'Nehru jacket', opts: Object.keys(STUDIO_MENS_WAISTCOAT_ASSET_MAP.nehruJacket) },
      { key: 'westernTuxedo', label: 'Western / tuxedo', opts: Object.keys(STUDIO_MENS_WAISTCOAT_ASSET_MAP.westernTuxedo) },
    ];
  }
  if (productType === 'shirt') {
    return [
      { key: 'collar', label: 'Collar', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.collar) },
      { key: 'placket', label: 'Placket', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.placket) },
      { key: 'cuff', label: 'Cuffs', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.cuff) },
      { key: 'sleeve', label: 'Sleeves', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.sleeve) },
      { key: 'fit', label: 'Fit', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.fit) },
      { key: 'hem', label: 'Hems', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.hem) },
      { key: 'backPleat', label: 'Back pleats', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.backPleat) },
      { key: 'pocket', label: 'Pockets', opts: Object.keys(STUDIO_MENS_SHIRT_ASSET_MAP.pocket) },
    ];
  }
  if (productType === 'jacket') {
    return [
      { key: 'collar', label: 'Collar', opts: Object.keys(STUDIO_MENS_JACKET_ASSET_MAP.collar) },
      { key: 'sleeve', label: 'Sleeve option', opts: Object.keys(STUDIO_MENS_JACKET_ASSET_MAP.sleeve) },
      { key: 'outerShell', label: 'Outer shell', opts: Object.keys(STUDIO_MENS_JACKET_ASSET_MAP.outerShell) },
    ];
  }
  return null;
}

function getStudioMensReferenceAsset(productType, attrKey, opt) {
  if (productType === 'tshirt') return STUDIO_MENS_TSHIRT_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'kurta') return STUDIO_MENS_KURTA_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'kurta-dhoti') return STUDIO_MENS_DHOTI_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'pajama') return STUDIO_MENS_PAJAMA_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'trouser') return STUDIO_MENS_PANTS_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'blazer') return STUDIO_MENS_BLAZER_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'waistcoat') return STUDIO_MENS_WAISTCOAT_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'shirt') return STUDIO_MENS_SHIRT_ASSET_MAP?.[attrKey]?.[opt] || null;
  if (productType === 'jacket') return STUDIO_MENS_JACKET_ASSET_MAP?.[attrKey]?.[opt] || null;
  return null;
}

function toAbsoluteAssetUrl(u) {
  return toAbsolutePublicAssetUrl(u);
}

/** Resolve customization reference image + type for magic edit v2 from a clicked studio option. */
function getCustomizationReferenceContext(kit, lastClickedStyle) {
  if (!kit || !lastClickedStyle?.type || !lastClickedStyle?.value) {
    return { referenceImageUrl: null, customizationType: null, customizationValue: null };
  }

  const kitProductType = kit?.productDetails?.productType || '';
  const kitProductCategory = kit?.productDetails?.productCategory || '';
  const clickedType = lastClickedStyle.type;
  const clickedValue = lastClickedStyle.value;
  const studioKidAgeRange =
    kit?.productDetails?.kidAge ||
    kit?.productDetails?.kidAgeRange ||
    (kitProductCategory === 'kids-boy' || kitProductCategory === 'kids-girl' ? 'kids' : null);
  const normalizedMenType =
    STUDIO_SCHEMA_CATEGORIES.has(kitProductCategory) &&
    (kitProductCategory === 'men-clothing' || kitProductCategory === 'kids-boy') &&
    kitProductType
      ? normalizeMenClothingTypeForCustomization(kitProductType)
      : null;
  const normalizedWomenType =
    STUDIO_SCHEMA_CATEGORIES.has(kitProductCategory) &&
    (kitProductCategory === 'women-clothing' || kitProductCategory === 'kids-girl') &&
    kitProductType
      ? normalizeProductTypeForStudioCustomization(kitProductCategory, kitProductType)
      : null;

  const mensRefAsset =
    normalizedMenType && clickedType && clickedValue
      ? getReferenceAsset({
          categoryValue: kitProductCategory,
          typeValue: normalizedMenType,
          attrKey: clickedType === 'neck' ? 'neck' : clickedType,
          opt: clickedValue,
          kidAgeRange: studioKidAgeRange,
        })
      : null;
  const womenRefAsset =
    normalizedWomenType && clickedType && clickedValue
      ? getReferenceAsset({
          categoryValue: kitProductCategory,
          typeValue: normalizedWomenType,
          attrKey: clickedType,
          opt: clickedValue,
          kidAgeRange: studioKidAgeRange,
        })
      : null;
  const necklineIcon =
    clickedType === 'neckline' && clickedValue
      ? (STUDIO_LEGACY_NECKLINE_OPTIONS.find((o) => o.id === clickedValue)?.icon || null)
      : null;

  const referenceImageUrl =
    (mensRefAsset ? toAbsoluteAssetUrl(mensRefAsset) : null) ||
    (womenRefAsset ? toAbsoluteAssetUrl(womenRefAsset) : null) ||
    (necklineIcon ? toAbsoluteAssetUrl(necklineIcon) : null) ||
    null;

  return {
    referenceImageUrl,
    customizationType: clickedType,
    customizationValue: clickedValue,
  };
}

function buildMagicEditV2Payload({
  imageUrl,
  originalImageUrl,
  prompt,
  originalKitId,
  kit,
  lastClickedStyle,
  extra = {},
}) {
  const { referenceImageUrl, customizationType } = getCustomizationReferenceContext(kit, lastClickedStyle);
  return {
    imageUrl,
    originalImageUrl,
    prompt,
    originalKitId,
    createNewKit: true,
    aspectRatio: 'match_input_image',
    ...(referenceImageUrl ? { referenceImageUrl, customizationType } : {}),
    ...extra,
  };
}

/** Map kit subtype values (e.g. dress-evening, blazer-formal) to base types used by Generate / MultiProductTypeSelector assets. */
function normalizeWomenClothingTypeForCustomization(typeValue) {
  if (!typeValue) return typeValue;
  const v = String(typeValue).toLowerCase();
  if (v === 'dress' || v.startsWith('dress-')) return 'dress';
  if (v === 'frock' || v.startsWith('frock-')) return 'frock';
  if (v === 'shirt' || v.startsWith('shirt-')) return 'shirt';
  return typeValue;
}

function normalizeMenClothingTypeForCustomization(typeValue) {
  if (!typeValue) return typeValue;
  const v = String(typeValue).toLowerCase();
  if (v === 'tshirt' || v.startsWith('tshirt-')) return 'tshirt';
  if (v === 'shirt' || v.startsWith('shirt-')) return 'shirt';
  if (v === 'trouser' || v.startsWith('trouser-')) return 'trouser';
  if (v === 'blazer' || v.startsWith('blazer-')) return 'blazer';
  if (v === 'jacket' || v.startsWith('jacket-')) return 'jacket';
  if (v === 'kurta-dhoti' || v.startsWith('kurta-dhoti')) return 'kurta-dhoti';
  if (v === 'kurta' || v.startsWith('kurta-')) return 'kurta';
  if (v === 'pajama' || v.startsWith('pajama-')) return 'pajama';
  if (v === 'waistcoat' || v.startsWith('waistcoat-')) return 'waistcoat';
  if (v === 'cordset' || v.startsWith('cordset')) return 'cordset';
  if (v === 'nehru jacket' || v.startsWith('nehru-jacket') || v.startsWith('nehru jacket')) {
    return 'nehru jacket';
  }
  return typeValue;
}

function normalizeProductTypeForStudioCustomization(productCategory, typeValue) {
  if (productCategory === 'women-clothing' || productCategory === 'kids-girl') {
    return normalizeWomenClothingTypeForCustomization(typeValue);
  }
  if (productCategory === 'men-clothing' || productCategory === 'kids-boy') {
    return normalizeMenClothingTypeForCustomization(typeValue);
  }
  return typeValue;
}

const STUDIO_SCHEMA_CATEGORIES = new Set(['women-clothing', 'men-clothing', 'kids-boy', 'kids-girl']);

function isBottomWear(productType) {
  if (!productType) return false;
  const pt = productType.toLowerCase();
  return BOTTOM_WEAR_TYPES.some(bw => pt === bw || pt.startsWith(bw + '-'));
}

function isSleevelessType(productType) {
  if (!productType) return false;
  const pt = productType.toLowerCase();
  return SLEEVELESS_TYPES.some(s => pt === s || pt.startsWith(s + '-'));
}

// Design Editor Section Component (Neckline/Sleeve/Silhouette/Length/Colors)
const DesignEditorSection = ({
  designSettings,
  onSettingsChange,
  onStyleClick,
  showNotification,
  kit,
  mainImage,
  originalMainImage,
  setIsMagicEditing,
  setIsMagicEditPreviewLoading,
  setMagicEditImages,
  setSelectedImageIndex,
  magicEditImages,
  selectedImageIndex,
  studioFabricProduct,
  studioFabricProductLoading,
  isMobileEditorPage = false,
  onExitMobileEditor,
}) => {
  const NECKLINE_REFERENCE_LOCKED_TEXT = 'Neckline edit using reference';
  const SLEEVE_REFERENCE_LOCKED_TEXT = 'Sleeve edit using reference';
  const SILHOUETTE_REFERENCE_LOCKED_TEXT = 'Silhouette edit using reference';
  const productType = kit?.productDetails?.productType || '';
  const productCategory = kit?.productDetails?.productCategory || '';
  const bottomWear = isBottomWear(productType);
  const sleeveless = isSleevelessType(productType);
  const studioCustomContext = useMemo(() => {
    if (!STUDIO_SCHEMA_CATEGORIES.has(productCategory) || !productType) return null;

    const kidAgeRange =
      kit?.productDetails?.kidAge ||
      kit?.productDetails?.kidAgeRange ||
      (productCategory === 'kids-boy' || productCategory === 'kids-girl' ? 'kids' : null);

    const assetType = normalizeProductTypeForStudioCustomization(productCategory, productType);
    const schema = getCustomizationForType(assetType, productCategory, kidAgeRange);
    if (!schema?.length) return null;

    return {
      schema,
      categoryValue: productCategory,
      typeValue: assetType,
      kidAgeRange,
    };
  }, [
    productCategory,
    productType,
    kit?.productDetails?.kidAge,
    kit?.productDetails?.kidAgeRange,
  ]);

  const studioSchema = studioCustomContext?.schema ?? null;

  const showNeckline = !bottomWear;
  const showSleeves = !bottomWear && !sleeveless;

  const availableTabs = useMemo(() => {
    if (studioSchema && studioSchema.length > 0) {
      const shortByKey = {
        necklineFront: 'Front neck',
        necklineBack: 'Back neck',
        bodyCutOut: 'Body cut-outs',
        waistAttachment: 'Waist add-on',
        waistPlacement: 'Waist line',
        shoulderDetail: 'Shoulders',
        surfaceVolume: 'Surface',
        slitVariationLength: 'Slit length',
        slitVariationTypes: 'Slit type',
        strap: 'Straps',
        bodice: 'Bodice',
        openingCloser: 'Openings',
        openingCenterFront: 'Center front',
        openingBack: 'Back opening',
        openingSide: 'Side opening',
        openingZipper: 'Zipper',
        closureTie: 'Tie closure',
        closureHook: 'Hook closure',
        closureLoop: 'Loop closure',
        closureFashion: 'Closure',
        openingPullOn: 'Pull-on',
        topLength: 'Length',
        shape: 'Shape',
        buttons: 'Buttons',
        vents: 'Vents',
        pockets: 'Pockets',
        hem: 'Hem',
        collar: 'Collar',
      };
      const tabs = studioSchema.map((a) => ({
        id: a.key,
        label: a.label,
        shortLabel: shortByKey[a.key] || a.label,
      }));
      tabs.push({ id: 'color', label: 'Fabric colors', shortLabel: 'Colors' });
      return tabs;
    }
    const tabs = [];
    if (showNeckline) tabs.push({ id: 'neckline', label: 'Neckline', shortLabel: 'Neck' });
    if (showSleeves) tabs.push({ id: 'sleeve', label: 'Sleeves', shortLabel: 'Sleeve' });
    tabs.push({ id: 'silhouette', label: 'Silhouette', shortLabel: 'Shape' });
    tabs.push({ id: 'length', label: 'Length', shortLabel: 'Length' });
    tabs.push({ id: 'color', label: 'Colors', shortLabel: 'Colors' });
    return tabs;
  }, [showNeckline, showSleeves, studioSchema]);

  const [activeDesignTab, setActiveDesignTab] = useState(() => availableTabs[0]?.id || 'silhouette');
  const [isApplying, setIsApplying] = useState(false);
  const necklineReferenceInputRef = useRef(null);
  const sleeveReferenceInputRef = useRef(null);
  const silhouetteReferenceInputRef = useRef(null);
  const [isUploadingNecklineReference, setIsUploadingNecklineReference] = useState(false);
  const [isUploadingSleeveReference, setIsUploadingSleeveReference] = useState(false);
  const [isUploadingSilhouetteReference, setIsUploadingSilhouetteReference] = useState(false);

  // Reset active tab when available tabs change (e.g. kit switch)
  useEffect(() => {
    if (!availableTabs.find(t => t.id === activeDesignTab)) {
      setActiveDesignTab(availableTabs[0]?.id || 'silhouette');
    }
  }, [availableTabs, activeDesignTab]);

  const openDesignTab = (tabId) => {
    setActiveDesignTab(tabId);
  };

  const necklineOptions = STUDIO_LEGACY_NECKLINE_OPTIONS;

  const sleeveOptions = [
    { id: 'cap', label: 'Cap Sleeve', icon: '◐' },
    { id: 'puff', label: 'Puff Sleeve', icon: '◉' },
    { id: 'full', label: 'Full Sleeve', icon: '◑' },
    { id: 'sleeveless', label: 'Sleeveless', icon: '○' }
  ];

  const silhouetteOptions = [
    { id: 'a-line', label: 'A-Line', icon: 'A' },
    { id: 'straight', label: 'Straight', icon: '|' },
    { id: 'flared', label: 'Flared', icon: '◊' }
  ];

  const garmentLengthOptions = [
    { id: 'slightly-shorter', label: 'Slightly shorter', icon: '−' },
    { id: 'much-shorter', label: 'Much shorter', icon: '−−' },
    { id: 'slightly-longer', label: 'Slightly longer', icon: '+' },
    { id: 'much-longer', label: 'Much longer', icon: '++' }
  ];

  const renderStudioSchemaOptionsGrid = (attr, onSelect) => {
    if (!attr) return null;
    const selected = (designSettings?.productTypeCustomizations?.[attr.key] || [])[0] || null;
    return (
      <div className="grid auto-rows-min grid-cols-2 content-start gap-2.5 sm:grid-cols-2 lg:max-h-[min(52vh,26rem)] lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain lg:[scrollbar-width:thin] lg:grid-cols-2 xl:grid-cols-3">
        {attr.opts.map((opt) => {
          const img = studioCustomContext
            ? getReferenceAsset({
                categoryValue: studioCustomContext.categoryValue,
                typeValue: studioCustomContext.typeValue,
                attrKey: attr.key,
                opt,
                kidAgeRange: studioCustomContext.kidAgeRange,
              })
            : null;
          const isSelected = selected === opt;
          return (
            <button
              key={opt}
              type="button"
              title={opt}
              onClick={() => onSelect(attr.key, isSelected ? null : opt)}
              className={[
                'group flex w-full min-w-0 flex-col items-stretch gap-2 self-start rounded-xl border-2 px-2 py-2.5 text-center transition-all sm:px-2.5 sm:py-3',
                isSelected
                  ? 'border-indigo-500 bg-indigo-50/95 shadow-md ring-1 ring-indigo-200/70'
                  : 'border-slate-200/90 bg-white hover:border-indigo-200 hover:bg-slate-50/90',
              ].join(' ')}
            >
              <div className="flex h-[4.25rem] w-full shrink-0 items-center justify-center rounded-lg bg-slate-50 ring-1 ring-slate-100 transition-colors group-hover:bg-white sm:h-[4.5rem]">
                {img ? (
                  <img src={img} alt="" className="max-h-full max-w-full object-contain p-0.5" loading="lazy" />
                ) : (
                  <span className="text-xs font-semibold text-slate-400">{opt?.[0] || '?'}</span>
                )}
              </div>
              <div
                className={`flex min-h-[2.75rem] shrink-0 items-start justify-center text-center text-[10px] font-medium leading-snug sm:text-[11px] ${
                  isSelected ? 'text-indigo-900' : 'text-slate-700'
                }`}
              >
                <span className="line-clamp-3">{opt}</span>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  const handleSettingChange = (type, value) => {
    const waistcoatAttrKeys =
      productCategory === 'men-clothing' && productType === 'waistcoat' && studioSchema
        ? Object.keys(STUDIO_MENS_WAISTCOAT_ASSET_MAP)
        : null;

    onSettingsChange((prev) => {
      const base = { ...prev, [type]: value };
      if (!studioSchema) {
        return { ...base, productTypeCustomizations: prev.productTypeCustomizations };
      }
      let nextPtc = { ...(prev.productTypeCustomizations || {}) };
      if (waistcoatAttrKeys && waistcoatAttrKeys.includes(type)) {
        if (value) {
          waistcoatAttrKeys.forEach((k) => {
            nextPtc[k] = k === type ? [value] : [];
          });
        } else {
          nextPtc[type] = [];
        }
      } else {
        nextPtc[type] = value ? [value] : [];
      }
      return { ...base, productTypeCustomizations: nextPtc };
    });
    // Notify parent about the clicked style
    if (onStyleClick) {
      onStyleClick({ type, value });
    }
    const notifyLabel =
      type === 'garmentLength' && GARMENT_LENGTH_DISPLAY[value]
        ? `Garment length: ${GARMENT_LENGTH_DISPLAY[value]}`
        : `${type.charAt(0).toUpperCase() + type.slice(1)} updated to ${value}`;
    showNotification?.(notifyLabel, 'success');
  };

  const handleMobileCustomizeSelect = (type, value) => {
    if (!value) {
      handleSettingChange(type, value);
      return;
    }
    handleSettingChange(type, value);
    if (isMobileEditorPage) {
      onExitMobileEditor?.();
    }
  };

  const handleCustomizeSelect = (type, value) => {
    if (isMobileEditorPage) {
      handleMobileCustomizeSelect(type, value);
    } else {
      handleSettingChange(type, value);
    }
  };

  const renderStudioSchemaTab = (attr) => {
    if (!attr) return null;
    return (
      <div className="flex flex-col gap-2 lg:gap-3">
        <div className="shrink-0 rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/60 px-3 py-2 shadow-sm lg:py-2.5">
          <h3 className="text-xs font-semibold text-slate-900 lg:text-sm">{attr.label}</h3>
          <p className="mt-0.5 hidden text-[11px] leading-snug text-slate-500 lg:block">
            Choose one reference. Your edit applies to the main preview after a moment.
          </p>
        </div>
        {renderStudioSchemaOptionsGrid(attr, handleCustomizeSelect)}
      </div>
    );
  };

  const handleNecklineReferenceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification?.('Please upload an image file', 'error');
      if (necklineReferenceInputRef.current) {
        necklineReferenceInputRef.current.value = '';
      }
      return;
    }

    let previewUrl = null;
    setIsUploadingNecklineReference(true);

    try {
      previewUrl = URL.createObjectURL(file);

      onSettingsChange((prev) => {
        if (prev?.necklineReferenceImageUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(prev.necklineReferenceImageUrl);
        }
        return {
          ...prev,
          necklineReferenceImageUrl: previewUrl,
          necklineReferenceImageName: file.name,
          necklinePrompt: NECKLINE_REFERENCE_LOCKED_TEXT
        };
      });

      const uploadResult = await uploadImageWithFallback(file, { folder: 'magic-edits/neckline-references' });
      const uploadedUrl = uploadResult?.url || (typeof uploadResult === 'string' ? uploadResult : null);
      if (!uploadedUrl) {
        throw new Error('Upload did not return a URL');
      }

      onSettingsChange((prev) => {
        if (previewUrl && prev?.necklineReferenceImageUrl === previewUrl) {
          URL.revokeObjectURL(previewUrl);
          previewUrl = null;
        }
        return {
          ...prev,
          necklineReferenceImageUrl: uploadedUrl,
          necklineReferenceImageName: file.name,
          necklinePrompt: NECKLINE_REFERENCE_LOCKED_TEXT
        };
      });

      showNotification?.('Neckline reference image uploaded', 'success');
    } catch (error) {
      console.error('Error uploading neckline reference image:', error);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      onSettingsChange((prev) => ({
        ...prev,
        necklineReferenceImageUrl: '',
        necklineReferenceImageName: '',
        necklinePrompt: prev?.necklinePrompt === NECKLINE_REFERENCE_LOCKED_TEXT ? '' : prev?.necklinePrompt
      }));
      showNotification?.('Failed to upload reference image', 'error');
    } finally {
      setIsUploadingNecklineReference(false);
    }
  };

  const handleRemoveNecklineReference = () => {
    onSettingsChange((prev) => {
      if (prev?.necklineReferenceImageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.necklineReferenceImageUrl);
      }
      return {
        ...prev,
        necklineReferenceImageUrl: '',
        necklineReferenceImageName: '',
        necklinePrompt: prev?.necklinePrompt === NECKLINE_REFERENCE_LOCKED_TEXT ? '' : prev?.necklinePrompt
      };
    });
    if (necklineReferenceInputRef.current) {
      necklineReferenceInputRef.current.value = '';
    }
  };

  const handleSleeveReferenceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification?.('Please upload an image file', 'error');
      if (sleeveReferenceInputRef.current) {
        sleeveReferenceInputRef.current.value = '';
      }
      return;
    }

    let previewUrl = null;
    setIsUploadingSleeveReference(true);

    try {
      previewUrl = URL.createObjectURL(file);

      onSettingsChange((prev) => {
        if (prev?.sleeveReferenceImageUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(prev.sleeveReferenceImageUrl);
        }
        return {
          ...prev,
          sleeveReferenceImageUrl: previewUrl,
          sleeveReferenceImageName: file.name,
          sleevePrompt: SLEEVE_REFERENCE_LOCKED_TEXT
        };
      });

      const uploadResult = await uploadImageWithFallback(file, { folder: 'magic-edits/sleeve-references' });
      const uploadedUrl = uploadResult?.url || (typeof uploadResult === 'string' ? uploadResult : null);
      if (!uploadedUrl) {
        throw new Error('Upload did not return a URL');
      }

      onSettingsChange((prev) => {
        if (previewUrl && prev?.sleeveReferenceImageUrl === previewUrl) {
          URL.revokeObjectURL(previewUrl);
          previewUrl = null;
        }
        return {
          ...prev,
          sleeveReferenceImageUrl: uploadedUrl,
          sleeveReferenceImageName: file.name,
          sleevePrompt: SLEEVE_REFERENCE_LOCKED_TEXT
        };
      });

      showNotification?.('Sleeve reference image uploaded', 'success');
    } catch (error) {
      console.error('Error uploading sleeve reference image:', error);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      onSettingsChange((prev) => ({
        ...prev,
        sleeveReferenceImageUrl: '',
        sleeveReferenceImageName: '',
        sleevePrompt: prev?.sleevePrompt === SLEEVE_REFERENCE_LOCKED_TEXT ? '' : prev?.sleevePrompt
      }));
      showNotification?.('Failed to upload reference image', 'error');
    } finally {
      setIsUploadingSleeveReference(false);
    }
  };

  const handleRemoveSleeveReference = () => {
    onSettingsChange((prev) => {
      if (prev?.sleeveReferenceImageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.sleeveReferenceImageUrl);
      }
      return {
        ...prev,
        sleeveReferenceImageUrl: '',
        sleeveReferenceImageName: '',
        sleevePrompt: prev?.sleevePrompt === SLEEVE_REFERENCE_LOCKED_TEXT ? '' : prev?.sleevePrompt
      };
    });
    if (sleeveReferenceInputRef.current) {
      sleeveReferenceInputRef.current.value = '';
    }
  };

  const handleSilhouetteReferenceUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification?.('Please upload an image file', 'error');
      if (silhouetteReferenceInputRef.current) {
        silhouetteReferenceInputRef.current.value = '';
      }
      return;
    }

    let previewUrl = null;
    setIsUploadingSilhouetteReference(true);

    try {
      previewUrl = URL.createObjectURL(file);

      onSettingsChange((prev) => {
        if (prev?.silhouetteReferenceImageUrl?.startsWith('blob:')) {
          URL.revokeObjectURL(prev.silhouetteReferenceImageUrl);
        }
        return {
          ...prev,
          silhouetteReferenceImageUrl: previewUrl,
          silhouetteReferenceImageName: file.name,
          silhouettePrompt: SILHOUETTE_REFERENCE_LOCKED_TEXT
        };
      });

      const uploadResult = await uploadImageWithFallback(file, { folder: 'magic-edits/silhouette-references' });
      const uploadedUrl = uploadResult?.url || (typeof uploadResult === 'string' ? uploadResult : null);
      if (!uploadedUrl) {
        throw new Error('Upload did not return a URL');
      }

      onSettingsChange((prev) => {
        if (previewUrl && prev?.silhouetteReferenceImageUrl === previewUrl) {
          URL.revokeObjectURL(previewUrl);
          previewUrl = null;
        }
        return {
          ...prev,
          silhouetteReferenceImageUrl: uploadedUrl,
          silhouetteReferenceImageName: file.name,
          silhouettePrompt: SILHOUETTE_REFERENCE_LOCKED_TEXT
        };
      });

      showNotification?.('Silhouette reference image uploaded', 'success');
    } catch (error) {
      console.error('Error uploading silhouette reference image:', error);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      onSettingsChange((prev) => ({
        ...prev,
        silhouetteReferenceImageUrl: '',
        silhouetteReferenceImageName: '',
        silhouettePrompt: prev?.silhouettePrompt === SILHOUETTE_REFERENCE_LOCKED_TEXT ? '' : prev?.silhouettePrompt
      }));
      showNotification?.('Failed to upload reference image', 'error');
    } finally {
      setIsUploadingSilhouetteReference(false);
    }
  };

  const handleRemoveSilhouetteReference = () => {
    onSettingsChange((prev) => {
      if (prev?.silhouetteReferenceImageUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(prev.silhouetteReferenceImageUrl);
      }
      return {
        ...prev,
        silhouetteReferenceImageUrl: '',
        silhouetteReferenceImageName: '',
        silhouettePrompt: prev?.silhouettePrompt === SILHOUETTE_REFERENCE_LOCKED_TEXT ? '' : prev?.silhouettePrompt
      };
    });
    if (silhouetteReferenceInputRef.current) {
      silhouetteReferenceInputRef.current.value = '';
    }
  };

  return (
    <div
      className={[
        'flex flex-col bg-gradient-to-b from-white via-white to-slate-50/40',
        isMobileEditorPage ? 'p-3' : 'p-3 sm:p-4 md:p-5',
        'lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain lg:p-5 lg:[scrollbar-width:thin]',
      ].join(' ')}
    >
      <div className="flex flex-col gap-4 sm:gap-5">
        {(() => {
          const createdCustomisations = kit?.productDetails?.productTypeCustomizations;
          if (!createdCustomisations || typeof createdCustomisations !== 'object') return null;
          const entries = Object.entries(createdCustomisations)
            .map(([k, v]) => {
              const arr = Array.isArray(v) ? v : (v ? [v] : []);
              const first = arr.find(Boolean);
              return first ? { key: k, value: String(first) } : null;
            })
            .filter(Boolean);
          if (entries.length === 0) return null;

          const labelByKey = new Map((studioSchema || []).map((a) => [a.key, a.label]));

          return (
            <div className="shrink-0 rounded-2xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs font-semibold text-slate-700">Created with</div>
                <div className="text-[11px] text-slate-500">Original generate selections</div>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {entries.map((e) => (
                  <span
                    key={`${e.key}-${e.value}`}
                    className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700"
                    title={`${labelByKey.get(e.key) || e.key}: ${e.value}`}
                  >
                    <span className="text-slate-500">{labelByKey.get(e.key) || e.key}:</span>
                    <span className="text-slate-800">{e.value}</span>
                  </span>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Category tabs: horizontal scroll on mobile; wrap on large screens */}
        <div className="shrink-0 -mx-0.5 rounded-xl border border-slate-200/80 bg-white px-2 py-2 shadow-sm sm:-mx-1 sm:px-2.5 lg:sticky lg:top-0 lg:z-10 lg:bg-white/95 lg:backdrop-blur-sm">
          <div
            className="flex flex-nowrap gap-2 overflow-x-auto overscroll-x-contain pb-0.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin] snap-x snap-mandatory lg:flex-wrap lg:overflow-x-visible lg:pb-0"
            role="tablist"
            aria-label="Design options"
          >
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                title={tab.label}
                aria-selected={activeDesignTab === tab.id}
                onClick={() => openDesignTab(tab.id)}
                className={[
                  'shrink-0 snap-start whitespace-nowrap rounded-full border-2 px-3 py-2 text-xs font-semibold transition-all',
                  'lg:min-h-[2.75rem] lg:rounded-xl lg:px-2.5 lg:py-2 lg:text-left lg:leading-snug lg:whitespace-normal',
                  'lg:basis-[calc(25%-0.38rem)]',
                  activeDesignTab === tab.id
                    ? 'border-indigo-500 bg-indigo-600 text-white shadow-sm lg:bg-indigo-50 lg:text-indigo-900 lg:ring-1 lg:ring-indigo-200/60'
                    : 'border-slate-200/90 bg-white text-slate-600 hover:border-indigo-200 hover:bg-slate-50 lg:bg-slate-50/50',
                ].join(' ')}
              >
                <span className="lg:line-clamp-2">{tab.shortLabel ?? tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Customisation option icons — inline on mobile and desktop */}
        {studioSchema && studioSchema.length > 0 && studioSchema.some((a) => a.key === activeDesignTab) && (
          <div className="flex flex-col">
            {renderStudioSchemaTab(studioSchema.find((a) => a.key === activeDesignTab))}
          </div>
        )}

        {/* Neckline Options */}
        {!studioSchema && activeDesignTab === 'neckline' && (
          <div className="space-y-3 lg:space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-slate-900 lg:mb-2 lg:text-sm lg:text-gray-800">Neckline Style</h3>
              <p className="hidden text-xs text-gray-600 lg:mb-4 lg:block">
                Select the neckline style for your design
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-4 lg:gap-3">
              {necklineOptions.map((option) => {
                const isSelected = designSettings.neckline === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleCustomizeSelect('neckline', option.id)}
                    className={`p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                  >
                    <div className="flex items-center justify-center mb-2 h-12">
                      <img
                        src={option.icon}
                        alt={option.label}
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          // Fallback to text if image fails to load
                          e.target.style.display = 'none';
                          const fallback = e.target.nextSibling;
                          if (fallback) fallback.style.display = 'block';
                        }}
                      />
                      <span className="text-lg font-bold text-purple-700 hidden">{option.label.charAt(0)}</span>
                    </div>
                    <div className={`text-xs font-medium text-center ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                      {option.label}
                    </div>
                    {isSelected && (
                      <div className="mt-2">
                        <div className="w-full h-1 bg-purple-500 rounded-full"></div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Custom Prompt for Neckline — desktop only */}
            <div className="mt-6 hidden border-t border-gray-200 pt-4 lg:block">
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700">
                    Reference Image (Optional)
                  </label>
                  {designSettings.necklineReferenceImageUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveNecklineReference}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => necklineReferenceInputRef.current?.click()}
                  disabled={isUploadingNecklineReference}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-xs font-medium text-purple-700 disabled:opacity-60"
                >
                  {isUploadingNecklineReference ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Uploading reference image...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>{designSettings.necklineReferenceImageUrl ? 'Replace Reference Image' : 'Upload Reference Image'}</span>
                    </>
                  )}
                </button>
                <input
                  ref={necklineReferenceInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleNecklineReferenceUpload}
                  className="hidden"
                />
                {designSettings.necklineReferenceImageUrl && (
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <img
                      src={designSettings.necklineReferenceImageUrl}
                      alt="Neckline reference"
                      className="w-full max-h-64 object-contain rounded-md border border-gray-200 bg-white"
                    />
                    {designSettings.necklineReferenceImageName && (
                      <p className="text-[11px] text-gray-600 mt-1 truncate">{designSettings.necklineReferenceImageName}</p>
                    )}
                  </div>
                )}
              </div>

              <label className="text-sm font-semibold text-gray-800 block mb-2">
                Custom Neckline Instructions (Optional)
              </label>
              <textarea
                value={designSettings.necklinePrompt || ''}
                onChange={(e) => onSettingsChange(prev => ({ ...prev, necklinePrompt: e.target.value }))}
                placeholder="Enter custom instructions for the neckline (e.g., 'Add a deep V-neck with delicate lace trim', 'Create a high neckline with button details'). Leave empty to use the selected style above."
                readOnly={!!designSettings.necklineReferenceImageUrl}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none ${
                  designSettings.necklineReferenceImageUrl
                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                    : 'focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                }`}
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1 mb-3">
                {designSettings.necklineReferenceImageUrl
                  ? 'Prompt is auto-filled and locked when a reference image is attached'
                  : designSettings.necklinePrompt
                    ? 'Custom prompt will be used instead of the selected style'
                    : 'Customize how the neckline should be applied'}
              </p>
              
              {/* Apply Button */}
              {designSettings.necklinePrompt && (
                <button
                  onClick={async () => {
                    if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
                      showNotification?.('Please ensure a kit is imported and image is available', 'error');
                      return;
                    }

                    setIsApplying(true);
                    setIsMagicEditing?.(true);
                    setIsMagicEditPreviewLoading?.(true);

                    try {
                      const token = localStorage.getItem('token');
                      if (!token) {
                        showNotification?.('Authentication required. Please log in.', 'error');
                        return;
                      }

                      const prompt = designSettings.necklinePrompt.trim();
                      if (!prompt) {
                        showNotification?.('Please enter custom instructions', 'error');
                        return;
                      }

                      const referenceImageUrl = designSettings.necklineReferenceImageUrl?.trim();
                      if (referenceImageUrl && referenceImageUrl.startsWith('blob:')) {
                        showNotification?.('Please wait for reference image upload to finish', 'warning');
                        return;
                      }

                      // Get current main image (could be edited version)
                      const currentMainImage = selectedImageIndex === -1 
                        ? (kit?.generatedImageUrl || kit?.productDetails?.image)
                        : (magicEditImages?.[selectedImageIndex] || kit?.generatedImageUrl || kit?.productDetails?.image);
                      
                      const response = referenceImageUrl
                        ? await applyPartialDesignInspirationApi({
                            imageUrl: currentMainImage,
                            inspirationImageUrl: referenceImageUrl,
                            referenceImageUrl: referenceImageUrl,
                            designPart: 'neckline',
                            prompt: prompt,
                            originalKitId: kit._id,
                            createNewKit: true,
                            aspectRatio: "match_input_image"
                          }, token)
                        : await magicEditV2Api(
                            buildMagicEditV2Payload({
                              imageUrl: currentMainImage,
                              prompt,
                              originalKitId: kit._id,
                              kit,
                              lastClickedStyle: designSettings.neckline
                                ? { type: 'neckline', value: designSettings.neckline }
                                : null,
                            }),
                            token
                          );

                      if (response.data?.success) {
                        const editedUrl = response.data.data?.editedImageUrl;
                        if (editedUrl) {
                          // Add new edited image to the array and select it
                          setMagicEditImages?.(prev => {
                            const newImages = [...(prev || []), editedUrl];
                            // Select the newly created edit (it will be at the last index)
                            setSelectedImageIndex?.(newImages.length - 1);
                            return newImages;
                          });
                          showNotification?.('Neckline custom edit applied successfully!', 'success');
                        } else {
                          showNotification?.('Magic edit completed but no image was returned', 'warning');
                        }
                      } else {
                        showNotification?.(response.data?.message || 'Failed to apply custom neckline. Please try again.', 'error');
                      }
                    } catch (error) {
                      console.error('Custom neckline magic edit error:', error);
                      showNotification?.(error.response?.data?.message || 'Failed to apply custom neckline. Please try again.', 'error');
                    } finally {
                      setIsApplying(false);
                      setIsMagicEditing?.(false);
                      setIsMagicEditPreviewLoading?.(false);
                    }
                  }}
                  disabled={isApplying || isUploadingNecklineReference || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.necklinePrompt?.trim()}
                  className={`w-full px-4 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    isApplying || isUploadingNecklineReference || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.necklinePrompt?.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                  }`}
                >
                  {isApplying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      <span>Apply Custom Neckline</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Sleeve Options */}
        {!studioSchema && activeDesignTab === 'sleeve' && (
          <div className="space-y-3 lg:space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-slate-900 lg:mb-2 lg:text-sm lg:text-gray-800">Sleeve Style</h3>
              <p className="hidden text-xs text-gray-600 mb-4 lg:block">
                Choose the sleeve style for your garment
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-2 lg:gap-3">
              {sleeveOptions.map((option) => {
                const isSelected = designSettings.sleeve === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleCustomizeSelect('sleeve', option.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                  >
                    <div className="text-2xl font-bold mb-2 text-purple-700">{option.icon}</div>
                    <div className={`text-xs font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                      {option.label}
                    </div>
                    {isSelected && (
                      <div className="mt-2">
                        <div className="w-full h-1 bg-purple-500 rounded-full"></div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Custom Prompt for Sleeves — desktop only */}
            <div className="mt-6 hidden border-t border-gray-200 pt-4 lg:block">
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700">
                    Reference Image (Optional)
                  </label>
                  {designSettings.sleeveReferenceImageUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveSleeveReference}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => sleeveReferenceInputRef.current?.click()}
                  disabled={isUploadingSleeveReference}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-xs font-medium text-purple-700 disabled:opacity-60"
                >
                  {isUploadingSleeveReference ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Uploading reference image...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>{designSettings.sleeveReferenceImageUrl ? 'Replace Reference Image' : 'Upload Reference Image'}</span>
                    </>
                  )}
                </button>
                <input
                  ref={sleeveReferenceInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSleeveReferenceUpload}
                  className="hidden"
                />
                {designSettings.sleeveReferenceImageUrl && (
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <img
                      src={designSettings.sleeveReferenceImageUrl}
                      alt="Sleeve reference"
                      className="w-full max-h-64 object-contain rounded-md border border-gray-200 bg-white"
                    />
                    {designSettings.sleeveReferenceImageName && (
                      <p className="text-[11px] text-gray-600 mt-1 truncate">{designSettings.sleeveReferenceImageName}</p>
                    )}
                  </div>
                )}
              </div>

              <label className="text-sm font-semibold text-gray-800 block mb-2">
                Custom Sleeve Instructions (Optional)
              </label>
              <textarea
                value={designSettings.sleevePrompt || ''}
                onChange={(e) => onSettingsChange(prev => ({ ...prev, sleevePrompt: e.target.value }))}
                placeholder="Enter custom instructions for the sleeves (e.g., 'Add bell sleeves with ruffled cuffs', 'Create three-quarter length sleeves with button details'). Leave empty to use the selected style above."
                readOnly={!!designSettings.sleeveReferenceImageUrl}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none ${
                  designSettings.sleeveReferenceImageUrl
                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                    : 'focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                }`}
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1 mb-3">
                {designSettings.sleeveReferenceImageUrl
                  ? 'Prompt is auto-filled and locked when a reference image is attached'
                  : designSettings.sleevePrompt
                    ? 'Custom prompt will be used instead of the selected style'
                    : 'Customize how the sleeves should be applied'}
              </p>
              
              {/* Apply Button */}
              {designSettings.sleevePrompt && (
                <button
                  onClick={async () => {
                    if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
                      showNotification?.('Please ensure a kit is imported and image is available', 'error');
                      return;
                    }

                    setIsApplying(true);
                    setIsMagicEditing?.(true);
                    setIsMagicEditPreviewLoading?.(true);

                    try {
                      const token = localStorage.getItem('token');
                      if (!token) {
                        showNotification?.('Authentication required. Please log in.', 'error');
                        return;
                      }

                      const prompt = designSettings.sleevePrompt.trim();
                      if (!prompt) {
                        showNotification?.('Please enter custom instructions', 'error');
                        return;
                      }

                      const referenceImageUrl = designSettings.sleeveReferenceImageUrl?.trim();
                      if (referenceImageUrl && referenceImageUrl.startsWith('blob:')) {
                        showNotification?.('Please wait for reference image upload to finish', 'warning');
                        return;
                      }

                      // Get current main image (could be edited version)
                      const currentMainImage = selectedImageIndex === -1 
                        ? (kit?.generatedImageUrl || kit?.productDetails?.image)
                        : (magicEditImages?.[selectedImageIndex] || kit?.generatedImageUrl || kit?.productDetails?.image);

                      const response = referenceImageUrl
                        ? await applyPartialSleeveInspirationApi({
                            imageUrl: currentMainImage,
                            inspirationImageUrl: referenceImageUrl,
                            referenceImageUrl: referenceImageUrl,
                            designPart: 'sleeve',
                            prompt: prompt,
                            originalKitId: kit._id,
                            createNewKit: true,
                            aspectRatio: "match_input_image"
                          }, token)
                        : await magicEditV2Api(
                            buildMagicEditV2Payload({
                              imageUrl: currentMainImage,
                              prompt,
                              originalKitId: kit._id,
                              kit,
                              lastClickedStyle: designSettings.sleeve
                                ? { type: 'sleeve', value: designSettings.sleeve }
                                : null,
                            }),
                            token
                          );

                      if (response.data?.success) {
                        const editedUrl = response.data.data?.editedImageUrl;
                        if (editedUrl) {
                          // Add new edited image to the array and select it
                          setMagicEditImages?.(prev => {
                            const newImages = [...(prev || []), editedUrl];
                            // Select the newly created edit (it will be at the last index)
                            setSelectedImageIndex?.(newImages.length - 1);
                            return newImages;
                          });
                          showNotification?.('Sleeve custom edit applied successfully!', 'success');
                        } else {
                          showNotification?.('Magic edit completed but no image was returned', 'warning');
                        }
                      } else {
                        showNotification?.(response.data?.message || 'Failed to apply custom sleeves. Please try again.', 'error');
                      }
                    } catch (error) {
                      console.error('Custom sleeve magic edit error:', error);
                      showNotification?.(error.response?.data?.message || 'Failed to apply custom sleeves. Please try again.', 'error');
                    } finally {
                      setIsApplying(false);
                      setIsMagicEditing?.(false);
                      setIsMagicEditPreviewLoading?.(false);
                    }
                  }}
                  disabled={isApplying || isUploadingSleeveReference || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.sleevePrompt?.trim()}
                  className={`w-full px-4 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    isApplying || isUploadingSleeveReference || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.sleevePrompt?.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                  }`}
                >
                  {isApplying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      <span>Apply Custom Sleeves</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Silhouette Options */}
        {!studioSchema && activeDesignTab === 'silhouette' && (
          <div className="space-y-3 lg:space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-slate-900 lg:mb-2 lg:text-sm lg:text-gray-800">Fit & Silhouette</h3>
              <p className="hidden text-xs text-gray-600 mb-4 lg:block">
                Select the overall shape and fit of your garment
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:gap-3">
              {silhouetteOptions.map((option) => {
                const isSelected = designSettings.silhouette === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleCustomizeSelect('silhouette', option.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                  >
                    <div className="text-2xl font-bold mb-2 text-purple-700">{option.icon}</div>
                    <div className={`text-xs font-medium ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                      {option.label}
                    </div>
                    {isSelected && (
                      <div className="mt-2">
                        <div className="w-full h-1 bg-purple-500 rounded-full"></div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
            
            {/* Custom Prompt for Silhouette — desktop only */}
            <div className="mt-6 hidden border-t border-gray-200 pt-4 lg:block">
              <div className="mb-3 space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-700">
                    Reference Image (Optional)
                  </label>
                  {designSettings.silhouetteReferenceImageUrl && (
                    <button
                      type="button"
                      onClick={handleRemoveSilhouetteReference}
                      className="text-xs text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => silhouetteReferenceInputRef.current?.click()}
                  disabled={isUploadingSilhouetteReference}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-xs font-medium text-purple-700 disabled:opacity-60"
                >
                  {isUploadingSilhouetteReference ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Uploading reference image...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      <span>{designSettings.silhouetteReferenceImageUrl ? 'Replace Reference Image' : 'Upload Reference Image'}</span>
                    </>
                  )}
                </button>
                <input
                  ref={silhouetteReferenceInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleSilhouetteReferenceUpload}
                  className="hidden"
                />
                {designSettings.silhouetteReferenceImageUrl && (
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <img
                      src={designSettings.silhouetteReferenceImageUrl}
                      alt="Silhouette reference"
                      className="w-full max-h-64 object-contain rounded-md border border-gray-200 bg-white"
                    />
                    {designSettings.silhouetteReferenceImageName && (
                      <p className="text-[11px] text-gray-600 mt-1 truncate">{designSettings.silhouetteReferenceImageName}</p>
                    )}
                  </div>
                )}
              </div>

              <label className="text-sm font-semibold text-gray-800 block mb-2">
                Custom Silhouette Instructions (Optional)
              </label>
              <textarea
                value={designSettings.silhouettePrompt || ''}
                onChange={(e) => onSettingsChange(prev => ({ ...prev, silhouettePrompt: e.target.value }))}
                placeholder="Enter custom instructions for the silhouette (e.g., 'Create a fitted bodice with a flared A-line skirt', 'Make it a relaxed fit with subtle draping'). Leave empty to use the selected style above."
                readOnly={!!designSettings.silhouetteReferenceImageUrl}
                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none ${
                  designSettings.silhouetteReferenceImageUrl
                    ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                    : 'focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                }`}
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1 mb-3">
                {designSettings.silhouetteReferenceImageUrl
                  ? 'Prompt is auto-filled and locked when a reference image is attached'
                  : designSettings.silhouettePrompt
                    ? 'Custom prompt will be used instead of the selected style'
                    : 'Customize how the silhouette should be applied'}
              </p>
              
              {/* Apply Button */}
              {designSettings.silhouettePrompt && (
                <button
                  onClick={async () => {
                    if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
                      showNotification?.('Please ensure a kit is imported and image is available', 'error');
                      return;
                    }

                    setIsApplying(true);
                    setIsMagicEditing?.(true);
                    setIsMagicEditPreviewLoading?.(true);

                    try {
                      const token = localStorage.getItem('token');
                      if (!token) {
                        showNotification?.('Authentication required. Please log in.', 'error');
                        return;
                      }

                      const prompt = designSettings.silhouettePrompt.trim();
                      if (!prompt) {
                        showNotification?.('Please enter custom instructions', 'error');
                        return;
                      }

                      const referenceImageUrl = designSettings.silhouetteReferenceImageUrl?.trim();
                      if (referenceImageUrl && referenceImageUrl.startsWith('blob:')) {
                        showNotification?.('Please wait for reference image upload to finish', 'warning');
                        return;
                      }

                      // Get current main image (could be edited version)
                      const currentMainImage = selectedImageIndex === -1 
                        ? (kit?.generatedImageUrl || kit?.productDetails?.image)
                        : (magicEditImages?.[selectedImageIndex] || kit?.generatedImageUrl || kit?.productDetails?.image);
                      
                      const response = referenceImageUrl
                        ? await applyPartialSilhouetteInspirationApi({
                            imageUrl: currentMainImage,
                            inspirationImageUrl: referenceImageUrl,
                            referenceImageUrl: referenceImageUrl,
                            designPart: 'silhouette',
                            prompt: prompt,
                            originalKitId: kit._id,
                            createNewKit: true,
                            aspectRatio: "match_input_image"
                          }, token)
                        : await magicEditV2Api(
                            buildMagicEditV2Payload({
                              imageUrl: currentMainImage,
                              prompt,
                              originalKitId: kit._id,
                              kit,
                              lastClickedStyle: designSettings.silhouette
                                ? { type: 'silhouette', value: designSettings.silhouette }
                                : null,
                            }),
                            token
                          );

                      if (response.data?.success) {
                        const editedUrl = response.data.data?.editedImageUrl;
                        if (editedUrl) {
                          // Add new edited image to the array and select it
                          setMagicEditImages?.(prev => {
                            const newImages = [...(prev || []), editedUrl];
                            // Select the newly created edit (it will be at the last index)
                            setSelectedImageIndex?.(newImages.length - 1);
                            return newImages;
                          });
                          showNotification?.('Silhouette custom edit applied successfully!', 'success');
                        } else {
                          showNotification?.('Magic edit completed but no image was returned', 'warning');
                        }
                      } else {
                        showNotification?.(response.data?.message || 'Failed to apply custom silhouette. Please try again.', 'error');
                      }
                    } catch (error) {
                      console.error('Custom silhouette magic edit error:', error);
                      showNotification?.(error.response?.data?.message || 'Failed to apply custom silhouette. Please try again.', 'error');
                    } finally {
                      setIsApplying(false);
                      setIsMagicEditing?.(false);
                      setIsMagicEditPreviewLoading?.(false);
                    }
                  }}
                  disabled={isApplying || isUploadingSilhouetteReference || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.silhouettePrompt?.trim()}
                  className={`w-full px-4 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    isApplying || isUploadingSilhouetteReference || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.silhouettePrompt?.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                  }`}
                >
                  {isApplying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      <span>Apply Custom Silhouette</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Garment length (hem / overall length) */}
        {!studioSchema && activeDesignTab === 'length' && (
          <div className="space-y-3 lg:space-y-4">
            <div>
              <h3 className="text-xs font-semibold text-slate-900 lg:mb-2 lg:text-sm lg:text-gray-800">Garment length</h3>
              <p className="hidden text-xs text-gray-600 mb-4 lg:block">
                Shorten or lengthen the hem and overall vertical length. Works for tops, dresses, kurtas, trousers, skirts, and similar pieces.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2.5 lg:gap-3">
              {garmentLengthOptions.map((option) => {
                const isSelected = designSettings.garmentLength === option.id;
                return (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleCustomizeSelect('garmentLength', option.id)}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                    }`}
                  >
                    <div className="text-xl font-bold mb-2 text-purple-700 font-mono">{option.icon}</div>
                    <div className={`text-xs font-medium text-left ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                      {option.label}
                    </div>
                    {isSelected && (
                      <div className="mt-2">
                        <div className="w-full h-1 bg-purple-500 rounded-full" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mt-6 hidden border-t border-gray-200 pt-4 lg:block">
              <label className="text-sm font-semibold text-gray-800 block mb-2">
                Custom length instructions (optional)
              </label>
              <textarea
                value={designSettings.garmentLengthPrompt || ''}
                onChange={(e) => onSettingsChange(prev => ({ ...prev, garmentLengthPrompt: e.target.value }))}
                placeholder="e.g. 'Crop to waist length', 'Extend to ankle', 'Make it midi length'. Leave empty to use the preset buttons above."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1 mb-3">
                {designSettings.garmentLengthPrompt?.trim()
                  ? 'Custom prompt will be used when you click a length preset above (same behavior as silhouette). You can also apply with the button below.'
                  : 'Describe a specific length change and click Apply below.'}
              </p>
              {designSettings.garmentLengthPrompt?.trim() && (
                <button
                  onClick={async () => {
                    if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
                      showNotification?.('Please ensure a kit is imported and image is available', 'error');
                      return;
                    }

                    setIsApplying(true);
                    setIsMagicEditing?.(true);
                    setIsMagicEditPreviewLoading?.(true);

                    try {
                      const token = localStorage.getItem('token');
                      if (!token) {
                        showNotification?.('Authentication required. Please log in.', 'error');
                        return;
                      }

                      const prompt = designSettings.garmentLengthPrompt.trim();
                      if (!prompt) {
                        showNotification?.('Please enter custom instructions', 'error');
                        return;
                      }

                      const currentMainImage = selectedImageIndex === -1
                        ? (kit?.generatedImageUrl || kit?.productDetails?.image)
                        : (magicEditImages?.[selectedImageIndex] || kit?.generatedImageUrl || kit?.productDetails?.image);

                      const response = await magicEditV2Api({
                        imageUrl: currentMainImage,
                        prompt: `${prompt} Preserve fabric, print, colors, and design details; adjust only garment length and proportions naturally.`,
                        originalKitId: kit._id,
                        createNewKit: true,
                        aspectRatio: 'match_input_image'
                      }, token);

                      if (response.data?.success) {
                        const editedUrl = response.data.data?.editedImageUrl;
                        if (editedUrl) {
                          setMagicEditImages?.(prev => {
                            const newImages = [...(prev || []), editedUrl];
                            setSelectedImageIndex?.(newImages.length - 1);
                            return newImages;
                          });
                          showNotification?.('Custom length applied successfully!', 'success');
                        } else {
                          showNotification?.('Magic edit completed but no image was returned', 'warning');
                        }
                      } else {
                        showNotification?.(response.data?.message || 'Failed to apply length change. Please try again.', 'error');
                      }
                    } catch (error) {
                      console.error('Custom garment length magic edit error:', error);
                      showNotification?.(error.response?.data?.message || 'Failed to apply length change. Please try again.', 'error');
                    } finally {
                      setIsApplying(false);
                      setIsMagicEditing?.(false);
                      setIsMagicEditPreviewLoading?.(false);
                    }
                  }}
                  disabled={isApplying || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.garmentLengthPrompt?.trim()}
                  className={`w-full px-4 py-2.5 rounded-lg font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                    isApplying || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image) || !designSettings.garmentLengthPrompt?.trim()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                  }`}
                >
                  {isApplying ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Applying...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-4 h-4" />
                      <span>Apply custom length</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}

        {activeDesignTab === 'color' && (
          <FabricColorStudioSection
            embedded
            kit={kit}
            fabricProduct={studioFabricProduct}
            fabricProductLoading={studioFabricProductLoading}
            showNotification={showNotification}
            magicEditImages={magicEditImages}
            selectedImageIndex={selectedImageIndex}
            setMagicEditImages={setMagicEditImages}
            setSelectedImageIndex={setSelectedImageIndex}
            setIsMagicEditing={setIsMagicEditing}
            setIsMagicEditPreviewLoading={setIsMagicEditPreviewLoading}
            onMobileApplyStart={isMobileEditorPage ? onExitMobileEditor : undefined}
          />
        )}
      </div>
    </div>
  );
};

const DesignVersionsPanel = ({
  originalMainImage,
  magicEditImages,
  selectedImageIndex,
  setSelectedImageIndex,
  onDeleteVersion,
  isEditorPanelCollapsed,
  variant = 'sidebar',
}) => {
  const desktopScrollRef = useRef(null);

  // React's onWheel is passive — preventDefault only works with a non-passive native listener.
  useEffect(() => {
    if (variant === 'strip') return;
    const el = desktopScrollRef.current;
    if (!el) return;
    const onWheel = (event) => {
      if (el.scrollHeight <= el.clientHeight) return;
      el.scrollTop += event.deltaY;
      event.preventDefault();
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [variant]);

  const isCompactStrip = variant === 'strip' || variant === 'overlay';

  const renderThumbnail = (imageUrl, index, label, isSelected, isOriginal = false) => (
    <div
      key={isOriginal ? 'original' : index}
      className={`relative flex-shrink-0 ${isCompactStrip ? 'h-[4.5rem] w-14' : 'h-32 w-24'}`}
    >
      <button
        type="button"
        onClick={() => setSelectedImageIndex(isOriginal ? -1 : index)}
        className={`relative h-full w-full overflow-hidden rounded-lg border-2 transition-all ${
          isSelected
            ? 'border-purple-600 ring-2 ring-purple-300 shadow-md'
            : 'border-gray-200 active:border-purple-300'
        }`}
        title={label}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={label}
            className="h-full w-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-gray-100">
            <ImageIcon className="h-4 w-4 text-gray-400" />
          </div>
        )}
        <div
          className={`absolute bottom-0 left-0 right-0 px-1 py-0.5 text-center font-semibold ${
            isCompactStrip ? 'text-[8px]' : 'text-[9px]'
          } ${isSelected ? 'bg-purple-600 text-white' : 'bg-gray-800/70 text-white'}`}
        >
          {label}
        </div>
      </button>
      {!isOriginal && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm('Are you sure you want to delete this design version?')) {
              onDeleteVersion(index);
            }
          }}
          className={`absolute flex items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition-colors hover:bg-red-600 ${
            isCompactStrip ? 'right-0 top-0 h-4 w-4' : '-right-1 -top-1 h-5 w-5'
          }`}
          title="Delete this version"
        >
          <X className={isCompactStrip ? 'h-2.5 w-2.5' : 'h-3 w-3'} />
        </button>
      )}
    </div>
  );

  if (variant === 'strip') {
    return (
      <div className="mt-2 shrink-0 border-t border-purple-100/90 pt-2 lg:hidden">
        <div className="mb-1.5 flex items-center justify-between gap-2 px-0.5">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-purple-700">Design versions</p>
          <span className="text-[10px] text-gray-500">{magicEditImages.length + 1} total</span>
        </div>
        <div className="flex gap-2.5 overflow-x-auto overscroll-x-contain py-1 pr-3 [-webkit-overflow-scrolling:touch] [scrollbar-width:thin]">
          {renderThumbnail(originalMainImage, -1, 'Original', selectedImageIndex === -1, true)}
          {magicEditImages.map((imageUrl, index) =>
            renderThumbnail(
              imageUrl,
              index,
              `Edit ${index + 1}`,
              selectedImageIndex === index
            )
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`hidden lg:flex flex-shrink-0 mb-4 lg:mb-0 lg:sticky lg:top-6 bg-white/90 rounded-2xl shadow-xl border border-purple-100 overflow-hidden transition-all duration-300 flex-col min-h-0 ${isEditorPanelCollapsed ? 'lg:w-64' : 'lg:w-64'} lg:h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-8rem)]`}
    >
      <div className="p-3 sm:p-4 flex-shrink-0 border-b border-purple-50">
        <div className="flex items-center justify-between">
          <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide">Design Versions</p>
          <span className="text-xs text-gray-500">{magicEditImages.length + 1} version{magicEditImages.length !== 0 ? 's' : ''}</span>
        </div>
      </div>

      {/* Desktop: Vertical List */}
      <div
        ref={desktopScrollRef}
        className="hidden lg:block flex-1 min-h-0 overflow-y-auto overscroll-y-contain"
      >
        <div className="p-3 sm:p-4 pt-3 pr-2 flex flex-col gap-2">
          <button
            onClick={() => setSelectedImageIndex(-1)}
            className={`relative w-full aspect-[3/4] rounded-lg border-2 overflow-hidden transition-all ${
              selectedImageIndex === -1
                ? 'border-purple-600 ring-2 ring-purple-300 shadow-md'
                : 'border-gray-200 hover:border-purple-300'
            }`}
            title="Original Design"
          >
            {originalMainImage ? (
              <img
                src={originalMainImage}
                alt="Original"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-gray-400" />
              </div>
            )}
            <div className={`absolute bottom-0 left-0 right-0 text-[10px] font-semibold px-2 py-1 text-center ${
              selectedImageIndex === -1 ? 'bg-purple-600 text-white' : 'bg-gray-800/70 text-white'
            }`}>
              Original
            </div>
          </button>

          {magicEditImages.map((imageUrl, index) => (
            <div
              key={index}
              className="relative w-full aspect-[3/4] group"
            >
              <button
                onClick={() => setSelectedImageIndex(index)}
                className={`relative w-full h-full rounded-lg border-2 overflow-hidden transition-all ${
                  selectedImageIndex === index
                    ? 'border-purple-600 ring-2 ring-purple-300 shadow-md'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
                title={`Edit ${index + 1}`}
              >
                <img
                  src={imageUrl}
                  alt={`Edit ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                <div className={`absolute bottom-0 left-0 right-0 text-[10px] font-semibold px-2 py-1 text-center ${
                  selectedImageIndex === index ? 'bg-purple-600 text-white' : 'bg-gray-800/70 text-white'
                }`}>
                  Edit {index + 1}
                </div>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm('Are you sure you want to delete this design version?')) {
                    onDeleteVersion(index);
                  }
                }}
                className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                title="Delete this version"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Lazy-loaded Fabric Item Component (optimized with React.memo)
const FabricItem = React.memo(({ fabric, onSelect }) => {
  const [isInView, setIsInView] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef(null);

  // Intersection Observer for lazy loading images
  useEffect(() => {
    const currentRef = imgRef.current;
    if (!currentRef) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      { rootMargin: '50px' } // Start loading 50px before image enters viewport
    );

    observer.observe(currentRef);

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const fabricImage = fabric.image || fabric.imageUrl || '';
  const fabricName = fabric.name || 'Untitled Fabric';

  return (
    <button
      onClick={() => onSelect(fabric)}
      className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer text-left"
    >
      <div 
        ref={imgRef}
        className="aspect-square bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center relative overflow-hidden"
      >
        {isInView && fabricImage && !imageError ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-purple-400" />
              </div>
            )}
            <img
              src={fabricImage}
              alt={fabricName}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              onLoad={() => setImageLoaded(true)}
              onError={() => {
                setImageError(true);
                setImageLoaded(false);
              }}
              loading="lazy"
            />
          </>
        ) : (
          <div className="text-center px-4">
            <Palette className="w-12 h-12 mx-auto text-gray-400 mb-2" />
            <p className="text-xs text-gray-500">{imageError ? 'Failed to load' : 'No Image'}</p>
          </div>
        )}
      </div>
      <div className="p-3">
        <h4 className="font-semibold text-gray-800 truncate text-sm mb-1">
          {fabricName}
        </h4>
        {fabric.description && (
          <p className="text-xs text-gray-500 line-clamp-2">
            {fabric.description}
          </p>
        )}
      </div>
    </button>
  );
});

FabricItem.displayName = 'FabricItem';

// Multi-Fabric Combination Section Component
const MultiFabricSection = ({ settings, onSettingsChange, showNotification, kit, mainImage, originalMainImage, setIsMagicEditing, setIsMagicEditPreviewLoading, setMagicEditImages, setSelectedImageIndex, setActiveView }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [showFabricLibrary, setShowFabricLibrary] = useState(false);
  const [fabrics, setFabrics] = useState([]);
  const [isLoadingFabrics, setIsLoadingFabrics] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [fabricSearchTerm, setFabricSearchTerm] = useState('');
  const [fabricPage, setFabricPage] = useState(1);
  const [hasMoreFabrics, setHasMoreFabrics] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const loadMoreRef = useRef(null);
  const scrollContainerRef = useRef(null);

  const handleFabricUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification?.('Please upload an image file', 'error');
      return;
    }

    setUploading(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      onSettingsChange(prev => ({
        ...prev,
        secondFabric: {
          id: Date.now(),
          name: file.name,
          imageUrl: previewUrl,
          file: file
        }
      }));
      showNotification?.('Second fabric uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading fabric:', error);
      showNotification?.('Failed to upload fabric', 'error');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Helper function to build prompt from application areas
  const buildPromptFromAppliedAreas = (appliedTo) => {
    const areaNames = {
      yoke: 'yoke/neckline area',
      sleeves: 'sleeves',
      borders: 'borders/hem',
      contrastPanels: 'contrast panels'
    };

    const appliedAreas = Object.entries(appliedTo)
      .filter(([_, value]) => value)
      .map(([key]) => areaNames[key] || key);

    if (appliedAreas.length === 0) {
      return '';
    }

    const areasText = appliedAreas.join(', ');
    return `Apply the second fabric to the ${areasText}.`;
  };

  const handleToggleApplication = (area) => {
    const newState = !settings.appliedTo[area];
    const newAppliedTo = {
      ...settings.appliedTo,
      [area]: newState
    };

    // Auto-update the prompt based on selected areas
    // This will automatically update whenever areas are toggled
    const autoGeneratedPrompt = buildPromptFromAppliedAreas(newAppliedTo);
    
    onSettingsChange(prev => ({
      ...prev,
      appliedTo: newAppliedTo,
      // Auto-update prompt to reflect selected areas
      editPrompt: autoGeneratedPrompt
    }));
    
    showNotification?.(`${area.charAt(0).toUpperCase() + area.slice(1)} ${newState ? 'enabled' : 'disabled'}`, 'info');
  };

  const handleRemoveFabric = () => {
    onSettingsChange(prev => ({
      ...prev,
      secondFabric: null,
      enabled: false,
      appliedTo: {
        yoke: false,
        sleeves: false,
        borders: false,
        contrastPanels: false
      },
      editPrompt: ''
    }));
    showNotification?.('Second fabric removed', 'info');
  };

  // Fetch fabrics from library (optimized with useCallback)
  const fetchFabrics = useCallback(async (page = 1, search = '', append = false) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoadingFabrics(true);
    }
    
    try {
      const trimmedSearch = (search || '').trim();
      const response = trimmedSearch
        ? await semanticSearchProducts({
            queryText: trimmedSearch,
            page,
            limit: 20,
            filters: { hasCollection: 'false' },
            // Keep candidate count moderate for responsive modal search.
            candidateLimit: 250,
            maxNewEmbeddings: 10
          })
        : await getProducts(page, 20, '', { hasCollection: 'false' });
      const fetchedFabrics = response?.products || [];
      
      if (append) {
        setFabrics(prev => {
          // Prevent duplicates
          const existingIds = new Set(prev.map(f => f._id));
          const newFabrics = fetchedFabrics.filter(f => !existingIds.has(f._id));
          return [...prev, ...newFabrics];
        });
      } else {
        setFabrics(fetchedFabrics);
      }
      
      setHasMoreFabrics(response?.pagination?.hasNextPage || false);
      setFabricPage(page);
    } catch (error) {
      console.error('Error fetching fabrics:', error);
      showNotification?.('Failed to load fabrics', 'error');
    } finally {
      setIsLoadingFabrics(false);
      setIsLoadingMore(false);
    }
  }, [showNotification]);

  // Load fabrics when library modal opens
  useEffect(() => {
    if (showFabricLibrary) {
      setFabrics([]);
      setFabricPage(1);
      fetchFabrics(1, fabricSearchTerm, false);
    }
  }, [showFabricLibrary, fetchFabrics]);

  // Debounce search
  useEffect(() => {
    if (!showFabricLibrary) return;
    
    const timer = setTimeout(() => {
      setFabrics([]);
      setFabricPage(1);
      fetchFabrics(1, fabricSearchTerm, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [fabricSearchTerm, showFabricLibrary, fetchFabrics]);

  // Infinite scroll with Intersection Observer
  useEffect(() => {
    if (!showFabricLibrary || !hasMoreFabrics || isLoadingMore || isLoadingFabrics || !loadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && hasMoreFabrics && !isLoadingMore && !isLoadingFabrics) {
          const nextPage = fabricPage + 1;
          fetchFabrics(nextPage, fabricSearchTerm, true);
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '200px', // Start loading 200px before reaching the bottom for smoother experience
        threshold: 0.1
      }
    );

    const currentLoadMoreRef = loadMoreRef.current;
    observer.observe(currentLoadMoreRef);

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [showFabricLibrary, hasMoreFabrics, isLoadingMore, isLoadingFabrics, fabricPage, fabricSearchTerm, fetchFabrics]);

  // Handle fabric selection from library (optimized with useCallback)
  const handleSelectFabric = useCallback((fabric) => {
    // Extract fabric image URL from various possible properties
    const fabricImageUrl = fabric.image || 
                          fabric.imageUrl || 
                          fabric.images?.original?.s3Url || 
                          fabric.images?.original?.imgbbUrl || 
                          fabric.images?.original?.url || 
                          '';
    
    onSettingsChange(prev => ({
      ...prev,
      secondFabric: {
        id: fabric._id,
        name: fabric.name || 'Selected Fabric',
        imageUrl: fabricImageUrl,
        fabricId: fabric._id
      }
    }));
    setShowFabricLibrary(false);
    setFabricSearchTerm('');
    showNotification?.('Fabric selected successfully', 'success');
  }, [onSettingsChange, showNotification]);

  // Memoize application areas — filter out irrelevant ones based on product type
  const applicationAreas = useMemo(() => {
    const pt = kit?.productDetails?.productType || '';
    const bottomWear = isBottomWear(pt);
    const sleeveless = isSleevelessType(pt);

    const areas = [];
    if (!bottomWear) areas.push({ id: 'yoke', label: 'Yoke', icon: '▱', description: 'Apply to neckline/yoke area' });
    if (!bottomWear && !sleeveless) areas.push({ id: 'sleeves', label: 'Sleeves', icon: '◐', description: 'Apply to sleeves' });
    areas.push({ id: 'borders', label: 'Borders', icon: '▬', description: 'Apply to borders/hem' });
    areas.push({ id: 'contrastPanels', label: 'Contrast Panels', icon: '▦', description: 'Add contrast panels' });
    return areas;
  }, [kit?.productDetails?.productType]);

  return (
    <div className="p-4 sm:p-6 max-h-[500px] overflow-y-auto">
      <div className="space-y-6">
        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Two-Fabric / Multi-Fabric Combination</h3>
          <p className="text-xs text-gray-600 mb-4">
            Combine two fabrics in a single design. Perfect for ethnic wear, fusion wear, bridal wear, and modern tops.
          </p>
        </div>

        <>
            {/* Second Fabric Upload */}
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-700 mb-2 block">Second Fabric</label>
                {settings.secondFabric ? (
                  <div className="relative group border-2 border-purple-300 rounded-lg overflow-hidden">
                    <img
                      src={settings.secondFabric.imageUrl}
                      alt={settings.secondFabric.name}
                      className="w-full h-32 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={handleRemoveFabric}
                        className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600"
                      >
                        Remove Fabric
                      </button>
                    </div>
                    <div className="p-2 bg-white">
                      <p className="text-xs text-gray-700 truncate">{settings.secondFabric.name}</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="w-full flex flex-col items-center justify-center gap-2 px-4 py-6 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-sm font-medium text-purple-700 disabled:opacity-50"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span>Uploading...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-5 h-5" />
                          <span>Upload Second Fabric</span>
                          <span className="text-xs text-gray-500">or select from fabric library</span>
                        </>
                      )}
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFabricUpload}
                      className="hidden"
                    />
                    <button
                      onClick={() => {
                        // Check if mobile (screen width < 768px)
                        const isMobile = window.innerWidth < 768;
                        if (isMobile && setActiveView) {
                          // Store callback and context in sessionStorage for the fabric library page
                          sessionStorage.setItem('fabricLibraryCallback', JSON.stringify({
                            type: 'multi-fabric'
                          }));
                          setActiveView('fabric-library');
                        } else {
                          setShowFabricLibrary(true);
                        }
                      }}
                      className="w-full px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs font-medium flex items-center justify-center gap-2"
                    >
                      <Palette className="w-4 h-4" />
                      Browse Fabric Library
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Application Areas */}
            {settings.secondFabric && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-3 block">Apply Second Fabric To:</label>
                  <div className="grid grid-cols-2 gap-3">
                    {applicationAreas.map((area) => {
                      const isSelected = settings.appliedTo[area.id];
                      return (
                        <button
                          key={area.id}
                          onClick={() => handleToggleApplication(area.id)}
                          className={`p-3 rounded-xl border-2 transition-all text-left ${
                            isSelected
                              ? 'border-purple-500 bg-purple-50 shadow-md'
                              : 'border-gray-200 hover:border-purple-300 hover:bg-purple-50/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xl">{area.icon}</span>
                            <span className={`text-xs font-semibold ${isSelected ? 'text-purple-700' : 'text-gray-700'}`}>
                              {area.label}
                            </span>
                            {isSelected && (
                              <Check className="w-3 h-3 text-purple-600 ml-auto" />
                            )}
                          </div>
                          <p className="text-xs text-gray-500">{area.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Advanced Options */}
                {settings.appliedTo.contrastPanels && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="text-xs font-medium text-gray-700 mb-2 block">Contrast Panel Position</label>
                    <select
                      value={settings.contrastPanelPosition}
                      onChange={(e) => {
                        onSettingsChange(prev => {
                          const updated = { ...prev, contrastPanelPosition: e.target.value };
                          // Auto-update prompt when position changes
                          updated.editPrompt = buildPromptFromAppliedAreas(prev.appliedTo);
                          return updated;
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="front">Front Panel</option>
                      <option value="back">Back Panel</option>
                      <option value="sides">Side Panels</option>
                      <option value="all">All Panels</option>
                    </select>
                  </div>
                )}

                {settings.appliedTo.borders && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <label className="text-xs font-medium text-gray-700 mb-2 block">Border Position</label>
                    <select
                      value={settings.borderPosition}
                      onChange={(e) => {
                        onSettingsChange(prev => {
                          const updated = { ...prev, borderPosition: e.target.value };
                          // Auto-update prompt when position changes
                          updated.editPrompt = buildPromptFromAppliedAreas(prev.appliedTo);
                          return updated;
                        });
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="bottom">Bottom Hem</option>
                      <option value="top">Top Edge</option>
                      <option value="sides">Side Edges</option>
                      <option value="all">All Edges</option>
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* Edit Prompt */}
            {settings.secondFabric && (
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-700 block">Edit Prompt (Optional)</label>
                <textarea
                  value={settings.editPrompt || ''}
                  onChange={(e) => onSettingsChange(prev => ({ ...prev, editPrompt: e.target.value }))}
                  placeholder="Enter specific instructions for how to apply the second fabric (e.g., 'Blend seamlessly at the yoke', 'Create a gradient transition')"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  rows={3}
                />
                <p className="text-xs text-gray-500">Provide additional guidance for the AI on how to apply the second fabric</p>
              </div>
            )}

            {/* Apply Button */}
            {settings.secondFabric && (
                <div className="mt-4">
                  <button
                    onClick={async () => {
                      if (!kit?._id || !mainImage || !settings.secondFabric?.imageUrl) {
                        showNotification?.('Please ensure a kit is imported, image is available, and fabric is selected', 'error');
                        return;
                      }

                      setIsApplying(true);
                      setIsMagicEditing?.(true);
                      setIsMagicEditPreviewLoading?.(true);

                      try {
                        const token = localStorage.getItem('token');
                        if (!token) {
                          showNotification?.('Authentication required. Please log in.', 'error');
                          return;
                        }

                        // Build prompt from settings
                        let prompt = settings.editPrompt || '';
                        
                        // Build fabric application areas description
                        const appliedAreas = Object.entries(settings.appliedTo)
                          .filter(([_, value]) => value)
                          .map(([key]) => {
                            const areaNames = {
                              yoke: 'yoke/neckline area',
                              sleeves: 'sleeves',
                              borders: 'borders/hem',
                              contrastPanels: 'contrast panels'
                            };
                            return areaNames[key] || key;
                          });

                        if (appliedAreas.length > 0) {
                          const areasText = appliedAreas.join(', ');
                          const fabricPrompt = `Apply the second fabric to the ${areasText}. ${settings.editPrompt ? 'Additional instructions: ' + prompt : ''}`;
                          prompt = fabricPrompt.trim();
                        } else if (!prompt) {
                          prompt = 'Apply the second fabric to the garment.';
                        }

                        const response = await magicEditV2Api({
                          imageUrl: mainImage,
                          originalImageUrl: kit.productDetails?.image || originalMainImage,
                          prompt: prompt,
                          originalKitId: kit._id,
                          createNewKit: true,
                          fabricImageUrl: settings.secondFabric.imageUrl,
                          aspectRatio: "match_input_image"
                        }, token);

                        if (response.data?.success) {
                          const editedUrl = response.data.data?.editedImageUrl;
                          if (editedUrl) {
                            // Add new edited image to the array and select it
                            setMagicEditImages?.(prev => {
                              const newImages = appendCappedUrls(prev, editedUrl, 20);
                              // Select the newly created edit (it will be at the last index)
                              setSelectedImageIndex?.(newImages.length - 1);
                              return newImages;
                            });
                            showNotification?.('Multi-fabric magic edit applied successfully!', 'success');
                          } else {
                            showNotification?.('Magic edit completed but no image was returned', 'warning');
                          }
                        } else {
                          showNotification?.(response.data?.message || 'Magic edit failed. Please try again.', 'error');
                        }
                      } catch (error) {
                        console.error('Magic edit error:', error);
                        showNotification?.(error.response?.data?.message || 'Failed to apply magic edit. Please try again.', 'error');
                      } finally {
                        setIsApplying(false);
                        setIsMagicEditing?.(false);
                        setIsMagicEditPreviewLoading?.(false);
                      }
                    }}
                    disabled={isApplying || !settings.secondFabric || !kit?._id || !mainImage}
                    className={`w-full px-4 py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                      isApplying || !settings.secondFabric || !kit?._id || !mainImage
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                    }`}
                  >
                    {isApplying ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Applying Magic Edit...</span>
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        <span>Apply Magic Edit</span>
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    This will apply the selected fabric to the garment using AI magic edit
                  </p>
                </div>
            )}
          </>
      </div>

      {/* Fabric Library Modal */}
      {showFabricLibrary && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">Select Fabric</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Choose from your uploaded fabrics</p>
              </div>
              <button
                onClick={() => {
                  setShowFabricLibrary(false);
                  setFabricSearchTerm('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search fabrics..."
                  value={fabricSearchTerm}
                  onChange={(e) => setFabricSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Fabrics Grid with Infinite Scroll */}
            <div 
              ref={scrollContainerRef}
              className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6"
            >
              {isLoadingFabrics && fabrics.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : fabrics.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Palette className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm sm:text-base text-gray-600">
                    {fabricSearchTerm ? 'No fabrics found matching your search' : 'No fabrics available'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {fabrics.map((fabric) => (
                      <FabricItem
                        key={fabric._id}
                        fabric={fabric}
                        onSelect={handleSelectFabric}
                      />
                    ))}
                  </div>
                  
                  {/* Infinite Scroll Trigger Element */}
                  {hasMoreFabrics && (
                    <div 
                      ref={loadMoreRef}
                      className="flex items-center justify-center py-4"
                    >
                      {isLoadingMore && (
                        <div className="flex items-center gap-2 text-purple-600">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm">Loading more fabrics...</span>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* End of list indicator */}
                  {!hasMoreFabrics && fabrics.length > 0 && (
                    <div className="text-center py-4">
                      <p className="text-xs text-gray-500">All fabrics loaded</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Digital Appliqué Section Component
const AppliqueSection = ({ items, onAdd, onRemove }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [appliqueStyle, setAppliqueStyle] = useState('flat');

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      const newItem = {
        id: Date.now(),
        name: file.name,
        imageUrl: previewUrl,
        file: file,
        style: appliqueStyle,
        position: { x: 50, y: 50 },
        rotation: 0,
        scale: 100
      };
      onAdd(newItem);
    } catch (error) {
      console.error('Error uploading file:', error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-2">Digital Appliqué</h3>
        <p className="text-xs text-gray-600 mb-4">
          Add digital appliqué designs that can be positioned and styled on your garment
        </p>
      </div>

      <div>
        <label className="text-xs font-medium text-gray-700 mb-1 block">Style</label>
        <select
          value={appliqueStyle}
          onChange={(e) => setAppliqueStyle(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          <option value="flat">Flat Appliqué</option>
          <option value="raised">Raised Appliqué</option>
          <option value="embroidered">Embroidered Appliqué</option>
          <option value="patch">Patch Style</option>
        </select>
      </div>

      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-purple-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors text-sm font-medium text-purple-700 disabled:opacity-50"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Uploading...
          </>
        ) : (
          <>
            <Upload className="w-4 h-4" />
            Upload Appliqué Design
          </>
        )}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        className="hidden"
      />

      {items.length > 0 && (
        <div className="space-y-3">
          <p className="text-xs font-medium text-gray-700">Added Appliqués ({items.length})</p>
          <div className="grid grid-cols-2 gap-3">
            {items.map((item, index) => (
              <div key={item.id} className="relative group border border-gray-200 rounded-lg overflow-hidden">
                <img
                  src={item.imageUrl}
                  alt={item.name}
                  className="w-full h-24 object-cover"
                />
                <button
                  onClick={() => onRemove(index)}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
                <div className="p-2 bg-white">
                  <p className="text-xs text-gray-700 truncate">{item.name}</p>
                  <p className="text-xs text-gray-500 capitalize">{item.style}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Build an SVG path string that smoothly interpolates a list of {x, y} points
// using quadratic curves through the segment midpoints. This turns the jagged
// raw freehand polyline into a smooth, natural-looking stroke.
const buildSmoothPath = (points) => {
  if (!points || points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`;
  }

  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 1; i < points.length - 1; i += 1) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    d += ` Q ${points[i].x} ${points[i].y} ${midX} ${midY}`;
  }
  const last = points[points.length - 1];
  d += ` L ${last.x} ${last.y}`;
  return d;
};

// Convert an (x, y) position expressed as a percentage of the image (0–100,
// measured from the top-left, viewer's perspective) into a human-readable region
// description. Used to give the AI a precise spatial anchor for localized edits.
const describeLocation = (xPct, yPct) => {
  const x = Math.max(0, Math.min(100, xPct));
  const y = Math.max(0, Math.min(100, yPct));
  const horizontal = x < 38 ? 'left side' : x > 62 ? 'right side' : 'horizontal center';
  const vertical = y < 38 ? 'upper' : y > 62 ? 'lower' : 'middle';
  return `${vertical} ${horizontal}`;
};

// Draw a smoothed freehand stroke onto a 2D canvas context given pre-mapped
// canvas-space points, using quadratic curves through the segment midpoints.
const strokeSmoothOnCanvas = (ctx, pts) => {
  if (!pts || pts.length < 2) return;

  ctx.beginPath();
  ctx.moveTo(pts[0].x, pts[0].y);
  if (pts.length === 2) {
    ctx.lineTo(pts[1].x, pts[1].y);
  } else {
    for (let i = 1; i < pts.length - 1; i += 1) {
      const midX = (pts[i].x + pts[i + 1].x) / 2;
      const midY = (pts[i].y + pts[i + 1].y) / 2;
      ctx.quadraticCurveTo(pts[i].x, pts[i].y, midX, midY);
    }
    const last = pts[pts.length - 1];
    ctx.lineTo(last.x, last.y);
  }
  ctx.stroke();
};

const AiStudioView = ({ kit, savedKits, onImportKit, onBack, showNotification, setActiveView, refreshKits, showFashdeckWatermark = false, runningPlan = 'free', planExpiresAt = null }) => {
  const studioEditorEnabled = isPaidPlanActive(runningPlan, planExpiresAt);
  const [showImportModal, setShowImportModal] = useState(false);
  const [availableKits, setAvailableKits] = useState([]);
  const [isLoadingKits, setIsLoadingKits] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showTour, setShowTour] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [pendingAnnotation, setPendingAnnotation] = useState(null);
  const [annotationDraft, setAnnotationDraft] = useState('');
  const [annotationFabricImage, setAnnotationFabricImage] = useState(null);
  const [annotationFabricImageUrl, setAnnotationFabricImageUrl] = useState(null);
  const [isUploadingFabricImage, setIsUploadingFabricImage] = useState(false);
  const annotationFabricInputRef = useRef(null);
  const annotationTextareaRef = useRef(null);
  // Annotation fabric library state
  const [showAnnotationFabricLibrary, setShowAnnotationFabricLibrary] = useState(false);
  const [annotationFabrics, setAnnotationFabrics] = useState([]);
  const [isLoadingAnnotationFabrics, setIsLoadingAnnotationFabrics] = useState(false);
  const [isLoadingMoreAnnotationFabrics, setIsLoadingMoreAnnotationFabrics] = useState(false);
  const [annotationFabricSearchTerm, setAnnotationFabricSearchTerm] = useState('');
  const [annotationFabricPage, setAnnotationFabricPage] = useState(1);
  const [hasMoreAnnotationFabrics, setHasMoreAnnotationFabrics] = useState(false);
  const annotationLoadMoreRef = useRef(null);
  const annotationScrollContainerRef = useRef(null);
  const [activeAnnotationId, setActiveAnnotationId] = useState(null);
  const [strokes, setStrokes] = useState([]);
  const [currentStroke, setCurrentStroke] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isProcessingAnnotatedImage, setIsProcessingAnnotatedImage] = useState(false);
  const [isAnnotationModeEnabled, setIsAnnotationModeEnabled] = useState(false);
  const previewContainerRef = useRef(null);
  const fullscreenPreviewRef = useRef(null);
  const [isEditorPanelCollapsed, setIsEditorPanelCollapsed] = useState(false);
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [isPreviewFullscreenOpen, setIsPreviewFullscreenOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [previewPan, setPreviewPan] = useState({ x: 0, y: 0 });
  const [previewIsPanning, setPreviewIsPanning] = useState(false);
  const [previewIsPinching, setPreviewIsPinching] = useState(false);
  const previewPanStartRef = useRef({ x: 0, y: 0 });
  const previewDidPanRef = useRef(false);
  const previewPinchRef = useRef({
    active: false,
    startDistance: 1,
    startZoom: 1,
    startPan: { x: 0, y: 0 },
    startMid: { x: 0, y: 0 },
  });
  const previewZoomRef = useRef(1);
  const previewPanRef = useRef({ x: 0, y: 0 });
  const skipClickForDrawRef = useRef(false);
  const [showPatternResizer, setShowPatternResizer] = useState(false);
  const [isResizingPattern, setIsResizingPattern] = useState(false);
  const [isReapplyingFabric, setIsReapplyingFabric] = useState(false);
  const [reapplyFabricReview, setReapplyFabricReview] = useState(null); // { beforeUrl, afterUrl } | null
  const [isEnhancingTexture, setIsEnhancingTexture] = useState(false);
  const [textureOverrideFabricUrl, setTextureOverrideFabricUrl] = useState('');
  const [isUploadingTextureFabric, setIsUploadingTextureFabric] = useState(false);
  const textureFabricInputRef = useRef(null);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('role')?.toLowerCase() === 'admin'
  );

  useEffect(() => {
    setIsAdmin(localStorage.getItem('role')?.toLowerCase() === 'admin');
  }, []);

  useEffect(() => {
    setMobileEditorOpen(false);
  }, [kit?._id]);

  useEffect(() => {
    setTextureOverrideFabricUrl('');
  }, [kit?._id]);

  useEffect(() => {
    if (!mobileEditorOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [mobileEditorOpen]);

  // Annotate & Edit is a desktop-only feature. If the viewport drops below the
  // desktop (lg) breakpoint, turn annotation mode off and clear any in-progress
  // annotation/drawing state so touch interactions on mobile behave normally.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(max-width: 1023px)');
    const handle = (e) => {
      if (e.matches) {
        setIsAnnotationModeEnabled(false);
        setPendingAnnotation(null);
        setCurrentStroke(null);
        setIsDrawing(false);
      }
    };
    handle(mq);
    mq.addEventListener('change', handle);
    return () => mq.removeEventListener('change', handle);
  }, []);

  const resetPreviewView = useCallback(() => {
    setPreviewZoom(1);
    setPreviewPan({ x: 0, y: 0 });
    setPreviewIsPanning(false);
    setPreviewIsPinching(false);
    previewDidPanRef.current = false;
    previewPinchRef.current.active = false;
    previewZoomRef.current = 1;
    previewPanRef.current = { x: 0, y: 0 };
  }, []);

  useEffect(() => {
    previewZoomRef.current = previewZoom;
  }, [previewZoom]);

  useEffect(() => {
    previewPanRef.current = previewPan;
  }, [previewPan]);

  const openPreviewFullscreen = useCallback(() => {
    resetPreviewView();
    setIsPreviewFullscreenOpen(true);
  }, [resetPreviewView]);

  const closePreviewFullscreen = useCallback(() => {
    setIsPreviewFullscreenOpen(false);
    resetPreviewView();
    // Closing the fullscreen annotation surface also exits annotation mode and
    // clears any in-progress drawing, so the inline preview no longer shows
    // "Annotating" (e.g. when the user presses Escape or taps the close button).
    setIsAnnotationModeEnabled(false);
    setPendingAnnotation(null);
    setCurrentStroke(null);
    setIsDrawing(false);
  }, [resetPreviewView]);

  const handlePreviewZoomIn = useCallback(() => {
    setPreviewZoom((prev) => {
      const next = Math.min(prev * 1.25, 5);
      previewZoomRef.current = next;
      return next;
    });
  }, []);

  const handlePreviewZoomOut = useCallback(() => {
    setPreviewZoom((prev) => {
      const next = Math.max(prev / 1.25, 1);
      previewZoomRef.current = next;
      if (next <= 1) {
        setPreviewPan({ x: 0, y: 0 });
        previewPanRef.current = { x: 0, y: 0 };
      }
      return next;
    });
  }, []);

  // Embellishment state
  const [activeEmbellishmentTab, setActiveEmbellishmentTab] = useState('embroidery');
  const [embellishments, setEmbellishments] = useState({
    embroidery: [],
    sequins: [],
    lace: [],
    applique: []
  });
  const [embellishmentPrompt, setEmbellishmentPrompt] = useState('');
  const [isApplyingEmbellishment, setIsApplyingEmbellishment] = useState(false);

  /** Free-form magic edit (any instruction) — uses same pipeline as other studio edits */
  const [genericAiEditPrompt, setGenericAiEditPrompt] = useState('');
  const [isApplyingGenericAiEdit, setIsApplyingGenericAiEdit] = useState(false);
  const [isGeneratingBackView, setIsGeneratingBackView] = useState(false);

  /** Fashion video (Google Veo) of the generated concept */
  const [fashionVideoPrompt, setFashionVideoPrompt] = useState('');
  const [isGeneratingFashionVideo, setIsGeneratingFashionVideo] = useState(false);
  const [fashionVideoUrl, setFashionVideoUrl] = useState(null);
  const [showFashionVideoModal, setShowFashionVideoModal] = useState(false);
  const [fashionVideoPhase, setFashionVideoPhase] = useState(0);

  // Neckline/Sleeve/Silhouette state
  const [activeEditorTab, setActiveEditorTab] = useState('design');
  // Change fabric modal state (choose from "My Fabrics")
  const [showChangeFabricModal, setShowChangeFabricModal] = useState(false);

  // Native back button: close modals / editor before leaving the Studio.
  useBackButton(true, () => {
    if (showTour) { setShowTour(false); return; }
    if (showPatternResizer) { setShowPatternResizer(false); return; }
    if (reapplyFabricReview && !isReapplyingFabric) {
      setReapplyFabricReview(null);
      return;
    }
    if (showChangeFabricModal) { setShowChangeFabricModal(false); return; }
    if (showImportModal) { setShowImportModal(false); return; }
    if (mobileEditorOpen) { setMobileEditorOpen(false); return; }
    if (onBack) onBack();
  });

  const [changeFabricCandidates, setChangeFabricCandidates] = useState([]);
  const [isLoadingChangeFabricCandidates, setIsLoadingChangeFabricCandidates] = useState(false);
  const [isLoadingMoreChangeFabricCandidates, setIsLoadingMoreChangeFabricCandidates] = useState(false);
  const [changeFabricSearchTerm, setChangeFabricSearchTerm] = useState('');
  const [changeFabricPage, setChangeFabricPage] = useState(1);
  const [hasMoreChangeFabricCandidates, setHasMoreChangeFabricCandidates] = useState(false);
  const changeFabricLoadMoreRef = useRef(null);
  const changeFabricScrollContainerRef = useRef(null);
  const [isChangingFabric, setIsChangingFabric] = useState(false);

  const [designSettings, setDesignSettings] = useState({
    neckline: 'v-neck',
    sleeve: 'full',
    silhouette: 'a-line',
    garmentLength: 'slightly-longer',
    necklinePrompt: '',
    necklineReferenceImageUrl: '',
    necklineReferenceImageName: '',
    sleevePrompt: '',
    sleeveReferenceImageUrl: '',
    sleeveReferenceImageName: '',
    silhouettePrompt: '',
    silhouetteReferenceImageUrl: '',
    silhouetteReferenceImageName: '',
    garmentLengthPrompt: ''
  });

  // Multi-Fabric state
  const [multiFabricSettings, setMultiFabricSettings] = useState({
    editPrompt: '',
    enabled: false,
    secondFabric: null,
    appliedTo: {
      yoke: false,
      sleeves: false,
      borders: false,
      contrastPanels: false
    },
    contrastPanelPosition: 'front',
    borderPosition: 'bottom'
  });

  // Magic edit state
  const [isMagicEditing, setIsMagicEditing] = useState(false);
  const [isMagicEditPreviewLoading, setIsMagicEditPreviewLoading] = useState(false);
  const [magicEditImages, setMagicEditImages] = useState([]); // Array of all edited images
  const [selectedImageIndex, setSelectedImageIndex] = useState(-1); // -1 = original, 0+ = edited index
  const magicEditTimeoutRef = useRef(null);
  const [lastClickedStyle, setLastClickedStyle] = useState(null);

  /** Full fabric document for shade-card colorVariants (same source as fabric detail page). */
  const [studioFabricProduct, setStudioFabricProduct] = useState(null);
  const [studioFabricProductLoading, setStudioFabricProductLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStudioFabricProduct(null);
      let productId = resolveStudioProductIdFromKit(kit);
      if (!productId) {
        setStudioFabricProductLoading(true);
        productId = await resolveStudioProductIdByImageMatch(kit);
      }
      if (!productId) {
        if (!cancelled) {
          setStudioFabricProduct(null);
          setStudioFabricProductLoading(false);
        }
        return;
      }
      if (!cancelled) setStudioFabricProductLoading(true);
      try {
        const p = await getProductById(productId);
        if (!cancelled) setStudioFabricProduct(p);
      } catch (err) {
        console.warn('Studio: could not load fabric product for color palette', err);
        if (!cancelled) setStudioFabricProduct(null);
      } finally {
        if (!cancelled) setStudioFabricProductLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    kit?._id,
    kit?.productId,
    kit?.productDetails?._id,
    kit?.productDetails?.image,
    kit?.sourceFabricImage,
    kit?.meta?.linkedTo,
    kit?.sourceProductId,
  ]);
  
  // Use refs to store current values without triggering re-renders
  const mainImageRef = useRef(null);
  const originalMainImageRef = useRef(null);
  const magicEditImagesRef = useRef([]);
  const selectedImageIndexRef = useRef(-1);
  const kitRef = useRef(null);
  
  const magicEditPrompt = useMemo(() => buildMagicEditPrompt({
    lastClickedStyle,
    designSettings,
    productType: kit?.productDetails?.productType || '',
    productCategory: kit?.productDetails?.productCategory || '',
  }), [lastClickedStyle, designSettings, kit?.productDetails?.productType, kit?.productDetails?.productCategory]);

  // Load kits when import modal opens
  useEffect(() => {
    if (showImportModal && availableKits.length === 0) {
      loadKits();
    }
  }, [showImportModal]);

  // Listen for fabric selection from library page
  useEffect(() => {
    const handleFabricSelected = (event) => {
      const { fabric, callbackType } = event.detail;
      
      if (callbackType === 'multi-fabric') {
        // Extract fabric image URL from various possible properties
        const fabricImageUrl = fabric.image || 
                              fabric.imageUrl || 
                              fabric.images?.original?.s3Url || 
                              fabric.images?.original?.imgbbUrl || 
                              fabric.images?.original?.url || 
                              '';
        
        // Handle multi-fabric selection
        setMultiFabricSettings(prev => ({
          ...prev,
          secondFabric: {
            id: fabric._id,
            name: fabric.name || 'Selected Fabric',
            imageUrl: fabricImageUrl,
            fabricId: fabric._id
          }
        }));
        
        // Switch to multi-fabric tab to show the selection
        setActiveEditorTab('multifabric');
        
        // Clear stored fabric
        sessionStorage.removeItem('selectedFabric');
        
        showNotification?.('Fabric selected successfully', 'success');
      } else if (callbackType === 'annotation') {
        // Extract fabric image URL from various possible properties
        const fabricImageUrl = fabric.image || 
                              fabric.imageUrl || 
                              fabric.images?.original?.s3Url || 
                              fabric.images?.original?.imgbbUrl || 
                              fabric.images?.original?.url || 
                              '';
        
        // Handle annotation fabric selection
        setAnnotationFabricImageUrl(fabricImageUrl);
        setAnnotationFabricImage(null); // Clear file if any
        
        // Clear stored fabric
        sessionStorage.removeItem('selectedFabric');
        
        showNotification?.('Fabric selected successfully', 'success');
      }
    };

    window.addEventListener('fabricSelected', handleFabricSelected);
    
    // Check for stored fabric selection (fallback if event was missed)
    const checkStoredFabric = () => {
      try {
        const storedFabric = sessionStorage.getItem('selectedFabric');
        if (storedFabric) {
          const { fabric, callbackType, timestamp } = JSON.parse(storedFabric);
          // Only process if it's recent (within last 5 seconds)
          if (Date.now() - timestamp < 5000) {
            handleFabricSelected({ detail: { fabric, callbackType } });
          } else {
            sessionStorage.removeItem('selectedFabric');
          }
        }
      } catch (error) {
        console.error('Error processing stored fabric:', error);
        sessionStorage.removeItem('selectedFabric');
      }
    };
    
    // Check on mount and after a short delay
    checkStoredFabric();
    const timeoutId = setTimeout(checkStoredFabric, 200);
    
    return () => {
      window.removeEventListener('fabricSelected', handleFabricSelected);
      clearTimeout(timeoutId);
    };
  }, [showNotification]);

  const loadKits = async () => {
    setIsLoadingKits(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification?.('Please log in to import kits', 'error');
        return;
      }
      const response = await getKits(token, 1, 50);
      const { kits } = response.data.data;
      setAvailableKits(kits || []);
    } catch (error) {
      console.error('Error loading kits:', error);
      showNotification?.('Failed to load kits', 'error');
    } finally {
      setIsLoadingKits(false);
    }
  };

  const handleImportKit = (selectedKit) => {
    onImportKit?.(selectedKit);
    setShowImportModal(false);
    showNotification?.('Kit imported to FashDeck Studio', 'success');
  };

  // Helper function to build embellishment prompt from selected items
  const buildEmbellishmentPrompt = useCallback(() => {
    const parts = [];
    
    if (embellishments.embroidery.length > 0) {
      parts.push(`Add ${embellishments.embroidery.length} embroidery motif${embellishments.embroidery.length > 1 ? 's' : ''}`);
    }
    if (embellishments.sequins.length > 0) {
      parts.push(`Add ${embellishments.sequins.length} sequin${embellishments.sequins.length > 1 ? 's' : ''} or bead embellishment${embellishments.sequins.length > 1 ? 's' : ''}`);
    }
    if (embellishments.lace.length > 0) {
      parts.push(`Add ${embellishments.lace.length} lace or border${embellishments.lace.length > 1 ? 's' : ''}`);
    }
    if (embellishments.applique.length > 0) {
      parts.push(`Add ${embellishments.applique.length} digital appliqué${embellishments.applique.length > 1 ? 's' : ''}`);
    }
    
    let basePrompt = '';
    if (parts.length > 0) {
      basePrompt = parts.join(', ') + ' to the garment. ';
    }
    
    return basePrompt;
  }, [embellishments]);

  const handleClearKit = () => {
    onImportKit?.(null);
    showNotification?.('Kit cleared from Studio', 'info');
  };

  const getKitName = useCallback((kitItem) => (
    kitItem?.name || kitItem?.productDetails?.name || 'Untitled Kit'
  ), []);

  const getKitPreviewImage = useCallback((kitItem) => (
    kitItem?.generatedImageUrl ||
    kitItem?.productDetails?.image ||
    kitItem?.generatedContent?.['Image Posts (Social Media)']?.[0]?.image ||
    ''
  ), []);

  const recentKits = useMemo(() => {
    const kits = Array.isArray(savedKits) ? [...savedKits] : [];
    return kits
      .sort((a, b) => new Date(b?.createdAt || b?.updatedAt || 0) - new Date(a?.createdAt || a?.updatedAt || 0))
      .slice(0, 6);
  }, [savedKits]);

  const filteredKits = availableKits.filter((k) => {
    const kitName = getKitName(k);
    return kitName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  useEffect(() => {
    // Reset annotations when kit changes
    setAnnotations([]);
    setPendingAnnotation(null);
    setAnnotationDraft('');
    setActiveAnnotationId(null);
    setStrokes([]);
    setCurrentStroke(null);
    setIsDrawing(false);
    setIsAnnotationModeEnabled(false); // Reset annotation mode when kit changes
    setMagicEditImages([]); // Reset edited images when kit changes
    setSelectedImageIndex(-1); // Reset to original image
    setLastClickedStyle(null); // Reset last clicked style when kit changes
    setIsMagicEditPreviewLoading(false);
  }, [kit?._id]);

  useEffect(() => {
    if (!pendingAnnotation) return;
    const timer = window.setTimeout(() => {
      annotationTextareaRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [pendingAnnotation]);

  const getRelativePoint = (event, container) => {
    const rect = container.getBoundingClientRect();
    const clientX = 'touches' in event && event.touches[0]
      ? event.touches[0].clientX
      : event.clientX;
    const clientY = 'touches' in event && event.touches[0]
      ? event.touches[0].clientY
      : event.clientY;
    const x = ((clientX - rect.left) / rect.width) * 100;
    const y = ((clientY - rect.top) / rect.height) * 100;
    return { x, y };
  };

  const getPointerClientPoint = (event) => {
    if (event.touches && event.touches[0]) {
      return { x: event.touches[0].clientX, y: event.touches[0].clientY };
    }
    if (event.changedTouches && event.changedTouches[0]) {
      return { x: event.changedTouches[0].clientX, y: event.changedTouches[0].clientY };
    }
    return { x: event.clientX, y: event.clientY };
  };

  const getTouchPairMetrics = (touches) => {
    if (!touches || touches.length < 2) return null;
    const t0 = touches[0];
    const t1 = touches[1];
    const dx = t1.clientX - t0.clientX;
    const dy = t1.clientY - t0.clientY;
    return {
      distance: Math.hypot(dx, dy) || 1,
      mid: {
        x: (t0.clientX + t1.clientX) / 2,
        y: (t0.clientY + t1.clientY) / 2,
      },
    };
  };

  const beginPreviewPinch = (touches) => {
    const metrics = getTouchPairMetrics(touches);
    if (!metrics) return;
    previewPinchRef.current = {
      active: true,
      startDistance: metrics.distance,
      startZoom: previewZoomRef.current,
      startPan: { ...previewPanRef.current },
      startMid: metrics.mid,
    };
    setPreviewIsPinching(true);
    setPreviewIsPanning(false);
    previewDidPanRef.current = true;
  };

  const updatePreviewPinch = (touches) => {
    const pinch = previewPinchRef.current;
    if (!pinch.active) return;
    const metrics = getTouchPairMetrics(touches);
    if (!metrics) return;

    const ratio = metrics.distance / pinch.startDistance;
    const nextZoom = Math.max(1, Math.min(5, pinch.startZoom * ratio));
    const midDeltaX = metrics.mid.x - pinch.startMid.x;
    const midDeltaY = metrics.mid.y - pinch.startMid.y;

    let nextPan = { x: 0, y: 0 };
    if (nextZoom > 1) {
      const zoomRatio = nextZoom / pinch.startZoom;
      nextPan = {
        x: pinch.startPan.x * zoomRatio + midDeltaX,
        y: pinch.startPan.y * zoomRatio + midDeltaY,
      };
    }

    previewZoomRef.current = nextZoom;
    previewPanRef.current = nextPan;
    setPreviewZoom(nextZoom);
    setPreviewPan(nextPan);
  };

  const endPreviewPinch = () => {
    if (!previewPinchRef.current.active) return;
    previewPinchRef.current.active = false;
    setPreviewIsPinching(false);
    if (previewZoomRef.current <= 1) {
      setPreviewPan({ x: 0, y: 0 });
      previewPanRef.current = { x: 0, y: 0 };
    }
  };

  const handlePreviewClick = (event) => {
    // When annotation mode is off, click opens fullscreen preview
    if (!isAnnotationModeEnabled) {
      openPreviewFullscreen();
      return;
    }

    // Ignore the click that follows a completed draw gesture
    if (skipClickForDrawRef.current) {
      skipClickForDrawRef.current = false;
      return;
    }

    const container = event.currentTarget;
    const { x, y } = getRelativePoint(event, container);

    setPendingAnnotation({ x, y });
    setActiveAnnotationId(null);
    setAnnotationDraft('');
  };

  const handlePreviewPointerDown = (event) => {
    // Only allow drawing if annotation mode is enabled
    if (!isAnnotationModeEnabled) return;
    
    // Left-click or touch only
    if (event.button !== undefined && event.button !== 0) return;
    const container = event.currentTarget;
    const point = getRelativePoint(event, container);
    setIsDrawing(true);
    setCurrentStroke([point]);
    setPendingAnnotation(null);
    setActiveAnnotationId(null);
  };

  const handlePreviewPointerMove = (event) => {
    if (!isDrawing || !currentStroke) return;
    const container = event.currentTarget;
    const point = getRelativePoint(event, container);
    setCurrentStroke((prev) => (prev ? [...prev, point] : [point]));
  };

  const finishStroke = () => {
    setCurrentStroke((stroke) => {
      if (stroke && stroke.length > 1) {
        setStrokes((prev) => [...prev, stroke]);
        skipClickForDrawRef.current = true;
        const endPoint = stroke[stroke.length - 1];
        setPendingAnnotation({ x: endPoint.x, y: endPoint.y });
        setActiveAnnotationId(null);
        setAnnotationDraft('');
      }
      return null;
    });
    setIsDrawing(false);
  };

  const handlePreviewPointerUp = (event) => {
    if (!isDrawing) return;
    finishStroke();
  };

  const handlePreviewPointerLeave = () => {
    if (!isDrawing) return;
    finishStroke();
  };

  const handleFullscreenWheel = (event) => {
    if (isAnnotationModeEnabled) return;
    event.preventDefault();
    const delta = event.deltaY > 0 ? 0.9 : 1.1;
    setPreviewZoom((prev) => {
      const next = Math.max(1, Math.min(5, prev * delta));
      previewZoomRef.current = next;
      if (next <= 1) {
        setPreviewPan({ x: 0, y: 0 });
        previewPanRef.current = { x: 0, y: 0 };
      }
      return next;
    });
  };

  const handleFullscreenPointerDown = (event) => {
    if (isAnnotationModeEnabled) {
      handlePreviewPointerDown(event);
      return;
    }
    if (event.touches && event.touches.length >= 2) {
      event.preventDefault();
      beginPreviewPinch(event.touches);
      return;
    }
    if (previewZoomRef.current <= 1) return;
    if (event.button !== undefined && event.button !== 0) return;
    event.preventDefault();
    const { x, y } = getPointerClientPoint(event);
    previewDidPanRef.current = false;
    setPreviewIsPanning(true);
    previewPanStartRef.current = {
      x: x - previewPanRef.current.x,
      y: y - previewPanRef.current.y,
    };
  };

  const handleFullscreenPointerMove = (event) => {
    if (isAnnotationModeEnabled) {
      handlePreviewPointerMove(event);
      return;
    }
    if (event.touches && event.touches.length >= 2) {
      event.preventDefault();
      if (!previewPinchRef.current.active) {
        beginPreviewPinch(event.touches);
      } else {
        updatePreviewPinch(event.touches);
      }
      return;
    }
    if (previewPinchRef.current.active) {
      endPreviewPinch();
      if (event.touches && event.touches.length === 1 && previewZoomRef.current > 1) {
        const { x, y } = getPointerClientPoint(event);
        setPreviewIsPanning(true);
        previewPanStartRef.current = {
          x: x - previewPanRef.current.x,
          y: y - previewPanRef.current.y,
        };
      }
      return;
    }
    if (!previewIsPanning || previewZoomRef.current <= 1) return;
    event.preventDefault();
    const { x, y } = getPointerClientPoint(event);
    const nextPan = {
      x: x - previewPanStartRef.current.x,
      y: y - previewPanStartRef.current.y,
    };
    if (
      Math.abs(nextPan.x - previewPanRef.current.x) > 2 ||
      Math.abs(nextPan.y - previewPanRef.current.y) > 2
    ) {
      previewDidPanRef.current = true;
    }
    previewPanRef.current = nextPan;
    setPreviewPan(nextPan);
  };

  const handleFullscreenPointerUp = (event) => {
    if (isAnnotationModeEnabled) {
      handlePreviewPointerUp(event);
      return;
    }
    if (event.touches && event.touches.length >= 2) {
      return;
    }
    if (previewPinchRef.current.active && (!event.touches || event.touches.length < 2)) {
      endPreviewPinch();
    }
    setPreviewIsPanning(false);
  };

  const handleFullscreenPointerLeave = () => {
    if (isAnnotationModeEnabled) {
      handlePreviewPointerLeave();
      return;
    }
    endPreviewPinch();
    setPreviewIsPanning(false);
  };

  const handleFullscreenClick = (event) => {
    if (previewDidPanRef.current) {
      previewDidPanRef.current = false;
      return;
    }
    handlePreviewClick(event);
  };

  useEffect(() => {
    if (!isPreviewFullscreenOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        closePreviewFullscreen();
        return;
      }
      if (isAnnotationModeEnabled) return;
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        handlePreviewZoomIn();
      } else if (e.key === '-') {
        e.preventDefault();
        handlePreviewZoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        resetPreviewView();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isPreviewFullscreenOpen,
    closePreviewFullscreen,
    isAnnotationModeEnabled,
    handlePreviewZoomIn,
    handlePreviewZoomOut,
    resetPreviewView,
  ]);

  const handleRemoveAnnotation = (id) => {
    setAnnotations(prev => prev.filter(a => a.id !== id));
    setActiveAnnotationId(prevId => (prevId === id ? null : prevId));
  };

  const handleSaveAnnotation = () => {
    const note = annotationDraft.trim();
    if (!pendingAnnotation || !note) {
      setPendingAnnotation(null);
      setAnnotationDraft('');
      setAnnotationFabricImage(null);
      setAnnotationFabricImageUrl(null);
      return;
    }

    const newId = Date.now();

    setAnnotations(prev => [
      ...prev,
      {
        id: newId,
        x: pendingAnnotation.x,
        y: pendingAnnotation.y,
        text: note,
        fabricImageUrl: annotationFabricImageUrl
      }
    ]);
    setActiveAnnotationId(newId);
    setPendingAnnotation(null);
    setAnnotationDraft('');
    setAnnotationFabricImage(null);
    setAnnotationFabricImageUrl(null);
  };

  const handleGenerateBackView = useCallback(async () => {
    const originalMainImage = kit
      ? (kit.generatedImageUrl ||
         kit.productDetails?.image ||
         kit.generatedContent?.['Image Posts (Social Media)']?.[0]?.image)
      : null;
    const mainImageLocal = selectedImageIndex === -1
      ? originalMainImage
      : (magicEditImages[selectedImageIndex] || originalMainImage);

    if (!kit?._id || !mainImageLocal) {
      showNotification?.('Import a kit with a generated image first.', 'error');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please log in to generate back view.', 'error');
      return;
    }

    setIsGeneratingBackView(true);
    try {
      const response = await generateBackSideViewApi(
        {
          kit,
          fabric: studioFabricProduct || kit?.productDetails?.selectedProduct || null,
          imageUrl: mainImageLocal
        },
        token
      );

      const url = response?.data?.data?.imageUrl;
      if (!url || typeof url !== 'string' || !url.startsWith('http')) {
        throw new Error('No imageUrl returned');
      }

      setMagicEditImages((prev) => {
        const next = [...(prev || []), url];
        setSelectedImageIndex(next.length - 1);
        return next;
      });

      // Persist as a kit variant under the same magic-edit collection.
      // If the current kit is already a magic-edit variant, keep using its originalKitId so all variants stay grouped.
      try {
        const collectionOriginalKitId = kit?.magicEditData?.originalKitId || kit?._id;
        if (collectionOriginalKitId) {
          await createKit(
            {
              name: `${kit?.name || 'Kit'} (Back view)`,
              status: 'generated',
              sourceProductId: kit?.sourceProductId || null,
              brandProfile: kit?.brandProfile || null,
              productDetails: {
                ...(kit?.productDetails || {}),
                originalImage: kit?.productDetails?.image || mainImageLocal
              },
              thumbnail: url,
              generatedImageUrl: url,
              magicEditData: {
                originalKitId: collectionOriginalKitId,
                originalImageUrl: mainImageLocal,
                editedImageUrl: url,
                prompt: 'Generate a back-side view of this outfit',
                modelUsed: 'gemini',
                editType: 'back_view'
              }
            },
            token
          );
        }
      } catch (persistErr) {
        console.error('Failed to save back view as kit variant:', persistErr);
        // Non-blocking: user can still use the generated image in-session.
      }

      showNotification?.('Back side view generated.', 'success');
    } catch (err) {
      console.error('Back view generation failed:', err);
      showNotification?.(err?.response?.data?.message || err?.message || 'Failed to generate back view.', 'error');
    } finally {
      setIsGeneratingBackView(false);
    }
  }, [kit, selectedImageIndex, magicEditImages, showNotification, studioFabricProduct]);

  const handleFabricImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showNotification?.('Please select an image file', 'error');
      return;
    }

    setIsUploadingFabricImage(true);
    setAnnotationFabricImage(file);

    try {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setAnnotationFabricImageUrl(previewUrl);

      // Upload to S3
      const token = localStorage.getItem('token');
      if (token) {
        const uploadedUrl = await uploadImageWithFallback(file, token);
        if (uploadedUrl) {
          // Clean up preview URL and use uploaded URL
          URL.revokeObjectURL(previewUrl);
          setAnnotationFabricImageUrl(uploadedUrl);
        }
      }
    } catch (error) {
      console.error('Error uploading fabric image:', error);
      showNotification?.('Failed to upload fabric image', 'error');
    } finally {
      setIsUploadingFabricImage(false);
    }
  };

  const handleRemoveFabricImage = () => {
    if (annotationFabricImageUrl) {
      // Revoke object URL if it's a preview
      if (annotationFabricImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(annotationFabricImageUrl);
      }
    }
    setAnnotationFabricImage(null);
    setAnnotationFabricImageUrl(null);
    if (annotationFabricInputRef.current) {
      annotationFabricInputRef.current.value = '';
    }
  };

  // Fetch annotation fabrics from library
  const fetchAnnotationFabrics = useCallback(async (page = 1, search = '', append = false) => {
    if (append) {
      setIsLoadingMoreAnnotationFabrics(true);
    } else {
      setIsLoadingAnnotationFabrics(true);
    }
    
    try {
      const trimmedSearch = (search || '').trim();
      const response = trimmedSearch
        ? await semanticSearchProducts({
            queryText: trimmedSearch,
            page,
            limit: 20,
            filters: { hasCollection: 'false' },
            candidateLimit: 250,
            maxNewEmbeddings: 10
          })
        : await getProducts(page, 20, '', { hasCollection: 'false' });
      const fetchedFabrics = response?.products || [];
      
      if (append) {
        setAnnotationFabrics(prev => {
          // Prevent duplicates
          const existingIds = new Set(prev.map(f => f._id));
          const newFabrics = fetchedFabrics.filter(f => !existingIds.has(f._id));
          return [...prev, ...newFabrics];
        });
      } else {
        setAnnotationFabrics(fetchedFabrics);
      }
      
      setHasMoreAnnotationFabrics(response?.pagination?.hasNextPage || false);
      setAnnotationFabricPage(page);
    } catch (error) {
      console.error('Error fetching annotation fabrics:', error);
      showNotification?.('Failed to load fabrics', 'error');
    } finally {
      setIsLoadingAnnotationFabrics(false);
      setIsLoadingMoreAnnotationFabrics(false);
    }
  }, [showNotification]);

  // Load annotation fabrics when library modal opens
  useEffect(() => {
    if (showAnnotationFabricLibrary) {
      setAnnotationFabrics([]);
      setAnnotationFabricPage(1);
      fetchAnnotationFabrics(1, annotationFabricSearchTerm, false);
    }
  }, [showAnnotationFabricLibrary, fetchAnnotationFabrics]);

  // Debounce annotation fabric search
  useEffect(() => {
    if (!showAnnotationFabricLibrary) return;
    
    const timer = setTimeout(() => {
      setAnnotationFabrics([]);
      setAnnotationFabricPage(1);
      fetchAnnotationFabrics(1, annotationFabricSearchTerm, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [annotationFabricSearchTerm, showAnnotationFabricLibrary, fetchAnnotationFabrics]);

  // Infinite scroll for annotation fabrics
  useEffect(() => {
    if (!showAnnotationFabricLibrary || !hasMoreAnnotationFabrics || isLoadingMoreAnnotationFabrics || isLoadingAnnotationFabrics || !annotationLoadMoreRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && hasMoreAnnotationFabrics && !isLoadingMoreAnnotationFabrics && !isLoadingAnnotationFabrics) {
          const nextPage = annotationFabricPage + 1;
          fetchAnnotationFabrics(nextPage, annotationFabricSearchTerm, true);
        }
      },
      {
        root: annotationScrollContainerRef.current,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    const currentLoadMoreRef = annotationLoadMoreRef.current;
    observer.observe(currentLoadMoreRef);

    return () => {
      if (currentLoadMoreRef) {
        observer.unobserve(currentLoadMoreRef);
      }
    };
  }, [showAnnotationFabricLibrary, hasMoreAnnotationFabrics, isLoadingMoreAnnotationFabrics, isLoadingAnnotationFabrics, annotationFabricPage, annotationFabricSearchTerm, fetchAnnotationFabrics]);

  const fetchChangeFabricCandidates = useCallback(async (page = 1, search = '', append = false) => {
    if (append) {
      setIsLoadingMoreChangeFabricCandidates(true);
    } else {
      setIsLoadingChangeFabricCandidates(true);
    }

    try {
      const trimmedSearch = (search || '').trim();
      const response = trimmedSearch
        ? await semanticSearchProducts({
            queryText: trimmedSearch,
            page,
            limit: 20,
            filters: {},
            candidateLimit: 250,
            maxNewEmbeddings: 10
          })
        : await getProducts(page, 20, '', {});
      const fetchedFabrics = response?.products || [];

      if (append) {
        setChangeFabricCandidates((prev) => {
          const existingIds = new Set(prev.map((f) => f._id));
          const newFabrics = fetchedFabrics.filter((f) => !existingIds.has(f._id));
          return [...prev, ...newFabrics];
        });
      } else {
        setChangeFabricCandidates(fetchedFabrics);
      }

      setHasMoreChangeFabricCandidates(response?.pagination?.hasNextPage || false);
      setChangeFabricPage(page);
    } catch (error) {
      console.error('Error fetching change-fabric candidates:', error);
      showNotification?.('Failed to load fabrics', 'error');
    } finally {
      setIsLoadingChangeFabricCandidates(false);
      setIsLoadingMoreChangeFabricCandidates(false);
    }
  }, [showNotification]);

  useEffect(() => {
    if (!showChangeFabricModal) return;
    setChangeFabricCandidates([]);
    setChangeFabricPage(1);
    fetchChangeFabricCandidates(1, changeFabricSearchTerm, false);
  }, [showChangeFabricModal, fetchChangeFabricCandidates]);

  useEffect(() => {
    if (!showChangeFabricModal) return;

    const timer = setTimeout(() => {
      setChangeFabricCandidates([]);
      setChangeFabricPage(1);
      fetchChangeFabricCandidates(1, changeFabricSearchTerm, false);
    }, 500);

    return () => clearTimeout(timer);
  }, [changeFabricSearchTerm, showChangeFabricModal, fetchChangeFabricCandidates]);

  useEffect(() => {
    if (
      !showChangeFabricModal ||
      !hasMoreChangeFabricCandidates ||
      isLoadingMoreChangeFabricCandidates ||
      isLoadingChangeFabricCandidates ||
      !changeFabricLoadMoreRef.current
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const firstEntry = entries[0];
        if (firstEntry.isIntersecting && hasMoreChangeFabricCandidates && !isLoadingMoreChangeFabricCandidates && !isLoadingChangeFabricCandidates) {
          const nextPage = changeFabricPage + 1;
          fetchChangeFabricCandidates(nextPage, changeFabricSearchTerm, true);
        }
      },
      {
        root: changeFabricScrollContainerRef.current,
        rootMargin: '200px',
        threshold: 0.1
      }
    );

    const currentLoadMoreRef = changeFabricLoadMoreRef.current;
    observer.observe(currentLoadMoreRef);

    return () => {
      if (currentLoadMoreRef) observer.unobserve(currentLoadMoreRef);
    };
  }, [
    showChangeFabricModal,
    hasMoreChangeFabricCandidates,
    isLoadingMoreChangeFabricCandidates,
    isLoadingChangeFabricCandidates,
    changeFabricPage,
    changeFabricSearchTerm,
    fetchChangeFabricCandidates
  ]);

  const handleSelectNewStudioFabric = useCallback(async (fabric) => {
    const fabricId = fabric?._id;
    if (!fabricId || isChangingFabric) return;

    const fabricUrl = resolveFabricImageUrlForApply(fabric);
    if (!fabricUrl) {
      showNotification?.('Selected fabric has no image.', 'error');
      return;
    }

    setIsChangingFabric(true);
    // Close immediately for snappy UX; return to preview while applying.
    setShowChangeFabricModal(false);
    setChangeFabricSearchTerm('');
    setMobileEditorOpen(false);
    try {
      const garmentUrl = await resolveStudioGarmentUrlForApplyFabric(kit);
      if (!garmentUrl) {
        showNotification?.('Could not find a garment reference for this design.', 'error');
        return;
      }

      const fabricName = fabric.name || 'Fabric';
      const garmentName =
        kit?.productDetails?.name || kit?.name || 'Garment';

      const result = await applyFabricToGarment({
        garmentUrl,
        fabricUrl,
        productCategory: kit?.productDetails?.productCategory || 'women-clothing',
        productType: kit?.productDetails?.productType || 'other',
        // Create a brand new kit for the changed fabric (same as inspiration flow).
        saveToKit: true,
        sourceProductId: fabricId,
        sourceInspirationalDesignId: kit?.sourceInspirationalDesignId || undefined,
        fabricName,
        productName: fabricName,
        kitName: `${fabricName} · ${garmentName}`,
        advancedSettings: kit?.advancedSettings || null,
      });

      if (!result?.success || !result?.imageUrl) {
        throw new Error(result?.message || 'Failed to apply fabric');
      }

      setMagicEditImages([]);
      setSelectedImageIndex(-1);
      // Auto-open the newly created kit in Studio (do not overwrite existing kit).
      if (result?.kit) {
        onImportKit?.(result.kit);
        showNotification?.('New kit generated with selected fabric.', 'success');
      } else {
        // Fallback: still show success even if kit wasn't returned for any reason.
        showNotification?.('Fabric applied successfully.', 'success');
      }
    } catch (err) {
      console.error('Failed to apply fabric in studio:', err);
      showNotification?.(
        err?.response?.data?.message || err?.message || 'Failed to apply fabric.',
        'error'
      );
    } finally {
      setIsChangingFabric(false);
    }
  }, [kit, onImportKit, showNotification, isChangingFabric, setMobileEditorOpen]);

  // Handle annotation fabric selection from library
  const handleSelectAnnotationFabric = useCallback((fabric) => {
    const fabricImageUrl = fabric.image || fabric.images?.original?.s3Url || fabric.images?.original?.imgbbUrl || fabric.images?.original?.url || '';
    setAnnotationFabricImageUrl(fabricImageUrl);
    setAnnotationFabricImage(null); // Clear file if any
    setShowAnnotationFabricLibrary(false);
    setAnnotationFabricSearchTerm('');
    showNotification?.('Fabric selected successfully', 'success');
  }, [showNotification]);

  // Calculate image URLs (before other functions that use them)
  const originalMainImage = kit
    ? (kit.generatedImageUrl ||
       kit.productDetails?.image ||
       kit.generatedContent?.['Image Posts (Social Media)']?.[0]?.image)
    : null;

  // Determine which image to show based on selected index
  const mainImage = selectedImageIndex === -1 
    ? originalMainImage 
    : (magicEditImages[selectedImageIndex] || originalMainImage);

  const studioFabricDisplay = useMemo(
    () =>
      resolveStudioFabricDisplay(kit, studioFabricProduct, {
        annotationFabricImageUrl,
        multiFabricImageUrl: multiFabricSettings?.secondFabric?.imageUrl,
      }),
    [
      kit,
      studioFabricProduct,
      annotationFabricImageUrl,
      multiFabricSettings?.secondFabric?.imageUrl,
    ]
  );

  // Combine all annotation texts into a prompt, with explicit spatial guard rails
  // so the AI applies each change ONLY where it was marked (and never on the
  // opposite/mirrored side).
  const buildAnnotationPrompt = useCallback(() => {
    const parts = [];

    if (annotations.length > 0) {
      const annotationTexts = annotations.map((annotation, index) => {
        const loc = describeLocation(annotation.x, annotation.y);
        return `Annotation ${index + 1} — located at the ${loc} of the image (about ${Math.round(annotation.x)}% from the left edge and ${Math.round(annotation.y)}% from the top edge): ${annotation.text}`;
      });
      parts.push(`Apply the following changes exactly at the marked locations: ${annotationTexts.join('. ')}.`);
    }

    const allStrokes = [...strokes, currentStroke].filter(Boolean);
    if (allStrokes.length > 0) {
      const pts = allStrokes.flat().filter((p) => p && typeof p.x === 'number');
      if (pts.length > 0) {
        const xs = pts.map((p) => p.x);
        const ys = pts.map((p) => p.y);
        const minX = Math.round(Math.min(...xs));
        const maxX = Math.round(Math.max(...xs));
        const minY = Math.round(Math.min(...ys));
        const maxY = Math.round(Math.max(...ys));
        const loc = describeLocation((minX + maxX) / 2, (minY + maxY) / 2);
        parts.push(`The user hand-drew an outline marking the exact target region to edit. This marked region is on the ${loc} of the image, spanning roughly ${minX}%–${maxX}% horizontally and ${minY}%–${maxY}% vertically (measured from the top-left corner). Apply the change inside this drawn outline only.`);
      } else {
        parts.push('Apply the changes only where indicated by the drawn marking on the image.');
      }
    }

    if (parts.length === 0) {
      return '';
    }

    // Guard rails: lock the overall orientation (prevent the model from returning
    // a horizontally flipped / laterally inverted image) and keep the edit strictly
    // localized to the marked region.
    parts.push('ORIENTATION LOCK (very important): The output image MUST preserve the exact same left-right orientation, framing, pose, and composition as the input image. Do NOT horizontally flip, mirror, or laterally invert the image or any part of it. The person and the garment must face the same direction and remain on the same side as in the input image. If any text, prints, asymmetric details, or the pose appear on a given side in the input, they must stay on that same side in the output.');
    parts.push('CRITICAL SPATIAL RULES: Apply the requested change ONLY within the specific marked region described above, and nowhere else. Do NOT modify, restyle, recolor, or move any other part of the garment, the person, or the background — every area outside the marked region must remain pixel-identical to the input image. Interpret all positions from the viewer\'s perspective: "left" means the left side of the image as you look at it and "right" means the right side. Do NOT mirror or flip the change, and do NOT apply it to the opposite side of where it was marked.');

    // Remove annotation markers and drawings from the final image
    parts.push('Remove all annotation markers, circles, pins, and drawn lines from the final image. The output should be a clean, professional image without any annotation overlays or markings.');

    return parts.join(' ');
  }, [annotations, strokes, currentStroke]);

  // Capture annotated image with annotations and strokes drawn on it
  const captureAnnotatedImage = useCallback(async () => {
    // Use the fullscreen annotation surface when it's open, otherwise the inline
    // preview. Strokes/pins are stored relative to whichever container the user
    // drew on, so capture must use the same container to stay aligned.
    const activeContainer = (isPreviewFullscreenOpen && fullscreenPreviewRef.current)
      ? fullscreenPreviewRef.current
      : previewContainerRef.current;

    if (!mainImage || !activeContainer) {
      throw new Error('No image or container available');
    }

    return new Promise((resolve, reject) => {
      const container = activeContainer;
      if (!container) {
        reject(new Error('Container not found'));
        return;
      }

      // Find the actual img element in the container to get its rendered size
      const imgElement = container.querySelector('img');
      if (!imgElement) {
        reject(new Error('Image element not found in container'));
        return;
      }

      // Get container and image dimensions
      const containerRect = container.getBoundingClientRect();
      const imgRect = imgElement.getBoundingClientRect();
      
      // Calculate how the image is displayed in the container
      const displayedWidth = imgRect.width;
      const displayedHeight = imgRect.height;
      const displayedLeft = imgRect.left - containerRect.left;
      const displayedTop = imgRect.top - containerRect.top;

      // Try to use the displayed image element first (avoids CORS issues)
      const tryUseDisplayedImage = () => {
        try {
          // Get natural dimensions from the displayed image
          const naturalWidth = imgElement.naturalWidth || imgElement.width;
          const naturalHeight = imgElement.naturalHeight || imgElement.height;

          if (!naturalWidth || !naturalHeight) {
            throw new Error('Image dimensions not available');
          }

          // Create canvas with actual image dimensions
          const canvas = document.createElement('canvas');
          canvas.width = naturalWidth;
          canvas.height = naturalHeight;
          const ctx = canvas.getContext('2d');

          // Try to draw the displayed image element directly (avoids CORS)
          try {
            ctx.drawImage(imgElement, 0, 0, naturalWidth, naturalHeight);
          } catch (drawError) {
            // If drawing the displayed element fails, fall back to loading the image
            throw new Error('Cannot draw displayed image, will try loading directly');
          }

          // Calculate scale factors from displayed size to actual image size
          const scaleX = naturalWidth / displayedWidth;
          const scaleY = naturalHeight / displayedHeight;

          // Draw strokes (smoothed) with a bold, fully-opaque line so the marked
          // region is clearly visible to the AI. Annotation marker dots are
          // intentionally NOT drawn — only the freehand sketch is sent to the AI.
          ctx.strokeStyle = 'rgba(79, 70, 229, 1)';
          ctx.lineWidth = Math.max(6, 5 * Math.min(scaleX, scaleY));
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';

          [...strokes, currentStroke].filter(Boolean).forEach((stroke) => {
            if (stroke.length < 2) return;

            // Map in-bounds points to canvas coordinates, then draw a smooth curve
            const pts = [];
            stroke.forEach((point) => {
              const displayedX = (point.x / 100) * containerRect.width;
              const displayedY = (point.y / 100) * containerRect.height;
              const relativeX = displayedX - displayedLeft;
              const relativeY = displayedY - displayedTop;
              if (relativeX >= 0 && relativeX <= displayedWidth &&
                  relativeY >= 0 && relativeY <= displayedHeight) {
                pts.push({ x: relativeX * scaleX, y: relativeY * scaleY });
              }
            });
            strokeSmoothOnCanvas(ctx, pts);
          });

          // Convert canvas to blob
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `annotated_image_${Date.now()}.jpg`, {
                type: 'image/jpeg',
                lastModified: Date.now()
              });
              resolve(file);
            } else {
              reject(new Error('Failed to create image blob'));
            }
          }, 'image/jpeg', 0.9);
        } catch (error) {
          // If using displayed image fails, try loading the image URL directly
          console.warn('Failed to use displayed image, trying direct load:', error);
          loadImageDirectly();
        }
      };

      // Fallback: Load image via backend proxy (avoids CORS / canvas-taint),
      // falling back to a direct cross-origin load if the proxy is unavailable.
      const loadImageDirectly = async () => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        let objectUrl = null;

        img.onload = () => {
          try {
            const containerRect = container.getBoundingClientRect();
            const imgRect = imgElement.getBoundingClientRect();
            
            const displayedWidth = imgRect.width;
            const displayedHeight = imgRect.height;
            const displayedLeft = imgRect.left - containerRect.left;
            const displayedTop = imgRect.top - containerRect.top;

            // Create canvas with actual image dimensions
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');

            // Draw the original image
            ctx.drawImage(img, 0, 0, img.width, img.height);
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
              objectUrl = null;
            }

            // Calculate scale factors from displayed size to actual image size
            const scaleX = img.width / displayedWidth;
            const scaleY = img.height / displayedHeight;

            // Draw strokes (smoothed) with a bold, fully-opaque line so the marked
            // region is clearly visible to the AI. Annotation marker dots are
            // intentionally NOT drawn — only the freehand sketch is sent to the AI.
            ctx.strokeStyle = 'rgba(79, 70, 229, 1)';
            ctx.lineWidth = Math.max(6, 5 * Math.min(scaleX, scaleY));
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';

            [...strokes, currentStroke].filter(Boolean).forEach((stroke) => {
              if (stroke.length < 2) return;

              // Map in-bounds points to canvas coordinates, then draw a smooth curve
              const pts = [];
              stroke.forEach((point) => {
                const displayedX = (point.x / 100) * containerRect.width;
                const displayedY = (point.y / 100) * containerRect.height;
                const relativeX = displayedX - displayedLeft;
                const relativeY = displayedY - displayedTop;
                if (relativeX >= 0 && relativeX <= displayedWidth &&
                    relativeY >= 0 && relativeY <= displayedHeight) {
                  pts.push({ x: relativeX * scaleX, y: relativeY * scaleY });
                }
              });
              strokeSmoothOnCanvas(ctx, pts);
            });

            // Convert canvas to blob
            canvas.toBlob((blob) => {
              if (blob) {
                const file = new File([blob], `annotated_image_${Date.now()}.jpg`, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(file);
              } else {
                reject(new Error('Failed to create image blob'));
              }
            }, 'image/jpeg', 0.9);
          } catch (error) {
            reject(new Error(`Failed to process image: ${error.message}`));
          }
        };

        img.onerror = () => {
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          reject(new Error(`Failed to load image from URL. The image may have CORS restrictions. URL: ${mainImage}`));
        };

        // Prefer fetching through the backend image proxy so the bytes are served
        // same-origin and the canvas is never tainted (works regardless of the
        // remote host's CORS configuration).
        try {
          const token = localStorage.getItem('token');
          const proxyUrl = `${API_BASE_URL}/s3/proxy-image?url=${encodeURIComponent(mainImage)}`;
          const proxyResponse = await fetch(proxyUrl, {
            headers: token ? { Authorization: `Bearer ${token}` } : {}
          });
          if (proxyResponse.ok) {
            const blob = await proxyResponse.blob();
            objectUrl = URL.createObjectURL(blob);
            img.src = objectUrl;
            return;
          }
          console.warn('Proxy image fetch returned non-OK status:', proxyResponse.status);
        } catch (proxyError) {
          console.warn('Proxy image fetch failed, attempting direct load:', proxyError);
        }

        // Last resort: direct cross-origin load (may fail if the host lacks CORS).
        img.src = mainImage;
      };

      // Try using displayed image first (avoids CORS issues)
      tryUseDisplayedImage();
    });
  }, [mainImage, strokes, currentStroke, isPreviewFullscreenOpen]);

  // Handle delete design version
  const handleDeleteDesignVersion = useCallback((indexToDelete) => {
    setMagicEditImages(prev => {
      if (indexToDelete < 0 || indexToDelete >= prev.length) {
        return prev;
      }

      const removed = prev[indexToDelete];
      if (typeof removed === 'string' && removed.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(removed);
        } catch (_) {
          /* ignore */
        }
      }

      const newImages = prev.filter((_, index) => index !== indexToDelete);
      
      // Update selected index if needed
      setSelectedImageIndex(currentIndex => {
        if (currentIndex === indexToDelete) {
          // If we deleted the currently selected image, switch to original
          return -1;
        } else if (currentIndex > indexToDelete) {
          // If we deleted an image before the selected one, adjust the index
          return currentIndex - 1;
        }
        return currentIndex;
      });

      showNotification?.('Design version deleted', 'success');
      return newImages;
    });
  }, [showNotification]);

  // Update refs with current values
  mainImageRef.current = mainImage;
  originalMainImageRef.current = originalMainImage;
  magicEditImagesRef.current = magicEditImages;
  selectedImageIndexRef.current = selectedImageIndex;
  kitRef.current = kit;

  // Handle magic edit with annotated image
  const handleMagicEditWithAnnotations = useCallback(async () => {
    if (!kit?._id || !mainImage) {
      showNotification?.('No kit or image available for editing', 'error');
      return;
    }

    if (annotations.length === 0 && strokes.length === 0 && !currentStroke) {
      showNotification?.('Please add at least one annotation or drawing before applying magic edit', 'error');
      return;
    }

    setIsProcessingAnnotatedImage(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification?.('Please log in to use magic edit', 'error');
        return;
      }

      // Capture annotated image
      showNotification?.('Capturing annotated image...', 'info');
      const annotatedImageFile = await captureAnnotatedImage();

      // Upload annotated image to S3
      showNotification?.('Uploading annotated image...', 'info');
      const uploadResult = await uploadImageWithFallback(annotatedImageFile, {
        folder: 'annotated-images',
        fileName: `annotated_${kit._id}_${Date.now()}.jpg`
      });

      if (!uploadResult.success) {
        throw new Error('Failed to upload annotated image');
      }

      // Build prompt from annotations
      const annotationPrompt = buildAnnotationPrompt();
      if (!annotationPrompt) {
        throw new Error('No annotation prompt available');
      }

      // Find fabric image URL from annotations (use first annotation with fabric image)
      // Also check pending annotation if it has a fabric image
      // Only use URLs that are not blob URLs (which are temporary preview URLs)
      const annotationWithFabric = annotations.find(a => a.fabricImageUrl && !a.fabricImageUrl.startsWith('blob:'));
      let fabricImageUrl = annotationWithFabric?.fabricImageUrl || null;
      if (!fabricImageUrl && annotationFabricImageUrl && !annotationFabricImageUrl.startsWith('blob:')) {
        fabricImageUrl = annotationFabricImageUrl;
      }

      // Call magic edit V2 API with annotated image (using Nano Banana)
      showNotification?.('Applying magic edit...', 'info');
      const response = await magicEditV2Api({
        imageUrl: uploadResult.url,
        originalImageUrl: kit.productDetails?.image || originalMainImage,
        prompt: annotationPrompt,
        originalKitId: kit._id,
        createNewKit: true,
        fabricImageUrl: fabricImageUrl,
        aspectRatio: "match_input_image"
      }, token);

      if (response.data?.success) {
        const editedUrl = response.data.data?.editedImageUrl;
        if (editedUrl) {
          // Clear all annotations and strokes to show clean output image, and
          // exit annotation mode so the preview no longer shows "Annotating".
          setAnnotations([]);
          setStrokes([]);
          setCurrentStroke(null);
          setPendingAnnotation(null);
          setAnnotationDraft('');
          setActiveAnnotationId(null);
          setIsAnnotationModeEnabled(false);
          setAnnotationFabricImage(null);
          setAnnotationFabricImageUrl(null);

          // Add new edited image to the array and select it
          setMagicEditImages(prev => {
            const newImages = appendCappedUrls(prev, editedUrl, 20);
            setSelectedImageIndex(newImages.length - 1);
            return newImages;
          });
          showNotification?.('Magic edit applied successfully!', 'success');
        } else {
          showNotification?.('Magic edit completed but no image was returned', 'warning');
        }
      } else {
        throw new Error(response.data?.message || 'Magic edit failed');
      }
    } catch (error) {
      console.error('Magic edit with annotations error:', error);
      showNotification?.(
        error.message || 'Failed to apply magic edit with annotations',
        'error'
      );
    } finally {
      setIsProcessingAnnotatedImage(false);
    }
  }, [
    kit?._id,
    mainImage,
    originalMainImage,
    annotations,
    strokes,
    currentStroke,
    annotationFabricImageUrl,
    captureAnnotatedImage,
    buildAnnotationPrompt,
    showNotification
  ]);

  const handleApplyGenericAiEdit = useCallback(async () => {
    const prompt = genericAiEditPrompt.trim();
    if (!prompt) {
      showNotification?.('Enter what you want to change.', 'error');
      return;
    }
    if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
      showNotification?.('Import a kit with a generated image first.', 'error');
      return;
    }
    setIsApplyingGenericAiEdit(true);
    setIsMagicEditing(true);
    setIsMagicEditPreviewLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification?.('Please log in to run AI edits.', 'error');
        return;
      }
      const currentMainImage =
        selectedImageIndex === -1
          ? kit?.generatedImageUrl || kit?.productDetails?.image
          : magicEditImages[selectedImageIndex] ||
            kit?.generatedImageUrl ||
            kit?.productDetails?.image;
      const currentOriginalImage =
        kit?.productDetails?.image || originalMainImage || kit?.generatedImageUrl;

      const response = await magicEditV2Api(
        {
          imageUrl: currentMainImage,
          originalImageUrl: currentOriginalImage,
          prompt,
          originalKitId: kit._id,
          createNewKit: true,
          aspectRatio: 'match_input_image',
        },
        token
      );

      if (response.data?.success) {
        const editedUrl = response.data.data?.editedImageUrl;
        if (editedUrl) {
          setMagicEditImages((prev) => {
            const next = appendCappedUrls(prev, editedUrl, 20);
            setSelectedImageIndex(next.length - 1);
            return next;
          });
          showNotification?.('Custom AI edit applied.', 'success');
        } else {
          showNotification?.('Edit finished but no image URL was returned.', 'warning');
        }
      } else {
        showNotification?.(
          response.data?.message || 'AI edit failed. Try a shorter or clearer instruction.',
          'error'
        );
      }
    } catch (err) {
      console.error('Generic AI studio edit:', err);
      showNotification?.(
        err.response?.data?.message || 'AI edit failed. Please try again.',
        'error'
      );
    } finally {
      setIsApplyingGenericAiEdit(false);
      setIsMagicEditing(false);
      setIsMagicEditPreviewLoading(false);
    }
  }, [
    genericAiEditPrompt,
    kit,
    selectedImageIndex,
    magicEditImages,
    originalMainImage,
    showNotification,
  ]);

  // Pattern resizer: regenerate the current garment via GPT Image 2 with the fabric's
  // repeating motif scaled up/down, keeping fabric/garment/scene consistent.
  const handleApplyPatternResize = useCallback(async (patternScale) => {
    const garmentUrl = mainImage;
    if (!kit?._id || !garmentUrl) {
      showNotification?.('Import a kit with a generated image first.', 'error');
      return false;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please log in to resize patterns.', 'error');
      return false;
    }

    const fabricSrc = studioFabricDisplay?.fabricSrc;
    const fabricImageUrl =
      fabricSrc && !String(fabricSrc).startsWith('blob:') ? fabricSrc : undefined;

    setIsResizingPattern(true);
    try {
      const response = await resizeFabricPatternApi(
        {
          imageUrl: garmentUrl,
          // Pre-edit base is the CURRENT kit garment being resized — not the
          // parent fabric/inspiration image.
          originalImageUrl: garmentUrl,
          fabricImageUrl,
          patternScale,
          originalKitId: kit._id,
          createNewKit: true,
          aspectRatio: 'match_input_image',
          advancedSettings: kit?.advancedSettings || null,
        },
        token
      );

      if (response.data?.success) {
        const editedUrl = response.data.data?.editedImageUrl;
        if (editedUrl) {
          setMagicEditImages((prev) => {
            const next = appendCappedUrls(prev, editedUrl, 20);
            setSelectedImageIndex(next.length - 1);
            return next;
          });
          showNotification?.(`Pattern rescaled to ${patternScale}%.`, 'success');
          return true;
        }
        showNotification?.('Resize finished but no image URL was returned.', 'warning');
        return false;
      }
      showNotification?.(
        response.data?.message || 'Pattern resize failed. Please try again.',
        'error'
      );
      return false;
    } catch (err) {
      console.error('Pattern resize error:', err);
      showNotification?.(
        err.response?.data?.message || 'Pattern resize failed. Please try again.',
        'error'
      );
      return false;
    } finally {
      setIsResizingPattern(false);
    }
  }, [kit, mainImage, originalMainImage, studioFabricDisplay, showNotification]);

  // Reapply the kit's linked fabric onto the current generated garment.
  // Result is shown in a review modal; only accepted images enter the preview.
  const handleReapplyFabric = useCallback(async () => {
    if (isReapplyingFabric || isChangingFabric || isResizingPattern) return;

    const garmentUrl = mainImage;
    if (!kit?._id || !garmentUrl) {
      showNotification?.('Import a kit with a generated image first.', 'error');
      return;
    }

    const fabricUrl = studioFabricDisplay?.fabricSrc;
    if (!fabricUrl) {
      showNotification?.(
        'No source fabric found for this kit. Use Change fabric to pick one.',
        'error'
      );
      return;
    }

    const sourceProductId =
      kit?.sourceProductId ||
      studioFabricProduct?._id ||
      kit?.productDetails?.selectedProduct?._id ||
      null;

    setIsReapplyingFabric(true);
    setReapplyFabricReview({ beforeUrl: garmentUrl, afterUrl: null });
    setMobileEditorOpen(false);
    try {
      const fabricName =
        studioFabricDisplay?.fabricName ||
        studioFabricProduct?.name ||
        'Fabric';

      // Pro model + reapply guardrails: higher fidelity texture/color lock for studio remaps.
      const result = await applyFabricToGarment({
        garmentUrl,
        fabricUrl,
        productCategory: kit?.productDetails?.productCategory || 'women-clothing',
        productType: kit?.productDetails?.productType || 'other',
        saveToKit: false,
        useNanoBananaPro: true,
        sourceProductId: sourceProductId || undefined,
        sourceInspirationalDesignId: kit?.sourceInspirationalDesignId || undefined,
        fabricName,
        advancedSettings: {
          ...(kit?.advancedSettings && typeof kit.advancedSettings === 'object'
            ? kit.advancedSettings
            : {}),
          reapplyFabric: true,
        },
      });

      if (!result?.success || !result?.imageUrl) {
        throw new Error(result?.message || 'Failed to reapply fabric');
      }

      setReapplyFabricReview({
        beforeUrl: garmentUrl,
        afterUrl: result.imageUrl,
      });
    } catch (err) {
      console.error('Failed to reapply fabric in studio:', err);
      setReapplyFabricReview(null);
      showNotification?.(
        err?.response?.data?.message || err?.message || 'Failed to reapply fabric.',
        'error'
      );
    } finally {
      setIsReapplyingFabric(false);
    }
  }, [
    isReapplyingFabric,
    isChangingFabric,
    isResizingPattern,
    kit,
    mainImage,
    studioFabricDisplay,
    studioFabricProduct,
    showNotification,
    setMobileEditorOpen,
  ]);

  const handleAcceptReapplyFabric = useCallback(() => {
    const afterUrl = reapplyFabricReview?.afterUrl;
    if (!afterUrl) {
      setReapplyFabricReview(null);
      return;
    }

    setMagicEditImages((prev) => {
      const next = appendCappedUrls(prev, afterUrl, 20);
      setSelectedImageIndex(next.length - 1);
      return next;
    });
    setReapplyFabricReview(null);
    showNotification?.('Fabric reapplied — showing in preview.', 'success');
  }, [reapplyFabricReview, showNotification]);

  const handleRejectReapplyFabric = useCallback(() => {
    if (isReapplyingFabric) return;
    setReapplyFabricReview(null);
    showNotification?.('Fabric reapply discarded.', 'info');
  }, [isReapplyingFabric, showNotification]);

  const textureFabricUrl = useMemo(() => {
    const override = String(textureOverrideFabricUrl || '').trim();
    if (override && !override.startsWith('blob:')) return override;
    const linked = studioFabricDisplay?.fabricSrc;
    if (linked && !String(linked).startsWith('blob:')) return linked;
    return '';
  }, [textureOverrideFabricUrl, studioFabricDisplay]);

  const handleUploadTextureFabric = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showNotification?.('Please upload a fabric image.', 'error');
      return;
    }
    setIsUploadingTextureFabric(true);
    try {
      const uploadResult = await uploadImageWithFallback(file, {
        folder: 'studio-texture-fabric',
      });
      const url = uploadResult?.url || (typeof uploadResult === 'string' ? uploadResult : null);
      if (!url) {
        throw new Error('Upload did not return an image URL');
      }
      setTextureOverrideFabricUrl(url);
      showNotification?.('Fabric image ready for texture enhance.', 'success');
    } catch (error) {
      console.error('Texture fabric upload error:', error);
      showNotification?.(error?.message || 'Failed to upload fabric image.', 'error');
    } finally {
      setIsUploadingTextureFabric(false);
      if (textureFabricInputRef.current) {
        textureFabricInputRef.current.value = '';
      }
    }
  }, [showNotification]);

  const handleEnhanceTexture = useCallback(async () => {
    const garmentUrl = mainImage;
    if (!kit?._id || !garmentUrl) {
      showNotification?.('Import a kit with a generated image first.', 'error');
      return false;
    }
    if (!textureFabricUrl) {
      showNotification?.('Add a fabric image first — upload one or use Change fabric.', 'error');
      return false;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please log in to enhance texture.', 'error');
      return false;
    }

    setMobileEditorOpen(false);
    setIsEnhancingTexture(true);
    setIsMagicEditing(true);
    setIsMagicEditPreviewLoading(true);
    try {
      const response = await enhanceFabricTextureApi(
        {
          imageUrl: garmentUrl,
          originalImageUrl: kit?.productDetails?.image || originalMainImage || garmentUrl,
          fabricImageUrl: textureFabricUrl,
          originalKitId: kit._id,
          createNewKit: true,
          aspectRatio: 'match_input_image',
        },
        token
      );

      if (response.data?.success) {
        const editedUrl = response.data.data?.editedImageUrl;
        if (editedUrl) {
          setMagicEditImages((prev) => {
            const next = appendCappedUrls(prev, editedUrl, 20);
            setSelectedImageIndex(next.length - 1);
            return next;
          });
          showNotification?.('Fabric texture enhanced to match the source fabric.', 'success');
          return true;
        }
        showNotification?.('Enhance finished but no image URL was returned.', 'warning');
        return false;
      }
      showNotification?.(
        response.data?.message || 'Texture enhance failed. Please try again.',
        'error'
      );
      return false;
    } catch (err) {
      console.error('Enhance texture error:', err);
      showNotification?.(
        err.response?.data?.message || 'Texture enhance failed. Please try again.',
        'error'
      );
      return false;
    } finally {
      setIsEnhancingTexture(false);
      setIsMagicEditing(false);
      setIsMagicEditPreviewLoading(false);
    }
  }, [kit, mainImage, originalMainImage, textureFabricUrl, showNotification]);

  /** Create a fashion video of the currently displayed concept using Google Veo. */
  const handleGenerateFashionVideo = useCallback(async () => {
    if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
      showNotification?.('Import a kit with a generated concept first.', 'error');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      showNotification?.('Please log in to create videos.', 'error');
      return;
    }

    const currentMainImage =
      selectedImageIndex === -1
        ? kit?.generatedImageUrl || kit?.productDetails?.image
        : magicEditImages[selectedImageIndex] ||
          kit?.generatedImageUrl ||
          kit?.productDetails?.image;

    const garmentLabel =
      kit?.productDetails?.productType ||
      kit?.productDetails?.type ||
      kit?.productDetails?.name ||
      'outfit';

    const defaultPrompt = `Cinematic fashion video of a model showcasing this ${garmentLabel}. Smooth camera movement from a close-up to a full-body shot, elegant slow-motion runway walk, professional studio lighting, high-fashion editorial aesthetic. Keep the garment's design, colors and details true to the reference image.`;
    const prompt = fashionVideoPrompt.trim() || defaultPrompt;

    setIsGeneratingFashionVideo(true);
    try {
      const response = await generateVeoVideo({
        prompt,
        imageUrl: currentMainImage,
        aspectRatio: '9:16',
        kitId: kit._id,
      });

      if (response?.success && response?.videoUrl) {
        setFashionVideoUrl(response.videoUrl);
        setShowFashionVideoModal(true);
        if (kit) {
          kit.videoUrl = response.videoUrl;
        }
        showNotification?.('Fashion video created successfully!', 'success');
        // Refresh the kit list so the video shows in AI Creations & kit details.
        try {
          await refreshKits?.();
        } catch (refreshErr) {
          console.log('Kit refresh after video generation failed:', refreshErr?.message);
        }
      } else {
        showNotification?.(response?.message || 'Video generation failed.', 'error');
      }
    } catch (err) {
      console.error('Fashion video (Veo) generation:', err);
      showNotification?.(
        err.response?.data?.message || 'Video generation failed. Please try again.',
        'error'
      );
    } finally {
      setIsGeneratingFashionVideo(false);
    }
  }, [kit, selectedImageIndex, magicEditImages, fashionVideoPrompt, showNotification, refreshKits]);

  /** Cycle through cinematic status messages while the fashion video renders. */
  const FASHION_VIDEO_PHASES = useMemo(
    () => [
      'Setting up the scene',
      'Directing the shoot',
      'Lighting the runway',
      'Choreographing the walk',
      'Rendering cinematic frames',
      'Adding the final polish',
    ],
    []
  );
  useEffect(() => {
    if (!isGeneratingFashionVideo) {
      setFashionVideoPhase(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setFashionVideoPhase((prev) => (prev + 1) % FASHION_VIDEO_PHASES.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isGeneratingFashionVideo, FASHION_VIDEO_PHASES.length]);

  // Trigger Magic Edit automatically when design settings change
  const handleMagicEdit = useCallback(async () => {
    if (!kit?._id || !mainImage) {
      return; // Don't trigger if no kit or image
    }

    const prompt = magicEditPrompt;
    if (!prompt) {
      return; // Don't trigger if no prompt
    }

    setIsMagicEditing(true);
    setIsMagicEditPreviewLoading(true);
    let editedImageApplied = false;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return; // Don't trigger if not authenticated
      }

      // Use the currently displayed image in preview as input
      // This allows users to apply edits on top of previous edits
      const response = await magicEditV2Api(
        buildMagicEditV2Payload({
          imageUrl: mainImage,
          originalImageUrl: kit.productDetails?.image || originalMainImage,
          prompt,
          originalKitId: kit._id,
          kit,
          lastClickedStyle,
          extra: { guidanceScale: 6 },
        }),
        token
      );

      if (response.data?.success) {
        const editedUrl = response.data.data?.editedImageUrl;
        if (editedUrl) {
          setMagicEditImageUrl(editedUrl);
          setShowOriginalImage(false); // Show edited image by default when new edit is applied
          editedImageApplied = true;
        } else {
          setIsMagicEditPreviewLoading(false);
        }
        // Don't show notification for automatic edits to avoid spam
      } else {
        console.error('Magic edit failed:', response.data?.message);
        setIsMagicEditPreviewLoading(false);
      }
    } catch (error) {
      console.error('Magic edit error:', error);
      setIsMagicEditPreviewLoading(false);
      // Don't show notification for automatic edits to avoid spam
    } finally {
      setIsMagicEditing(false);
      // If no edited image was set (error path), ensure the overlay clears
      if (!editedImageApplied) {
        setIsMagicEditPreviewLoading(false);
      }
    }
  }, [kit, mainImage, originalMainImage, magicEditPrompt, lastClickedStyle]);

  // Auto-trigger magic edit when a style is clicked (with debouncing)
  useEffect(() => {
    // Only trigger if kit is loaded, we have an image, and a style was clicked
    if (!kitRef.current?._id || !mainImageRef.current || !lastClickedStyle) {
      return;
    }

    // Clear existing timeout to prevent multiple calls
    if (magicEditTimeoutRef.current) {
      clearTimeout(magicEditTimeoutRef.current);
    }

    // Debounce the magic edit call by 800ms to avoid too many API calls
    // Use refs to get current values at execution time without triggering re-runs
    magicEditTimeoutRef.current = setTimeout(async () => {
      // Get the current values from refs at the time of execution
      const currentKit = kitRef.current;
      const currentSelectedIndex = selectedImageIndexRef.current;
      const currentMainImage = currentSelectedIndex === -1
        ? originalMainImageRef.current
        : (magicEditImagesRef.current[currentSelectedIndex] || originalMainImageRef.current);
      
      if (!currentMainImage || !currentKit?._id) {
        return;
      }

      // Rebuild prompt from lastClickedStyle and designSettings
      const prompt = buildMagicEditPrompt({
        lastClickedStyle,
        designSettings,
        productType: currentKit?.productDetails?.productType || '',
        productCategory: currentKit?.productDetails?.productCategory || '',
      });
      if (!prompt) {
        return;
      }

      setIsMagicEditing(true);
      setIsMagicEditPreviewLoading(true);
      let editedImageApplied = false;
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          showNotification?.('Please log in to run Magic Edit.', 'error');
          return;
        }

        const response = await magicEditV2Api(
          buildMagicEditV2Payload({
            imageUrl: currentMainImage,
            originalImageUrl: currentKit.productDetails?.image || originalMainImageRef.current,
            prompt,
            originalKitId: currentKit._id,
            kit: currentKit,
            lastClickedStyle,
            extra: { guidanceScale: 6 },
          }),
          token
        );

        if (response.data?.success) {
          const editedUrl = response.data.data?.editedImageUrl;
          if (editedUrl) {
            // Add new edited image to the array and select it
            setMagicEditImages(prev => {
              const newImages = appendCappedUrls(prev, editedUrl, 20);
              // Select the newly created edit (it will be at the last index)
              setSelectedImageIndex(newImages.length - 1);
              return newImages;
            });
            editedImageApplied = true;
          } else {
            showNotification?.('Magic edit completed but no image was returned', 'warning');
            setIsMagicEditPreviewLoading(false);
          }
        } else {
          console.error('Magic edit failed:', response.data?.message);
          showNotification?.(response.data?.message || 'Magic edit failed. Please try again.', 'error');
          setIsMagicEditPreviewLoading(false);
        }
      } catch (error) {
        console.error('Magic edit error:', error);
        showNotification?.(error.response?.data?.message || 'Magic edit failed. Please try again.', 'error');
        setIsMagicEditPreviewLoading(false);
      } finally {
        setIsMagicEditing(false);
        if (!editedImageApplied) {
          setIsMagicEditPreviewLoading(false);
        }
      }
    }, 800);

    // Cleanup timeout on unmount or when dependencies change
    return () => {
      if (magicEditTimeoutRef.current) {
        clearTimeout(magicEditTimeoutRef.current);
      }
    };
  }, [lastClickedStyle, showNotification]); // Only trigger when lastClickedStyle changes

  // Empty state - no kit imported
  if (!kit) {
    return (
      <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden">
        <div className="h-full w-full flex flex-col bg-white">
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-6 sm:pb-4 sm:pt-[max(1rem,env(safe-area-inset-top))] lg:pb-5 lg:pt-[max(1.25rem,env(safe-area-inset-top))] flex items-center justify-between shadow-xl border-b border-purple-500/20">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              <button
                onClick={onBack}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all hover:bg-white/20 active:scale-95"
                title="Go back"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/30 bg-white/20 shadow-lg backdrop-blur-sm sm:h-12 sm:w-12 sm:rounded-xl">
                  <Wand2 className="h-4 w-4 sm:h-6 sm:w-6" />
                </div>
                <div className="min-w-0">
                  <h1 className="truncate text-base font-bold tracking-tight sm:text-2xl md:text-3xl">FashDeck Studio</h1>
                  <p className="hidden text-xs text-purple-100/90 font-medium sm:block sm:text-sm">Create & Edit Designs</p>
                </div>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              <button
                onClick={() => setShowImportModal(true)}
                data-tour="import-button"
                className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/15 px-2 py-1.5 text-xs font-medium text-white transition-all hover:bg-white/25 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm sm:shadow-md sm:hover:shadow-lg"
              >
                <Package size={16} />
                <span className="hidden sm:inline">Import Kit</span>
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
            <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6 sm:py-8">
              {/* Empty State Card */}
              <div className="bg-white/90 rounded-2xl shadow-xl border border-purple-100 p-6 sm:p-8 md:p-12">
                <div className="text-center max-w-md mx-auto">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 sm:mb-6 rounded-2xl bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                    <Wand2 className="w-8 h-8 sm:w-10 sm:h-10 text-purple-700" />
                  </div>
                  <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-2 sm:mb-3">FashDeck Studio is Empty</h2>
                  <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8">
                    Import a design from your existing kits to start editing, or create something new.
                  </p>

                  <button
                    onClick={() => setShowImportModal(true)}
                    className="w-full sm:w-auto inline-flex items-center justify-center px-5 sm:px-6 py-2.5 sm:py-3 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold shadow-md hover:from-purple-700 hover:to-pink-700 transition-all duration-200 mb-3 sm:mb-4"
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Import from Kit
                  </button>
                  <p className="text-xs text-gray-500">
                    Or navigate to a kit and click "Edit in Studio"
                  </p>
                </div>
              </div>

              {recentKits.length > 0 && (
                <div className="mt-6 bg-white/90 rounded-2xl shadow-xl border border-purple-100 p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-gray-800">Recently Created Kits</h3>
                    <button
                      onClick={() => setShowImportModal(true)}
                      className="text-xs sm:text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      View all
                    </button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {recentKits.map((k) => {
                      const kitName = getKitName(k);
                      const kitImage = getKitPreviewImage(k);
                      const thumbnailKitImage = kitImage ? toThumbnailUrl(kitImage) : '';
                      return (
                        <button
                          key={k._id}
                          type="button"
                          onClick={() => handleImportKit(k)}
                          className="text-left bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-purple-300 hover:shadow-md transition-all"
                        >
                          <div className="aspect-[3/4] bg-gradient-to-br from-purple-50 to-indigo-50">
                            {kitImage ? (
                              <img
                                src={thumbnailKitImage}
                                alt={kitName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  if (e.currentTarget.src !== kitImage) {
                                    e.currentTarget.src = kitImage;
                                  } else {
                                    e.currentTarget.style.display = 'none';
                                  }
                                }}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="w-7 h-7 text-gray-400" />
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Import Kit Modal */}
        {showImportModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
              {/* Modal Header */}
              <div className="flex items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-200 gap-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-800">Import Kit to Studio</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mt-1">
                    Select a kit from your collection to import
                  </p>
                </div>
                <button
                  onClick={() => setShowImportModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Search Bar */}
              <div className="p-3 sm:p-4 border-b border-gray-200">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search kits..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Kits Grid */}
              <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
                {isLoadingKits ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                  </div>
                ) : filteredKits.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm sm:text-base text-gray-600">
                      {searchTerm ? 'No kits found matching your search' : 'No kits available'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    {filteredKits.map((k) => {
                      const kitName = getKitName(k);
                      const kitImage = getKitPreviewImage(k);

                      return (
                        <div
                          key={k._id}
                          onClick={() => handleImportKit(k)}
                          className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer"
                        >
                          <div className="aspect-[3/4] bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center relative overflow-hidden">
                            {kitImage ? (
                              <img
                                src={kitImage}
                                alt={kitName}
                                className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="text-center px-4">
                                <Package className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                                <p className="text-xs text-gray-500">No Image</p>
                              </div>
                            )}
                            {k.status === 'generated' && (
                              <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                                ✓ Generated
                              </div>
                            )}
                            {(k.hasTechPack || k.techPack || k.techPackGeneration?.status === 'completed') && (
                              <div className="absolute top-2 left-2 inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-md">
                                <FileText className="h-2.5 w-2.5" />
                                Tech Pack
                              </div>
                            )}
                          </div>
                          <div className="p-3">
                            <h4 className="font-semibold text-gray-800 truncate text-sm mb-1">
                              {kitName}
                            </h4>
                            <p className="text-xs text-gray-500">
                              {new Date(k.createdAt || k.dateGenerated).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="p-3 sm:p-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 text-sm sm:text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Kit loaded state - show editing interface
  const kitName =
    kit.name || (kit.productDetails?.name ? `${kit.productDetails.name} Kit` : 'Untitled Kit');

  // Note: originalMainImage, mainImage, handleMagicEdit, and useEffect are already declared above before the conditional return

  const activeAnnotation = activeAnnotationId
    ? annotations.find(a => a.id === activeAnnotationId)
    : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm overflow-hidden lg:overflow-y-auto lg:overflow-hidden">
      {showTour && (
        <FashDeckStudioTour
          onClose={() => setShowTour(false)}
          onComplete={() => setShowTour(false)}
        />
      )}
      <div className={`w-full flex flex-col bg-white lg:overflow-hidden ${mobileEditorOpen ? 'h-[100dvh] lg:h-full lg:max-h-[100dvh]' : 'h-[100dvh] overflow-hidden lg:min-h-full lg:h-full lg:max-h-[100dvh]'}`}>
        {/* Header — hidden on mobile while design editor page is open */}
        <div className={`shrink-0 bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 text-white px-3 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] sm:px-6 sm:pb-4 sm:pt-[max(1rem,env(safe-area-inset-top))] lg:pb-5 lg:pt-[max(1.25rem,env(safe-area-inset-top))] flex items-center justify-between shadow-xl border-b border-purple-500/20 ${mobileEditorOpen ? 'hidden lg:flex' : 'flex'}`}>
          <div className="flex min-w-0 items-center gap-2 sm:gap-4">
            <button
              onClick={onBack}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-all hover:bg-white/20 active:scale-95"
              title="Go back"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div className="flex min-w-0 items-center gap-2 sm:gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/30 bg-white/20 shadow-lg backdrop-blur-sm sm:h-12 sm:w-12 sm:rounded-xl">
                <Wand2 className="h-4 w-4 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-base font-bold tracking-tight sm:text-2xl md:text-3xl">FashDeck Studio</h1>
                <p className="hidden text-xs text-purple-100/90 font-medium sm:block sm:text-sm">Create & Edit Designs</p>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1 sm:gap-2">
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowPromptModal(true)}
                disabled={!kit}
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 bg-white/15 px-2 py-1.5 text-white transition-all hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm sm:font-medium sm:shadow-md sm:hover:shadow-lg"
                title="View the full generation prompt for this kit"
              >
                <FileText className="h-4 w-4 sm:h-4 sm:w-4" />
                <span className="hidden sm:inline">View prompt</span>
              </button>
            )}
            <button
              onClick={() => setShowImportModal(true)}
              data-tour="import-button"
              className="inline-flex items-center gap-1.5 rounded-lg border border-white/20 bg-white/15 px-2 py-1.5 text-xs font-medium text-white transition-all hover:bg-white/25 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm sm:shadow-md sm:hover:shadow-lg"
            >
              <Package className="h-4 w-4" />
              <span className="hidden sm:inline">Import Kit</span>
              <span className="sm:hidden">Import</span>
            </button>
            <button
              onClick={handleClearKit}
              className="inline-flex items-center rounded-lg border border-white/20 bg-white/15 p-1.5 text-white transition-all hover:bg-white/25 sm:gap-2 sm:px-4 sm:py-2 sm:text-sm sm:font-medium sm:shadow-md sm:hover:shadow-lg"
              title="Clear kit"
            >
              <X className="h-4 w-4" />
              <span className="hidden sm:inline">Clear</span>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 lg:overflow-hidden">
          <div className="mx-auto flex min-h-0 w-full max-w-7xl flex-1 flex-col px-4 py-3 sm:px-8 sm:py-6 lg:h-full lg:flex-row lg:gap-6 lg:px-8 lg:py-6">
            {/* Center: Preview Section — hidden on mobile while editor page is open */}
            <div
              className={`flex min-h-0 flex-1 flex-col overflow-hidden pb-24 lg:overflow-visible lg:pb-0 ${mobileEditorOpen ? 'hidden lg:flex' : 'flex'} ${isEditorPanelCollapsed ? 'lg:mx-auto lg:max-w-4xl' : ''}`}
            >
              {/* Annotate & Edit (desktop only) — apply an AI magic edit using the
                  annotations/sketches drawn on the preview. Only shown once the
                  user has added at least one annotation or drawing. */}
              {(annotations.length > 0 || strokes.length > 0 || currentStroke) && (
                <div className="hidden lg:block px-3 sm:px-4 md:px-6 pb-3 sm:pb-4">
                  <button
                    type="button"
                    onClick={handleMagicEditWithAnnotations}
                    disabled={isProcessingAnnotatedImage || (annotations.length === 0 && strokes.length === 0 && !currentStroke)}
                    className="w-full px-4 py-2.5 rounded-lg text-sm font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-md hover:shadow-lg"
                  >
                    {isProcessingAnnotatedImage ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-4 h-4" />
                        Apply Magic Edit
                      </>
                    )}
                  </button>
                </div>
              )}

              {/* Preview Container — scrollable on mobile so versions + actions are never clipped */}
              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden rounded-2xl border border-purple-100 bg-white/90 p-3 shadow-xl sm:p-4 md:p-6 lg:overflow-hidden">
              <div
                ref={previewContainerRef}
                  className={`relative flex h-[min(42dvh,24rem)] shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-purple-50 to-indigo-50 transition-all duration-300 lg:h-auto lg:min-h-0 lg:flex-1 ${
                    isAnnotationModeEnabled ? 'cursor-crosshair' : 'cursor-default'
                  }`}
                style={{ touchAction: isAnnotationModeEnabled ? 'none' : undefined }}
                onClick={handlePreviewClick}
                onMouseDown={handlePreviewPointerDown}
                onMouseMove={handlePreviewPointerMove}
                onMouseUp={handlePreviewPointerUp}
                onMouseLeave={handlePreviewPointerLeave}
                onTouchStart={handlePreviewPointerDown}
                onTouchMove={handlePreviewPointerMove}
                onTouchEnd={handlePreviewPointerUp}
                title={isAnnotationModeEnabled ? "Click anywhere on the preview to add an annotation" : "Click the annotation icon to enable annotation mode"}
              >
                {/* Annotate & Edit toggle — desktop only */}
                {!isAnnotationModeEnabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAnnotationModeEnabled(true);
                      openPreviewFullscreen();
                      showNotification?.('Annotation mode enabled. Draw or tap on the enlarged image to add annotations.', 'info');
                    }}
                    className="absolute top-4 right-4 z-20 p-3 bg-white/90 hover:bg-white border-2 border-purple-300 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 hidden lg:flex items-center gap-2 group"
                    title="Enable annotation mode"
                  >
                    <Edit3 className="w-5 h-5 text-purple-600 group-hover:text-purple-700" />
                    <span className="text-sm font-semibold text-purple-700">Annotate</span>
                  </button>
                )}
                
                {/* Annotation mode active indicator — desktop only */}
                {isAnnotationModeEnabled && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAnnotationModeEnabled(false);
                      // Clear any pending annotation or drawing
                      setPendingAnnotation(null);
                      setCurrentStroke(null);
                      setIsDrawing(false);
                      showNotification?.('Annotation mode disabled', 'info');
                    }}
                    className="absolute top-4 right-4 z-20 p-3 bg-purple-600 hover:bg-purple-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 active:scale-95 hidden lg:flex items-center gap-2 group"
                    title="Disable annotation mode"
                  >
                    <Edit3 className="w-5 h-5" />
                    <span className="text-sm font-semibold">Annotating</span>
                    <X className="w-4 h-4 ml-1" />
                  </button>
                )}
                {mainImage ? (
                  <>
                    <div className="relative w-full h-full min-h-0 flex items-center justify-center p-3 sm:p-4 box-border">
                    <img
                      src={mainImage}
                      alt={kitName}
                        className="max-w-full max-h-full w-auto h-auto object-contain rounded-lg shadow-md pointer-events-none transition-all duration-300"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        setIsMagicEditPreviewLoading(false);
                      }}
                      onLoad={() => setIsMagicEditPreviewLoading(false)}
                    />
                    <FashdeckPlanWatermark show={showFashdeckWatermark} />
                    </div>

                    {/* Bottom-left overlay: input fabric image */}
                    {(() => {
                      const { fabricSrc, fabricName } = studioFabricDisplay;

                      if (!fabricSrc) return null;

                      return (
                        <div className="absolute bottom-3 left-3 z-30 flex items-end gap-2 pointer-events-none">
                          <div className="bg-white/90 backdrop-blur-sm border border-purple-200 shadow-lg rounded-xl overflow-hidden">
                            <div className="px-2 py-1 text-[10px] font-semibold text-purple-800 bg-gradient-to-r from-purple-50 to-pink-50 border-b border-purple-100">
                              Input fabric
                            </div>
                            <img
                              src={fabricSrc}
                              alt={fabricName}
                              className="w-20 h-20 sm:w-24 sm:h-24 object-cover"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Loading overlay when magic edit is in progress */}
                    {(isMagicEditing || isMagicEditPreviewLoading) && (
                      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm rounded-xl flex items-center justify-center z-10">
                        <div className="bg-white/90 rounded-xl p-4 sm:p-6 flex flex-col items-center gap-3 shadow-lg">
                          <Loader2 className="w-8 h-8 sm:w-10 sm:h-10 animate-spin text-purple-600" />
                          <p className="text-xs sm:text-sm font-semibold text-gray-800">
                            Applying Magic Edit...
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-600 text-center">
                            This may take a few moments
                          </p>
                        </div>
                      </div>
                    )}

                  </>
                ) : (
                  <div className="text-center px-4 py-6 sm:py-8">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 mx-auto mb-3 rounded-2xl bg-gradient-to-br from-purple-200 to-pink-200 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 sm:w-7 sm:h-7 text-purple-700" />
                    </div>
                    <p className="text-xs sm:text-sm font-medium text-gray-700">
                      No image available for this kit
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      You can still edit the content and settings in Studio.
                    </p>
                    {/* Bottom-left overlay placeholder (helps debug missing fabric src) */}
                    <div className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-purple-200 bg-white/90 text-[11px] text-purple-800 shadow-sm">
                      Input fabric: not found (no fabric image URL available)
                    </div>
                  </div>
                )}
                {/* Annotation pins */}
                {annotations.map((annotation, index) => (
                  <button
                    key={annotation.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveAnnotationId(prev =>
                        prev === annotation.id ? null : annotation.id
                      );
                    }}
                    className={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center shadow-md border ${
                      activeAnnotationId === annotation.id
                        ? 'bg-white text-purple-700 border-purple-600'
                        : 'bg-purple-600 text-white border-white'
                    }`}
                    style={{
                      left: `${annotation.x}%`,
                      top: `${annotation.y}%`
                    }}
                  >
                    {index + 1}
                  </button>
                ))}
                {/* Sketch overlay */}
                {(strokes.length > 0 || currentStroke) && (
                  <div className="absolute inset-0 pointer-events-none">
                    <svg
                      className="w-full h-full"
                      viewBox="0 0 100 100"
                      preserveAspectRatio="none"
                    >
                      {strokes.map((stroke, idx) => (
                        <path
                          // eslint-disable-next-line react/no-array-index-key
                          key={idx}
                          d={buildSmoothPath(stroke)}
                          fill="none"
                          stroke="rgba(79, 70, 229, 0.9)"
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      ))}
                      {currentStroke && (
                        <path
                          d={buildSmoothPath(currentStroke)}
                          fill="none"
                          stroke="rgba(147, 51, 234, 0.9)"
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      )}
                    </svg>
                  </div>
                )}
                {(isMagicEditing || isMagicEditPreviewLoading) && (
                  <div className="absolute inset-0 rounded-xl bg-white/70 backdrop-blur-[1px] flex items-center justify-center z-10">
                    <div className="inline-flex items-center px-3 py-2 rounded-lg bg-white shadow-sm border border-purple-100 text-purple-700 text-xs sm:text-sm font-semibold">
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {isMagicEditing ? 'Applying Magic Edit...' : 'Loading preview...'}
                    </div>
                  </div>
                )}
                {activeAnnotation && !isPreviewFullscreenOpen && (
                  <div
                    className="absolute max-w-[70%] sm:max-w-[60%] bg-white/95 border border-purple-200 shadow-lg rounded-xl px-3 py-2 text-[11px] text-gray-800"
                    style={{
                      left: `${Math.min(activeAnnotation.x + 6, 92)}%`,
                      top: `${Math.max(activeAnnotation.y - 6, 8)}%`,
                      transform: 'translate(-50%, -50%)'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <span className="text-purple-700 font-semibold mr-1">
                        #{annotations.findIndex(a => a.id === activeAnnotation.id) + 1}
                      </span>
                      <p className="flex-1 break-words">{activeAnnotation.text}</p>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-gray-600 ml-1 flex-shrink-0"
                        onClick={() => setActiveAnnotationId(null)}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                    {activeAnnotation.fabricImageUrl && (
                      <div className="mt-2">
                        <img
                          src={activeAnnotation.fabricImageUrl}
                          alt="Fabric reference"
                          className="w-full h-20 object-cover rounded-lg border border-gray-200"
                        />
                      </div>
                    )}
                  </div>
                )}
                {/* New annotation editor (inline) — hidden while the fullscreen
                    annotation surface is open to avoid a duplicate editor window */}
                {pendingAnnotation && !isPreviewFullscreenOpen && (
                  <div
                    className="absolute left-3 right-3"
                    style={{
                      // Smart positioning: if click is in bottom 55% of container, position above; otherwise below
                      // This ensures the box never gets clipped at the bottom
                      top: pendingAnnotation.y > 55 
                        ? 'auto' 
                        : `${Math.max(0.5, Math.min(pendingAnnotation.y, 45))}%`,
                      bottom: pendingAnnotation.y > 55 
                        ? `${Math.max(0.5, Math.min(100 - pendingAnnotation.y, 45))}%` 
                        : 'auto',
                      maxHeight: 'calc(100% - 1rem)',
                      maxWidth: 'calc(100% - 1.5rem)',
                      zIndex: 1000
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onTouchStart={(e) => e.stopPropagation()}
                  >
                    <div className="bg-white rounded-xl shadow-lg border border-purple-100 p-3 space-y-2 text-xs" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-semibold text-gray-800">
                          Add annotation
                        </p>
                        <span className="text-[10px] text-gray-500">
                          Saved as pin #{annotations.length + 1}
                        </span>
                      </div>
                      <textarea
                        ref={annotationTextareaRef}
                        rows={2}
                        autoFocus
                        className="w-full resize-none rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        placeholder="Describe what to change or review here..."
                        value={annotationDraft}
                        onChange={(e) => setAnnotationDraft(e.target.value)}
                      />
                      
                      {/* Fabric Image Attachment */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="text-[10px] font-medium text-gray-700">
                            Attach Fabric Image (Optional)
                          </label>
                          {annotationFabricImageUrl && (
                            <button
                              type="button"
                              onClick={handleRemoveFabricImage}
                              className="text-[10px] text-red-600 hover:text-red-700"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                        {annotationFabricImageUrl ? (
                          <div className="relative">
                            <img
                              src={annotationFabricImageUrl}
                              alt="Fabric preview"
                              className="w-full h-24 object-cover rounded-lg border border-gray-200"
                            />
                            {isUploadingFabricImage && (
                              <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                                <Loader2 className="w-4 h-4 animate-spin text-white" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="flex flex-col gap-2">
                            <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 transition-colors">
                              <Upload className="w-3.5 h-3.5 text-gray-400" />
                              <span className="text-[10px] text-gray-600">
                                {isUploadingFabricImage ? 'Uploading...' : 'Upload Fabric Image'}
                              </span>
                              <input
                                ref={annotationFabricInputRef}
                                type="file"
                                accept="image/*"
                                onChange={handleFabricImageSelect}
                                className="hidden"
                                disabled={isUploadingFabricImage}
                              />
                            </label>
                            <button
                              type="button"
                              onClick={() => {
                                // Check if mobile (screen width < 768px)
                                const isMobile = window.innerWidth < 768;
                                if (isMobile && setActiveView) {
                                  // Store callback and context in sessionStorage for the fabric library page
                                  sessionStorage.setItem('fabricLibraryCallback', JSON.stringify({
                                    type: 'annotation'
                                  }));
                                  setActiveView('fabric-library');
                                } else {
                                  setShowAnnotationFabricLibrary(true);
                                }
                              }}
                              className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-[10px] text-purple-700 font-medium"
                            >
                              <Palette className="w-3.5 h-3.5" />
                              Browse Fabric Library
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-600 hover:bg-gray-100"
                          onClick={() => {
                            setPendingAnnotation(null);
                            setAnnotationDraft('');
                            handleRemoveFabricImage();
                            // Remove the current stroke/drawing when annotation is cancelled
                            if (currentStroke) {
                              setCurrentStroke(null);
                            }
                            // Remove the last completed stroke (the one that triggered the annotation)
                            if (strokes.length > 0) {
                              setStrokes(prev => prev.slice(0, -1));
                            }
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
                          onClick={handleSaveAnnotation}
                          disabled={!annotationDraft.trim()}
                        >
                          Save
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {magicEditImages.length > 0 && (
                <DesignVersionsPanel
                  variant="strip"
                  originalMainImage={originalMainImage}
                  magicEditImages={magicEditImages}
                  selectedImageIndex={selectedImageIndex}
                  setSelectedImageIndex={setSelectedImageIndex}
                  onDeleteVersion={handleDeleteDesignVersion}
                />
              )}

              {/* Custom AI prompt — compact on mobile preview */}
              {mainImage && kit?._id && (kit?.generatedImageUrl || kit?.productDetails?.image) && (
                <div
                  className="mt-2 shrink-0 space-y-2 border-t border-purple-100/90 pt-3 pb-1 sm:mt-3 sm:pt-4 lg:space-y-2"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <label htmlFor="studio-preview-generic-ai-prompt" className="block text-xs font-semibold text-gray-800">
                    Custom AI edit
                  </label>
                  {/* Prompt box with the "Apply AI edit" action docked inside it,
                      chat-input style. */}
                  <div className="rounded-xl border border-gray-200 bg-white transition-shadow focus-within:border-transparent focus-within:ring-2 focus-within:ring-purple-500">
                    <textarea
                      id="studio-preview-generic-ai-prompt"
                      value={genericAiEditPrompt}
                      onChange={(e) => setGenericAiEditPrompt(e.target.value)}
                      placeholder="Describe any change (background, styling, colors, drape…)"
                      rows={2}
                      className="w-full max-h-28 min-h-[56px] resize-none rounded-t-xl border-0 bg-transparent px-3 pt-2 pb-1 text-sm focus:outline-none focus:ring-0 lg:min-h-[72px] lg:max-h-40 lg:resize-y"
                    />
                    <div className="flex justify-end px-2 pb-2">
                      <button
                        type="button"
                        disabled={
                          isApplyingGenericAiEdit ||
                          !genericAiEditPrompt.trim()
                        }
                        onClick={handleApplyGenericAiEdit}
                        className={`inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                          isApplyingGenericAiEdit || !genericAiEditPrompt.trim()
                            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm hover:from-purple-700 hover:to-pink-700'
                        }`}
                      >
                        {isApplyingGenericAiEdit ? (
                          <>
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Applying…
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-3.5 h-3.5" />
                            Apply AI edit
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <button
                    type="button"
                    disabled={
                      isGeneratingBackView ||
                      isApplyingGenericAiEdit ||
                      isEnhancingTexture ||
                      !kit?._id ||
                      !(kit?.generatedImageUrl || kit?.productDetails?.image) ||
                      !mainImage
                    }
                    onClick={handleGenerateBackView}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                      isGeneratingBackView ||
                      isApplyingGenericAiEdit ||
                      isEnhancingTexture ||
                      !kit?._id ||
                      !(kit?.generatedImageUrl || kit?.productDetails?.image) ||
                      !mainImage
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                    }`}
                    title="Generate a back-side view of this outfit"
                  >
                    {isGeneratingBackView ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating back view…
                      </>
                    ) : (
                      <>
                        <Layers className="w-4 h-4" />
                        Generate back side view
                      </>
                    )}
                  </button>
                  {/* Pattern resizer — takes the slot formerly used by Enhance texture.
                      Available to all users. */}
                  <button
                    type="button"
                    disabled={
                      isGeneratingBackView ||
                      isApplyingGenericAiEdit ||
                      isEnhancingTexture ||
                      isResizingPattern ||
                      isReapplyingFabric ||
                      isChangingFabric ||
                      !mainImage
                    }
                    onClick={() => setShowPatternResizer(true)}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                      isGeneratingBackView ||
                      isApplyingGenericAiEdit ||
                      isEnhancingTexture ||
                      isResizingPattern ||
                      isReapplyingFabric ||
                      isChangingFabric ||
                      !mainImage
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                    }`}
                    title="Compare kit with fabric and resize pattern scale"
                  >
                    <Scale className="w-4 h-4" />
                    Pattern resizer
                  </button>

                  <button
                    type="button"
                    disabled={
                      isGeneratingBackView ||
                      isApplyingGenericAiEdit ||
                      isEnhancingTexture ||
                      isResizingPattern ||
                      isReapplyingFabric ||
                      isChangingFabric ||
                      !mainImage ||
                      !studioFabricDisplay?.fabricSrc
                    }
                    onClick={handleReapplyFabric}
                    className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                      isGeneratingBackView ||
                      isApplyingGenericAiEdit ||
                      isEnhancingTexture ||
                      isResizingPattern ||
                      isReapplyingFabric ||
                      isChangingFabric ||
                      !mainImage ||
                      !studioFabricDisplay?.fabricSrc
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-white border border-purple-200 text-purple-700 hover:bg-purple-50'
                    }`}
                    title="Reapply this kit's fabric to the generated garment"
                  >
                    {isReapplyingFabric ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Reapplying fabric…
                      </>
                    ) : (
                      <>
                        <RotateCcw className="w-4 h-4" />
                        Reapply fabric
                      </>
                    )}
                  </button>

                  {/* Create a fashion video of this concept (Google Veo) */}
                  <div className="mt-1 space-y-2 border-t border-purple-100/90 pt-3">
                    <label htmlFor="studio-fashion-video-prompt" className="block text-xs font-semibold text-gray-800">
                      Fashion video
                    </label>
                    <textarea
                      id="studio-fashion-video-prompt"
                      value={fashionVideoPrompt}
                      onChange={(e) => setFashionVideoPrompt(e.target.value)}
                      placeholder="Describe the video (camera movement, mood, setting…). Leave blank for a cinematic runway walk."
                      rows={2}
                      className="w-full max-h-28 min-h-[56px] resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-purple-500 lg:min-h-[72px] lg:max-h-40 lg:resize-y"
                    />
                    <button
                      type="button"
                      disabled={
                        isGeneratingFashionVideo ||
                        !kit?._id ||
                        !(kit?.generatedImageUrl || kit?.productDetails?.image) ||
                        !mainImage
                      }
                      onClick={handleGenerateFashionVideo}
                      className={`w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                        isGeneratingFashionVideo ||
                        !kit?._id ||
                        !(kit?.generatedImageUrl || kit?.productDetails?.image) ||
                        !mainImage
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white shadow-sm hover:from-fuchsia-700 hover:to-indigo-700'
                      }`}
                      title="Create a fashion video of this concept"
                    >
                      {isGeneratingFashionVideo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating video… (this can take a few minutes)
                        </>
                      ) : (
                        <>
                          <Film className="w-4 h-4" />
                          Create fashion video
                        </>
                      )}
                    </button>

                    {fashionVideoUrl && !isGeneratingFashionVideo && (
                      <button
                        type="button"
                        onClick={() => setShowFashionVideoModal(true)}
                        className="w-full py-2 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 bg-white border border-indigo-200 text-indigo-700 hover:bg-indigo-50 transition-all"
                      >
                        <Play className="w-4 h-4" />
                        View latest video
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
            </div>

            {/* Right: Design Versions — desktop sidebar only (mobile uses overlay on preview) */}
            {magicEditImages.length > 0 && (
              <div className="hidden lg:contents">
                <DesignVersionsPanel
                  variant="sidebar"
                  originalMainImage={originalMainImage}
                  magicEditImages={magicEditImages}
                  selectedImageIndex={selectedImageIndex}
                  setSelectedImageIndex={setSelectedImageIndex}
                  onDeleteVersion={handleDeleteDesignVersion}
                  isEditorPanelCollapsed={isEditorPanelCollapsed}
                />
              </div>
            )}

          {/* Editor panel — desktop sidebar; mobile = dedicated full-screen page */}
          <div
            className={[
              'flex flex-col bg-white transition-all duration-300',
              mobileEditorOpen
                ? 'fixed inset-0 z-[55] h-[100dvh] flex flex-col bg-white lg:relative lg:inset-auto lg:z-auto lg:h-auto lg:bg-white/90'
                : 'hidden lg:flex',
              'lg:sticky lg:top-6 lg:mb-0 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:overscroll-y-contain lg:rounded-2xl lg:border lg:border-purple-100 lg:shadow-xl',
              isEditorPanelCollapsed
                ? 'lg:w-0 lg:opacity-0 lg:pointer-events-none lg:overflow-hidden'
                : 'lg:w-96 lg:flex-shrink-0 lg:opacity-100',
            ].join(' ')}
          >
            {/* Mobile editor page header */}
            <div className="flex shrink-0 items-center gap-1.5 border-b border-purple-500/20 bg-gradient-to-r from-purple-600 via-purple-700 to-blue-600 px-2 pb-2 pt-[max(0.5rem,env(safe-area-inset-top))] text-white lg:hidden">
              <button
                type="button"
                onClick={() => setMobileEditorOpen(false)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition hover:bg-white/20 active:scale-95"
                aria-label="Back to preview"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
              <div className="min-w-0 flex-1">
                <p className="truncate text-base font-bold leading-tight">Design Editor</p>
                <p className="truncate text-[11px] text-purple-100">Customise neckline, sleeves & fabrics</p>
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/20">
                <Edit3 className="h-4 w-4" />
              </div>
            </div>

            {/* Toggle Button - Inside panel for collapsing (desktop) */}
            {!isEditorPanelCollapsed && (
              <button
                onClick={() => setIsEditorPanelCollapsed(true)}
                className="absolute top-4 -right-4 z-50 hidden h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-lg transition-all duration-300 hover:scale-110 hover:shadow-xl lg:flex"
                aria-label="Collapse editor panel"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {!studioEditorEnabled && (
              <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-3 py-2.5 sm:px-4">
                <button
                  type="button"
                  onClick={() => setActiveView?.('pricing')}
                  className="inline-flex w-full items-center justify-center gap-1.5 rounded-full bg-amber-400 px-3 py-2 text-xs font-bold text-amber-950 shadow-sm ring-1 ring-amber-500/40 transition hover:bg-amber-300 sm:text-sm"
                >
                  Available on Basic and Advance plans
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
            {!studioEditorEnabled && (
              <style>{`
                .studio-editor-locked button,
                .studio-editor-locked a,
                .studio-editor-locked input,
                .studio-editor-locked select,
                .studio-editor-locked textarea,
                .studio-editor-locked label {
                  pointer-events: none !important;
                }
              `}</style>
            )}
            <div
              className={`flex min-h-0 flex-1 flex-col overflow-hidden ${studioEditorEnabled ? '' : 'studio-editor-locked cursor-not-allowed'}`}
              onKeyDownCapture={(event) => {
                if (studioEditorEnabled) return;
                const tag = event.target?.tagName;
                if (tag === 'BUTTON' || tag === 'A' || tag === 'INPUT' || tag === 'SELECT' || tag === 'TEXTAREA') {
                  event.preventDefault();
                  event.stopPropagation();
                }
              }}
            >
            {/* Main Tab Selector */}
            <div className="hidden shrink-0 border-b border-purple-50 p-3 sm:p-4 lg:block">
              <div className="mb-3 flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium uppercase tracking-wide text-purple-500">
                    FashDeck Studio Editor
                  </p>
                  <p className="mt-1 text-xs text-gray-600 sm:text-sm">
                    Customize your design
                  </p>
                </div>
                <div className="ml-2 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg sm:h-9 sm:w-9">
                  <Edit3 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                </div>
              </div>
              <div className="flex flex-nowrap gap-1.5 sm:gap-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0 pb-0.5 [scrollbar-width:thin] [-webkit-overflow-scrolling:touch]">
                <button
                  onClick={() => setActiveEditorTab('design')}
                  className={`shrink-0 min-w-[5.5rem] sm:min-w-[100px] px-2.5 sm:px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeEditorTab === 'design'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Shirt className="w-3.5 h-3.5 inline mr-1 sm:mr-1.5 align-text-bottom" />
                  <span className="hidden sm:inline">Design Editor</span>
                  <span className="sm:hidden">Design</span>
                </button>
                <button
                  onClick={() => setActiveEditorTab('multifabric')}
                  className={`shrink-0 min-w-[5.5rem] sm:min-w-[100px] px-2.5 sm:px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeEditorTab === 'multifabric'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Grid3x3 className="w-3.5 h-3.5 inline mr-1 sm:mr-1.5 align-text-bottom" />
                  <span className="hidden sm:inline">Multi-Fabric</span>
                  <span className="sm:hidden">Multi</span>
                </button>
                <button
                  onClick={() => setActiveEditorTab('embellishment')}
                  className={`shrink-0 min-w-[5.5rem] sm:min-w-[100px] px-2.5 sm:px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeEditorTab === 'embellishment'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5 inline mr-1 sm:mr-1.5 align-text-bottom" />
                  <span className="hidden sm:inline">Embellishments</span>
                  <span className="sm:hidden">Add-ons</span>
                </button>
                <button
                  onClick={() => {
                    setActiveEditorTab('change-fabric');
                    setShowChangeFabricModal(true);
                  }}
                  className={`shrink-0 min-w-[5.5rem] sm:min-w-[100px] px-2.5 sm:px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeEditorTab === 'change-fabric'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Change the fabric reference for this Studio design"
                >
                  <Palette className="w-3.5 h-3.5 inline mr-1 sm:mr-1.5 align-text-bottom" />
                  <span className="hidden sm:inline">Change fabric</span>
                  <span className="sm:hidden">Fabric</span>
                </button>
                <button
                  onClick={() => setActiveEditorTab('enhance-texture')}
                  className={`shrink-0 min-w-[5.5rem] sm:min-w-[100px] px-2.5 sm:px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeEditorTab === 'enhance-texture'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Match the garment fabric to the source fabric image"
                >
                  <Gem className="w-3.5 h-3.5 inline mr-1 sm:mr-1.5 align-text-bottom" />
                  <span className="hidden sm:inline">Enhance Texture</span>
                  <span className="sm:hidden">Texture</span>
                </button>
                <button
                  onClick={() => setActiveEditorTab('techpack')}
                  className={`shrink-0 min-w-[5.5rem] sm:min-w-[100px] px-2.5 sm:px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                    activeEditorTab === 'techpack'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  title="Generate an AI tech pack from this design"
                >
                  <FileText className="w-3.5 h-3.5 inline mr-1 sm:mr-1.5 align-text-bottom" />
                  <span className="hidden sm:inline">Tech Pack</span>
                  <span className="sm:hidden">Tech</span>
                </button>
              </div>
            </div>

            {/* Mobile tab row (compact) */}
            <div className="shrink-0 border-b border-purple-50 px-3 py-2 lg:hidden">
              <div className="flex flex-nowrap gap-1.5 overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch] [scrollbar-width:none]">
                <button
                  onClick={() => setActiveEditorTab('design')}
                  className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap ${
                    activeEditorTab === 'design'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Design
                </button>
                <button
                  onClick={() => setActiveEditorTab('multifabric')}
                  className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap ${
                    activeEditorTab === 'multifabric'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Multi
                </button>
                <button
                  onClick={() => setActiveEditorTab('embellishment')}
                  className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap ${
                    activeEditorTab === 'embellishment'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Add-ons
                </button>
                <button
                  onClick={() => {
                    setActiveEditorTab('change-fabric');
                    setShowChangeFabricModal(true);
                  }}
                  className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap ${
                    activeEditorTab === 'change-fabric'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Fabric
                </button>
                <button
                  onClick={() => setActiveEditorTab('enhance-texture')}
                  className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap ${
                    activeEditorTab === 'enhance-texture'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Texture
                </button>
                <button
                  onClick={() => setActiveEditorTab('techpack')}
                  className={`shrink-0 rounded-lg px-3 py-2 text-xs font-semibold whitespace-nowrap ${
                    activeEditorTab === 'techpack'
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  Tech
                </button>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain [-webkit-overflow-scrolling:touch]">
            {/* Design Editor Content */}
            {activeEditorTab === 'design' && (
              <div data-tour="design-editor" className="flex flex-col lg:min-h-0 lg:flex-1 lg:overflow-hidden">
                <DesignEditorSection
                  designSettings={designSettings}
                  onSettingsChange={setDesignSettings}
                  onStyleClick={setLastClickedStyle}
                  showNotification={showNotification}
                  kit={kit}
                  mainImage={mainImage}
                  originalMainImage={originalMainImage}
                  setIsMagicEditing={setIsMagicEditing}
                  setIsMagicEditPreviewLoading={setIsMagicEditPreviewLoading}
                  setMagicEditImages={setMagicEditImages}
                  setSelectedImageIndex={setSelectedImageIndex}
                  magicEditImages={magicEditImages}
                  selectedImageIndex={selectedImageIndex}
                  studioFabricProduct={studioFabricProduct}
                  studioFabricProductLoading={studioFabricProductLoading}
                  isMobileEditorPage={mobileEditorOpen}
                  onExitMobileEditor={() => setMobileEditorOpen(false)}
                />
              </div>
            )}

            {/* Multi-Fabric Content */}
            {activeEditorTab === 'multifabric' && (
              <MultiFabricSection
                settings={multiFabricSettings}
                onSettingsChange={setMultiFabricSettings}
                showNotification={showNotification}
                kit={kit}
                mainImage={mainImage}
                originalMainImage={originalMainImage}
                setIsMagicEditing={setIsMagicEditing}
                setIsMagicEditPreviewLoading={setIsMagicEditPreviewLoading}
                setMagicEditImages={setMagicEditImages}
                setSelectedImageIndex={setSelectedImageIndex}
                setActiveView={setActiveView}
              />
            )}

            {/* Embellishment Content */}
            {activeEditorTab === 'embellishment' && (
              <>
                {/* Tabs for different embellishment types */}
                <div className="p-3 sm:p-4 border-b border-purple-50">
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
                    {[
                      { id: 'embroidery', label: 'Embroidery', shortLabel: 'Embroidery', icon: Scissors },
                      { id: 'sequins', label: 'Sequins & Beads', shortLabel: 'Sequins', icon: Gem },
                      { id: 'lace', label: 'Lace & Borders', shortLabel: 'Lace', icon: Layers },
                      { id: 'applique', label: 'Digital Appliqué', shortLabel: 'Appliqué', icon: FileImage }
                    ].map((tab) => {
                      const Icon = tab.icon;
                      const isActive = activeEmbellishmentTab === tab.id;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => setActiveEmbellishmentTab(tab.id)}
                          className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                            isActive
                              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                          <span className="hidden sm:inline">{tab.label}</span>
                          <span className="sm:hidden">{tab.shortLabel}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Scrollable Content Area - includes content sections and prompt box */}
                <div className="lg:overflow-y-auto lg:[max-height:calc(100vh-350px)]">
                  {/* Content for active tab */}
                  <div className="p-4 sm:p-6">
                    {activeEmbellishmentTab === 'embroidery' && (
                      <EmbroiderySection
                        items={embellishments.embroidery}
                        onAdd={(item) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            embroidery: [...prev.embroidery, item]
                          }));
                          showNotification?.('Embroidery motif added', 'success');
                        }}
                        onRemove={(index) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            embroidery: prev.embroidery.filter((_, i) => i !== index)
                          }));
                          showNotification?.('Embroidery motif removed', 'info');
                        }}
                      />
                    )}

                    {activeEmbellishmentTab === 'sequins' && (
                      <SequinsSection
                        items={embellishments.sequins}
                        onAdd={(item) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            sequins: [...prev.sequins, item]
                          }));
                          showNotification?.('Sequins/beads added', 'success');
                        }}
                        onRemove={(index) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            sequins: prev.sequins.filter((_, i) => i !== index)
                          }));
                          showNotification?.('Sequins/beads removed', 'info');
                        }}
                      />
                    )}

                    {activeEmbellishmentTab === 'lace' && (
                      <LaceSection
                        items={embellishments.lace}
                        onAdd={(item) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            lace: [...prev.lace, item]
                          }));
                          showNotification?.('Lace/border added', 'success');
                        }}
                        onRemove={(index) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            lace: prev.lace.filter((_, i) => i !== index)
                          }));
                          showNotification?.('Lace/border removed', 'info');
                        }}
                      />
                    )}

                    {activeEmbellishmentTab === 'applique' && (
                      <AppliqueSection
                        items={embellishments.applique}
                        onAdd={(item) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            applique: [...prev.applique, item]
                          }));
                          showNotification?.('Digital appliqué added', 'success');
                        }}
                        onRemove={(index) => {
                          setEmbellishments(prev => ({
                            ...prev,
                            applique: prev.applique.filter((_, i) => i !== index)
                          }));
                          showNotification?.('Digital appliqué removed', 'info');
                        }}
                      />
                    )}
                  </div>

                  {/* Embellishment Prompt Box and Apply Button */}
                  <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50">
                  <div className="space-y-4">
                    {/* Prompt Input */}
                    <div className="space-y-2">
                      <label className="text-sm font-semibold text-gray-800 block">
                        Embellishment Instructions
                      </label>
                      <textarea
                        value={embellishmentPrompt}
                        onChange={(e) => setEmbellishmentPrompt(e.target.value)}
                        placeholder="Describe how you want the embellishments applied (e.g., 'Add intricate embroidery on the sleeves and neckline', 'Apply sequins in a floral pattern on the bodice', 'Add lace borders along the hem and cuffs'). Leave empty to use default placement."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                        rows={4}
                      />
                      <p className="text-xs text-gray-500">
                        {buildEmbellishmentPrompt() ? `Selected: ${buildEmbellishmentPrompt()}` : 'Add embellishments above or describe your desired embellishments here'}
                      </p>
                    </div>

                    {/* Apply Button */}
                    <button
                      onClick={async () => {
                        if (!kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)) {
                          showNotification?.('Please ensure a kit is imported and image is available', 'error');
                          return;
                        }

                        const hasEmbellishments = 
                          embellishments.embroidery.length > 0 ||
                          embellishments.sequins.length > 0 ||
                          embellishments.lace.length > 0 ||
                          embellishments.applique.length > 0;

                        if (!hasEmbellishments && !embellishmentPrompt.trim()) {
                          showNotification?.('Please add embellishments or enter instructions', 'error');
                          return;
                        }

                        setIsApplyingEmbellishment(true);
                        setIsMagicEditing?.(true);
                        setIsMagicEditPreviewLoading?.(true);

                        try {
                          const token = localStorage.getItem('token');
                          if (!token) {
                            showNotification?.('Authentication required. Please log in.', 'error');
                            return;
                          }

                          // Build prompt from embellishments and user input
                          let prompt = buildEmbellishmentPrompt();
                          
                          if (embellishmentPrompt.trim()) {
                            if (prompt) {
                              prompt += embellishmentPrompt.trim();
                            } else {
                              prompt = embellishmentPrompt.trim();
                            }
                          }

                          // If no prompt was built, use a default
                          if (!prompt.trim()) {
                            prompt = 'Add elegant embellishments to the garment while maintaining its design integrity.';
                          }

                          // Get current main image (could be edited version)
                          const currentMainImage = selectedImageIndex === -1 
                            ? (kit?.generatedImageUrl || kit?.productDetails?.image)
                            : (magicEditImages[selectedImageIndex] || kit?.generatedImageUrl || kit?.productDetails?.image);
                          
                          const currentOriginalImage = kit?.productDetails?.image || kit?.generatedImageUrl;

                          // Upload embellishment images to S3 and collect URLs
                          let embellishmentImageUrl = null;
                          const allEmbellishments = [
                            ...embellishments.embroidery,
                            ...embellishments.sequins,
                            ...embellishments.lace,
                            ...embellishments.applique
                          ];

                          if (allEmbellishments.length > 0) {
                            try {
                              // Get the first embellishment with a file (prioritize uploaded files over blob URLs)
                              const firstEmbellishment = allEmbellishments.find(item => item.file) || allEmbellishments[0];
                              
                              if (firstEmbellishment) {
                                // If it has a file, upload it to S3
                                if (firstEmbellishment.file) {
                                  showNotification?.('Uploading embellishment image...', 'info');
                                  const uploadResult = await uploadImageWithFallback(firstEmbellishment.file, {
                                    folder: 'embellishments',
                                    fileName: `embellishment_${Date.now()}_${Math.random().toString(36).substring(2, 9)}.jpg`
                                  });
                                  
                                  if (uploadResult && uploadResult.success && uploadResult.url) {
                                    embellishmentImageUrl = uploadResult.url;
                                  } else if (uploadResult && typeof uploadResult === 'string') {
                                    // Fallback: if uploadImageWithFallback returns a string URL directly
                                    embellishmentImageUrl = uploadResult;
                                  } else if (firstEmbellishment.imageUrl && !firstEmbellishment.imageUrl.startsWith('blob:')) {
                                    // If imageUrl is already a valid URL (not a blob), use it directly
                                    embellishmentImageUrl = firstEmbellishment.imageUrl;
                                  }
                                } else if (firstEmbellishment.imageUrl && !firstEmbellishment.imageUrl.startsWith('blob:')) {
                                  // If no file but imageUrl is a valid URL (not a blob), use it directly
                                  embellishmentImageUrl = firstEmbellishment.imageUrl;
                                } else if (firstEmbellishment.imageUrl && firstEmbellishment.imageUrl.startsWith('blob:')) {
                                  // If it's a blob URL, we need to convert it to a file first
                                  try {
                                    const response = await fetch(firstEmbellishment.imageUrl);
                                    const blob = await response.blob();
                                    const file = new File([blob], firstEmbellishment.name || 'embellishment.jpg', { type: blob.type });
                                    
                                    showNotification?.('Uploading embellishment image...', 'info');
                                    const uploadResult = await uploadImageWithFallback(file, {
                                      folder: 'embellishments',
                                      fileName: `embellishment_${Date.now()}_${Math.random().toString(36).substring(2, 9)}.jpg`
                                    });
                                    
                                    if (uploadResult && uploadResult.success && uploadResult.url) {
                                      embellishmentImageUrl = uploadResult.url;
                                    } else if (uploadResult && typeof uploadResult === 'string') {
                                      embellishmentImageUrl = uploadResult;
                                    }
                                  } catch (blobError) {
                                    console.error('Error converting blob URL to file:', blobError);
                                  }
                                }
                              }
                            } catch (uploadError) {
                              console.error('Error uploading embellishment image:', uploadError);
                              showNotification?.('Warning: Could not upload embellishment image, proceeding without it', 'warning');
                            }
                          }

                          const response = await magicEditV2Api({
                            imageUrl: currentMainImage,
                            originalImageUrl: currentOriginalImage,
                            prompt: prompt,
                            originalKitId: kit._id,
                            createNewKit: true,
                            fabricImageUrl: embellishmentImageUrl, // Pass embellishment image URL
                            aspectRatio: "match_input_image"
                          }, token);

                          if (response.data?.success) {
                            const editedUrl = response.data.data?.editedImageUrl;
                            if (editedUrl) {
                              // Add new edited image to the array and select it
                              setMagicEditImages?.(prev => {
                                const newImages = appendCappedUrls(prev, editedUrl, 20);
                                // Select the newly created edit (it will be at the last index)
                                setSelectedImageIndex?.(newImages.length - 1);
                                return newImages;
                              });
                              showNotification?.('Embellishments applied successfully!', 'success');
                            } else {
                              showNotification?.('Magic edit completed but no image was returned', 'warning');
                            }
                          } else {
                            showNotification?.(response.data?.message || 'Failed to apply embellishments. Please try again.', 'error');
                          }
                        } catch (error) {
                          console.error('Embellishment magic edit error:', error);
                          showNotification?.(error.response?.data?.message || 'Failed to apply embellishments. Please try again.', 'error');
                        } finally {
                          setIsApplyingEmbellishment(false);
                          setIsMagicEditing?.(false);
                          setIsMagicEditPreviewLoading?.(false);
                        }
                      }}
                      disabled={isApplyingEmbellishment || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)}
                      className={`w-full px-4 py-3 rounded-xl font-semibold text-sm transition-all flex items-center justify-center gap-2 ${
                        isApplyingEmbellishment || !kit?._id || !(kit?.generatedImageUrl || kit?.productDetails?.image)
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-md hover:shadow-lg'
                      }`}
                    >
                      {isApplyingEmbellishment ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Applying Embellishments...</span>
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-4 h-4" />
                          <span>Apply Embellishments</span>
                        </>
                      )}
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      This will apply the selected embellishments to the garment using AI magic edit
                    </p>
                  </div>
                </div>
                </div>
              </>
            )}

            {/* Change Fabric Content (opens modal; content kept minimal) */}
            {activeEditorTab === 'change-fabric' && (
              <div className="p-4 sm:p-6">
                <div className="bg-white border border-purple-100 rounded-2xl p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white flex-shrink-0">
                      <Palette className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800">Change fabric</p>
                      <p className="text-xs text-gray-600 mt-1">
                        Pick a fabric from My Fabrics. Studio will regenerate this design with the new fabric (same as the inspiration apply-fabric flow).
                      </p>
                      {isChangingFabric && (
                        <div className="mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-purple-50 text-purple-700 border border-purple-100 text-sm font-semibold">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Applying fabric…
                        </div>
                      )}
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowChangeFabricModal(true)}
                          disabled={isChangingFabric}
                          className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
                            isChangingFabric
                              ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                              : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                          }`}
                        >
                          Choose fabric
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowChangeFabricModal(false);
                            setChangeFabricSearchTerm('');
                            setActiveEditorTab('design');
                          }}
                          disabled={isChangingFabric}
                          className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all"
                        >
                          Back to editor
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeEditorTab === 'enhance-texture' && (
              <div className="p-4 sm:p-6 space-y-4">
                <div className="bg-white border border-purple-100 rounded-2xl p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white flex-shrink-0">
                      <Gem className="w-5 h-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-800">Enhance texture</p>
                      <p className="text-xs text-gray-600 mt-1">
                        Rebuild the garment so its fabric matches the source swatch — weave, print, color, and grain.
                      </p>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-purple-700 mb-1.5">Kit image</p>
                      <div className="aspect-square rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50 to-indigo-50 overflow-hidden flex items-center justify-center">
                        {mainImage ? (
                          <img src={mainImage} alt={kitName} className="w-full h-full object-contain" />
                        ) : (
                          <ImageIcon className="w-8 h-8 text-gray-300" />
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-purple-700 mb-1.5">Fabric image</p>
                      <div className="aspect-square rounded-xl border border-purple-100 bg-gray-100 overflow-hidden flex items-center justify-center">
                        {textureFabricUrl ? (
                          <img src={textureFabricUrl} alt={studioFabricDisplay.fabricName || 'Fabric'} className="w-full h-full object-cover" />
                        ) : (
                          <div className="text-center px-2">
                            <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-1" />
                            <p className="text-[10px] text-gray-500">No fabric linked</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <input
                    ref={textureFabricInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleUploadTextureFabric}
                  />

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => textureFabricInputRef.current?.click()}
                      disabled={isUploadingTextureFabric || isEnhancingTexture}
                      className="px-3 py-2 rounded-xl text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-all disabled:opacity-50"
                    >
                      {isUploadingTextureFabric ? 'Uploading…' : textureFabricUrl ? 'Replace fabric' : 'Upload fabric'}
                    </button>
                  </div>

                  <button
                    type="button"
                    onClick={handleEnhanceTexture}
                    disabled={
                      isEnhancingTexture ||
                      isMagicEditing ||
                      !kit?._id ||
                      !mainImage ||
                      !textureFabricUrl
                    }
                    className={`mt-4 w-full py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all ${
                      isEnhancingTexture ||
                      isMagicEditing ||
                      !kit?._id ||
                      !mainImage ||
                      !textureFabricUrl
                        ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 shadow-sm'
                    }`}
                  >
                    {isEnhancingTexture ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enhancing texture…
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Enhance texture
                      </>
                    )}
                  </button>
                  {!textureFabricUrl && (
                    <p className="mt-2 text-[11px] text-gray-500">
                      Upload a fabric swatch or pick one from your library first.
                    </p>
                  )}
                </div>
              </div>
            )}

            {activeEditorTab === 'techpack' && (
              <TechPackPanel
                kit={kit}
                mainImage={mainImage}
                showNotification={showNotification}
                refreshKits={refreshKits}
              />
            )}
            </div>
            </div>
            </div>
          </div>

          {/* Mobile: open dedicated design editor page */}
          {!mobileEditorOpen && kit?._id && (
            <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 bg-gradient-to-t from-white via-white/95 to-transparent px-4 pt-6 pb-[max(1rem,env(safe-area-inset-bottom))] lg:hidden">
              <button
                type="button"
                onClick={() => setMobileEditorOpen(true)}
                className="pointer-events-auto flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 py-3.5 text-sm font-semibold text-white shadow-lg transition active:scale-[0.98] hover:from-purple-700 hover:to-pink-700"
              >
                <Edit3 className="h-4 w-4" />
                Customize Design
              </button>
            </div>
          )}
          
          {/* Floating Toggle Button - Shows when panel is collapsed */}
          {isEditorPanelCollapsed && (
            <button
              onClick={() => setIsEditorPanelCollapsed(false)}
              className="hidden lg:flex fixed left-4 top-1/2 -translate-y-1/2 z-40 w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white shadow-lg hover:shadow-xl transition-all duration-300 items-center justify-center border-2 border-white hover:scale-110"
              aria-label="Expand editor panel"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          )}
        </div>
          </div>
      </div>

      {/* Annotation Fabric Library Modal — above mobile editor (z-[55]) */}
      {showAnnotationFabricLibrary && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">Select Fabric for Annotation</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">Choose from your uploaded fabrics</p>
              </div>
              <button
                onClick={() => {
                  setShowAnnotationFabricLibrary(false);
                  setAnnotationFabricSearchTerm('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-3 sm:p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search fabrics..."
                  value={annotationFabricSearchTerm}
                  onChange={(e) => setAnnotationFabricSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div
              ref={annotationScrollContainerRef}
              className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6"
            >
              {isLoadingAnnotationFabrics && annotationFabrics.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : annotationFabrics.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Palette className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm sm:text-base text-gray-600">
                    {annotationFabricSearchTerm ? 'No fabrics found matching your search' : 'No fabrics available'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {annotationFabrics.map((fabric) => (
                      <FabricItem
                        key={fabric._id}
                        fabric={fabric}
                        onSelect={handleSelectAnnotationFabric}
                      />
                    ))}
                  </div>

                  {hasMoreAnnotationFabrics && (
                    <div
                      ref={annotationLoadMoreRef}
                      className="flex items-center justify-center py-4"
                    >
                      {isLoadingMoreAnnotationFabrics && (
                        <div className="flex items-center gap-2 text-purple-600">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm">Loading more fabrics...</span>
                        </div>
                      )}
                    </div>
                  )}

                  {!hasMoreAnnotationFabrics && annotationFabrics.length > 0 && (
                    <div className="text-center py-4">
                      <p className="text-xs text-gray-500">All fabrics loaded</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Change Fabric Modal (My Fabrics) — above mobile editor (z-[55]) */}
      {showChangeFabricModal && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">Change fabric</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Choose from My Fabrics — applies fabric to this garment design
                </p>
              </div>
              <button
                onClick={() => {
                  setShowChangeFabricModal(false);
                  setChangeFabricSearchTerm('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                disabled={isChangingFabric}
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="p-3 sm:p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search fabrics..."
                  value={changeFabricSearchTerm}
                  onChange={(e) => setChangeFabricSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            <div
              ref={changeFabricScrollContainerRef}
              className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6"
            >
              {isLoadingChangeFabricCandidates && changeFabricCandidates.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : changeFabricCandidates.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Palette className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm sm:text-base text-gray-600">
                    {changeFabricSearchTerm ? 'No fabrics found matching your search' : 'No fabrics available'}
                  </p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
                    {changeFabricCandidates.map((fabric) => (
                      <FabricItem
                        key={fabric._id}
                        fabric={fabric}
                        onSelect={handleSelectNewStudioFabric}
                      />
                    ))}
                  </div>

                  {hasMoreChangeFabricCandidates && (
                    <div
                      ref={changeFabricLoadMoreRef}
                      className="flex items-center justify-center py-4"
                    >
                      {(isLoadingMoreChangeFabricCandidates || isChangingFabric) && (
                        <div className="flex items-center gap-2 text-purple-600">
                          <Loader2 className="w-5 h-5 animate-spin" />
                          <span className="text-sm">
                            {isChangingFabric ? 'Applying fabric to design…' : 'Loading more fabrics...'}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {!hasMoreChangeFabricCandidates && changeFabricCandidates.length > 0 && (
                    <div className="text-center py-4">
                      <p className="text-xs text-gray-500">All fabrics loaded</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Kit Modal (when kit is loaded) */}
      {showImportModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-200 gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">Import Kit to Studio</h3>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Select a kit from your collection to import
                </p>
              </div>
              <button
                onClick={() => setShowImportModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Search Bar */}
            <div className="p-3 sm:p-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search kits..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 sm:pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Kits Grid */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6">
              {isLoadingKits ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : filteredKits.length === 0 ? (
                <div className="text-center py-8 sm:py-12">
                  <Package className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3" />
                  <p className="text-sm sm:text-base text-gray-600">
                    {searchTerm ? 'No kits found matching your search' : 'No kits available'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                  {filteredKits.map((k) => {
                    const kitName = k.name || k.productDetails?.name || 'Untitled Kit';
                    const kitImage =
                      k.generatedImageUrl ||
                      k.productDetails?.image ||
                      k.generatedContent?.['Image Posts (Social Media)']?.[0]?.image;

                    return (
                      <div
                        key={k._id}
                        onClick={() => handleImportKit(k)}
                        className="group bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer"
                      >
                        <div className="aspect-[3/4] bg-gradient-to-br from-purple-50 to-indigo-50 flex items-center justify-center relative overflow-hidden">
                          {kitImage ? (
                            <img
                              src={kitImage}
                              alt={kitName}
                              className="w-full h-full object-contain group-hover:scale-105 transition-transform"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="text-center px-4">
                              <Package className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                              <p className="text-xs text-gray-500">No Image</p>
                            </div>
                          )}
                          {k.status === 'generated' && (
                            <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-semibold">
                              ✓ Generated
                            </div>
                          )}
                          {(k.hasTechPack || k.techPack || k.techPackGeneration?.status === 'completed') && (
                            <div className="absolute top-2 left-2 inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-md">
                              <FileText className="h-2.5 w-2.5" />
                              Tech Pack
                            </div>
                          )}
                        </div>
                        <div className="p-3">
                          <h4 className="font-semibold text-gray-800 truncate text-sm mb-1">
                            {kitName}
                          </h4>
                          <p className="text-xs text-gray-500">
                            {new Date(k.createdAt || k.dateGenerated).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-3 sm:p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowImportModal(false)}
                className="px-4 py-2 text-sm sm:text-base text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
        )}

      <PatternResizerModal
        isOpen={showPatternResizer}
        onClose={() => setShowPatternResizer(false)}
        kitImageUrl={mainImage}
        kitName={kitName}
        fabricImageUrl={studioFabricDisplay.fabricSrc}
        fabricName={studioFabricDisplay.fabricName}
        fabricSize={studioFabricDisplay.fabricSize}
        onApply={handleApplyPatternResize}
        isApplying={isResizingPattern}
      />

      <ReapplyFabricReviewModal
        isOpen={isReapplyingFabric || Boolean(reapplyFabricReview)}
        isGenerating={isReapplyingFabric}
        beforeUrl={reapplyFabricReview?.beforeUrl || mainImage}
        afterUrl={reapplyFabricReview?.afterUrl || null}
        fabricName={studioFabricDisplay?.fabricName || 'Fabric'}
        onAccept={handleAcceptReapplyFabric}
        onReject={handleRejectReapplyFabric}
      />

      {/* Admin-only: Full Prompt Modal */}
      {isAdmin && showPromptModal && (
        <div
          className="fixed inset-0 z-[90] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-label="Full prompt"
          onClick={() => setShowPromptModal(false)}
        >
          <div
            className="relative w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div className="flex items-center gap-2 text-base font-semibold text-gray-800">
                <FileText className="w-5 h-5 text-gray-700" />
                Full Prompt
                <span className="text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full">Admin</span>
              </div>
              <button
                type="button"
                onClick={() => setShowPromptModal(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-5">
              <p className="text-sm text-gray-500 -mt-1">{kit?.name || kitName}</p>

              {(() => {
                // Flatten product-type customization icon references (the "Image 2+"
                // style references passed to the model from the generate wizard).
                const customizationRefs = [];
                const ptcr = kit?.productDetails?.productTypeCustomizationReferences;
                if (ptcr && typeof ptcr === 'object') {
                  Object.entries(ptcr).forEach(([attrKey, arr]) => {
                    if (!Array.isArray(arr)) return;
                    arr.forEach((item) => {
                      const url = item?.referenceImageUrl || item?.referenceImage;
                      if (url) {
                        customizationRefs.push({
                          label: item?.option ? `${item.option} (${attrKey})` : attrKey,
                          url
                        });
                      }
                    });
                  });
                }

                const refImages = [
                  { label: 'Image 1 — Fabric / Source', url: kit?.productDetails?.image },
                  { label: 'Original (pre-edit)', url: kit?.productDetails?.originalImage || kit?.magicEditData?.originalImageUrl },
                  { label: 'Inspirational / Body-cut', url: kit?.productDetails?.inspirationalDesignImage },
                  { label: 'Model', url: kit?.modelImage },
                  { label: 'Scene', url: kit?.sceneImage || kit?.productDetails?.sceneImage },
                  { label: kit?.magicEditData?.customizationType ? `${kit.magicEditData.customizationType} Reference` : 'Customization Reference', url: kit?.magicEditData?.referenceImageUrl },
                  { label: 'Fabric Reference (edit)', url: kit?.magicEditData?.fabricImageUrl },
                  ...customizationRefs,
                ].filter((r) => r.url && String(r.url).trim());

                if (refImages.length === 0) return null;

                return (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">
                      Reference Images ({refImages.length})
                    </label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {refImages.map((ref) => (
                        <a
                          key={ref.label}
                          href={ref.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="group text-left"
                          title={`Open ${ref.label}`}
                        >
                          <div className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
                            <img
                              src={String(ref.url).startsWith('http') ? toThumbnailUrl(ref.url) : ref.url}
                              alt={ref.label}
                              className="absolute inset-0 w-full h-full object-contain group-hover:scale-105 transition-transform"
                              loading="lazy"
                              onError={(e) => { if (e.target.src !== ref.url) e.target.src = ref.url; else e.target.src = 'https://placehold.co/200x200/ffe4f2/ede9fe?text=N/A'; }}
                            />
                          </div>
                          <p className="mt-1 text-[11px] font-medium text-gray-600 truncate">{ref.label}</p>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const sections = [
                  { label: 'Image Prompt', value: kit?.imagePrompt },
                  { label: 'Video Prompt', value: kit?.videoPrompt },
                  { label: 'Environment Prompt', value: kit?.advancedSettings?.environmentPrompt },
                  { label: 'User Instruction (magic edit)', value: kit?.magicEditData?.userPrompt },
                  { label: 'Magic Edit Prompt (sent to model)', value: kit?.magicEditData?.prompt },
                ].filter((s) => s.value && String(s.value).trim());

                if (sections.length === 0) {
                  return (
                    <p className="text-sm text-gray-500 italic">No prompt is stored for this kit.</p>
                  );
                }

                return sections.map((section) => (
                  <div key={section.label}>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide">
                        {section.label}
                      </label>
                      <button
                        type="button"
                        onClick={() => navigator.clipboard?.writeText(String(section.value))}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium"
                      >
                        <Copy className="w-3 h-3" />
                        Copy
                      </button>
                    </div>
                    <pre className="whitespace-pre-wrap break-words text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono">
                      {section.value}
                    </pre>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Fashion video result modal */}
      {showFashionVideoModal && fashionVideoUrl && (
        <div
          className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          role="dialog"
          aria-label="Fashion video"
          onClick={() => setShowFashionVideoModal(false)}
        >
          <div
            className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                <Film className="w-4 h-4 text-fuchsia-600" />
                Fashion video
              </div>
              <button
                type="button"
                onClick={() => setShowFashionVideoModal(false)}
                className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <video
              src={fashionVideoUrl}
              className="w-full h-auto max-h-[70vh] bg-black object-contain"
              controls
              autoPlay
              muted
              loop
              playsInline
              poster={mainImage}
            />
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100">
              <a
                href={fashionVideoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-fuchsia-600 to-indigo-600 text-white hover:from-fuchsia-700 hover:to-indigo-700"
              >
                Open in new tab
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen preview modal */}
      {isPreviewFullscreenOpen && mainImage && (
        <div
          className="fixed inset-0 z-[70] bg-black/90 backdrop-blur-sm flex flex-col"
          role="dialog"
          aria-label="Image preview"
        >
          {/* Top toolbar */}
          <div className="flex items-center justify-between gap-2 p-3 sm:p-4">
            <div className="flex items-center gap-2">
              {isAnnotationModeEnabled ? (
                <span className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-3 py-2 text-xs sm:text-sm font-semibold text-white shadow-md">
                  <Edit3 className="h-4 w-4" />
                  <span className="hidden sm:inline">Annotating — draw or tap on the image</span>
                  <span className="sm:hidden">Annotating</span>
                </span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      resetPreviewView();
                      setIsAnnotationModeEnabled(true);
                      showNotification?.('Annotation mode enabled. Draw or tap on the image to add annotations.', 'info');
                    }}
                    className="inline-flex items-center gap-2 rounded-xl border-2 border-purple-300 bg-white/90 px-3 py-2 text-xs sm:text-sm font-semibold text-purple-700 hover:bg-white"
                  >
                    <Edit3 className="h-4 w-4" />
                    Annotate
                  </button>
                  <span className="hidden sm:inline text-xs text-white/60">
                    Pinch or scroll to zoom · drag to pan
                  </span>
                  <span className="sm:hidden text-[11px] text-white/60">
                    Pinch to zoom
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!isAnnotationModeEnabled && (
                <div className="flex items-center gap-1 rounded-xl border border-white/20 bg-white/10 p-1">
                  <button
                    type="button"
                    onClick={handlePreviewZoomOut}
                    disabled={previewZoom <= 1}
                    className="w-9 h-9 rounded-lg text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Zoom out"
                    title="Zoom out (-)"
                  >
                    <ZoomOut className="h-4 w-4" />
                  </button>
                  <span className="min-w-[3rem] text-center text-xs font-semibold text-white tabular-nums">
                    {Math.round(previewZoom * 100)}%
                  </span>
                  <button
                    type="button"
                    onClick={handlePreviewZoomIn}
                    disabled={previewZoom >= 5}
                    className="w-9 h-9 rounded-lg text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Zoom in"
                    title="Zoom in (+)"
                  >
                    <ZoomIn className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={resetPreviewView}
                    disabled={previewZoom <= 1 && previewPan.x === 0 && previewPan.y === 0}
                    className="w-9 h-9 rounded-lg text-white hover:bg-white/20 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                    aria-label="Reset zoom"
                    title="Reset view (0)"
                  >
                    <Maximize2 className="h-4 w-4" />
                  </button>
                </div>
              )}
              {isAnnotationModeEnabled && (annotations.length > 0 || strokes.length > 0 || currentStroke) && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setAnnotations([]);
                      setStrokes([]);
                      setCurrentStroke(null);
                      setPendingAnnotation(null);
                      setActiveAnnotationId(null);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-white/20"
                  >
                    <RotateCcw className="h-4 w-4" />
                    <span className="hidden sm:inline">Clear</span>
                  </button>
                  <button
                    type="button"
                    onClick={handleMagicEditWithAnnotations}
                    disabled={isProcessingAnnotatedImage}
                    className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 px-4 py-2 text-xs sm:text-sm font-semibold text-white shadow-md hover:from-purple-700 hover:to-pink-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isProcessingAnnotatedImage ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Wand2 className="h-4 w-4" />
                    )}
                    Apply Magic Edit
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  setPendingAnnotation(null);
                  setCurrentStroke(null);
                  setIsDrawing(false);
                  closePreviewFullscreen();
                }}
                className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 text-white flex items-center justify-center border border-white/20"
                aria-label="Close full screen preview"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Image annotation surface */}
          <div
            className="relative flex flex-1 min-h-0 items-center justify-center overflow-hidden p-3 sm:p-6"
            onClick={(e) => {
              // Clicking the dark backdrop (outside the image) closes only when not annotating
              if (!isAnnotationModeEnabled && e.target === e.currentTarget) {
                closePreviewFullscreen();
              }
            }}
            onWheel={handleFullscreenWheel}
          >
            <div
              ref={fullscreenPreviewRef}
              className={`relative flex max-h-full max-w-full items-center justify-center ${
                isAnnotationModeEnabled
                  ? 'cursor-crosshair'
                  : previewZoom > 1
                    ? previewIsPanning || previewIsPinching
                      ? 'cursor-grabbing'
                      : 'cursor-grab'
                    : 'cursor-zoom-in'
              }`}
              style={{
                // Always none so mobile pinch isn't stolen by the browser.
                touchAction: 'none',
                transform: `translate(${previewPan.x}px, ${previewPan.y}px) scale(${previewZoom})`,
                transformOrigin: 'center center',
                transition: previewIsPanning || previewIsPinching ? 'none' : 'transform 0.15s ease-out',
                willChange: 'transform',
              }}
              onClick={handleFullscreenClick}
              onMouseDown={handleFullscreenPointerDown}
              onMouseMove={handleFullscreenPointerMove}
              onMouseUp={handleFullscreenPointerUp}
              onMouseLeave={handleFullscreenPointerLeave}
              onTouchStart={handleFullscreenPointerDown}
              onTouchMove={handleFullscreenPointerMove}
              onTouchEnd={handleFullscreenPointerUp}
              onTouchCancel={handleFullscreenPointerUp}
            >
              <img
                src={mainImage}
                alt={kitName || 'Preview'}
                className="max-w-[96vw] max-h-[80vh] object-contain rounded-xl shadow-2xl pointer-events-none select-none"
                draggable={false}
              />
              <FashdeckPlanWatermark show={showFashdeckWatermark} />

              {/* Annotation pins */}
              {annotations.map((annotation, index) => (
                <button
                  key={annotation.id}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveAnnotationId(prev => (prev === annotation.id ? null : annotation.id));
                  }}
                  className={`absolute -translate-x-1/2 -translate-y-1/2 w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center shadow-md border ${
                    activeAnnotationId === annotation.id
                      ? 'bg-white text-purple-700 border-purple-600'
                      : 'bg-purple-600 text-white border-white'
                  }`}
                  style={{ left: `${annotation.x}%`, top: `${annotation.y}%` }}
                >
                  {index + 1}
                </button>
              ))}

              {/* Sketch overlay */}
              {(strokes.length > 0 || currentStroke) && (
                <div className="absolute inset-0 pointer-events-none">
                  <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                    {strokes.map((stroke, idx) => (
                      <path
                        // eslint-disable-next-line react/no-array-index-key
                        key={idx}
                        d={buildSmoothPath(stroke)}
                        fill="none"
                        stroke="rgba(79, 70, 229, 0.9)"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    ))}
                    {currentStroke && (
                      <path
                        d={buildSmoothPath(currentStroke)}
                        fill="none"
                        stroke="rgba(147, 51, 234, 0.9)"
                        strokeWidth={1.5}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                  </svg>
                </div>
              )}

              {/* Active annotation note */}
              {activeAnnotation && (
                <div
                  className="absolute max-w-[70%] sm:max-w-[60%] bg-white/95 border border-purple-200 shadow-lg rounded-xl px-3 py-2 text-[11px] text-gray-800"
                  style={{
                    left: `${Math.min(activeAnnotation.x + 6, 92)}%`,
                    top: `${Math.max(activeAnnotation.y - 6, 8)}%`,
                    transform: 'translate(-50%, -50%)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-purple-700 font-semibold mr-1">
                      #{annotations.findIndex(a => a.id === activeAnnotation.id) + 1}
                    </span>
                    <p className="flex-1 break-words">{activeAnnotation.text}</p>
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-600 ml-1 flex-shrink-0"
                      onClick={() => setActiveAnnotationId(null)}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                  {activeAnnotation.fabricImageUrl && (
                    <img
                      src={activeAnnotation.fabricImageUrl}
                      alt="Fabric reference"
                      className="mt-2 w-full h-20 object-cover rounded-lg border border-gray-200"
                    />
                  )}
                </div>
              )}
            </div>

            {/* New annotation editor (fullscreen) */}
            {pendingAnnotation && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 w-[min(92vw,420px)] z-[72]"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onTouchStart={(e) => e.stopPropagation()}
              >
                <div className="bg-white rounded-xl shadow-2xl border border-purple-100 p-3 space-y-2 text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-gray-800">Add annotation</p>
                    <span className="text-[10px] text-gray-500">Saved as pin #{annotations.length + 1}</span>
                  </div>
                  <textarea
                    ref={annotationTextareaRef}
                    rows={2}
                    autoFocus
                    className="w-full resize-none rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Describe what to change or review here..."
                    value={annotationDraft}
                    onChange={(e) => setAnnotationDraft(e.target.value)}
                  />

                  {/* Fabric image attachment */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-medium text-gray-700">Attach Fabric Image (Optional)</label>
                      {annotationFabricImageUrl && (
                        <button
                          type="button"
                          onClick={handleRemoveFabricImage}
                          className="text-[10px] text-red-600 hover:text-red-700"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    {annotationFabricImageUrl ? (
                      <div className="relative">
                        <img
                          src={annotationFabricImageUrl}
                          alt="Fabric preview"
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        {isUploadingFabricImage && (
                          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
                            <Loader2 className="w-4 h-4 animate-spin text-white" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex flex-col gap-2">
                        <label className="flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-400 transition-colors">
                          <Upload className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-[10px] text-gray-600">
                            {isUploadingFabricImage ? 'Uploading...' : 'Upload Fabric Image'}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFabricImageSelect}
                            className="hidden"
                            disabled={isUploadingFabricImage}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowAnnotationFabricLibrary(true)}
                          className="flex items-center justify-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors text-[10px] text-purple-700 font-medium"
                        >
                          <Palette className="w-3.5 h-3.5" />
                          Browse Fabric Library
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      className="px-2.5 py-1.5 rounded-lg text-[11px] text-gray-600 hover:bg-gray-100"
                      onClick={() => {
                        setPendingAnnotation(null);
                        setAnnotationDraft('');
                        handleRemoveFabricImage();
                        if (currentStroke) setCurrentStroke(null);
                        if (strokes.length > 0) setStrokes(prev => prev.slice(0, -1));
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-3 py-1.5 rounded-lg text-[11px] font-semibold bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700 disabled:opacity-60"
                      onClick={handleSaveAnnotation}
                      disabled={!annotationDraft.trim()}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Fancy full-screen loader while a magic edit is in progress */}
      {(isProcessingAnnotatedImage || isMagicEditing || isApplyingGenericAiEdit || isGeneratingBackView || isEnhancingTexture) && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-gradient-to-br from-slate-900/90 via-purple-900/85 to-pink-900/90 backdrop-blur-md">
          <style>{`
            @keyframes studioShimmer {
              0% { transform: translateX(-160%); }
              100% { transform: translateX(260%); }
            }
            @keyframes studioFloat {
              0%, 100% { transform: translateY(0); opacity: 0.55; }
              50% { transform: translateY(-10px); opacity: 1; }
            }
          `}</style>

          <div className="relative flex flex-col items-center gap-6 px-8 text-center">
            {/* Floating sparkles */}
            <Sparkles
              className="absolute -top-6 -left-8 h-5 w-5 text-pink-300"
              style={{ animation: 'studioFloat 2.4s ease-in-out infinite' }}
            />
            <Sparkles
              className="absolute -top-2 right-0 h-4 w-4 text-purple-200"
              style={{ animation: 'studioFloat 3s ease-in-out infinite', animationDelay: '0.4s' }}
            />
            <Gem
              className="absolute top-10 -right-10 h-4 w-4 text-fuchsia-300"
              style={{ animation: 'studioFloat 2.8s ease-in-out infinite', animationDelay: '0.8s' }}
            />

            {/* Animated rings with glowing core */}
            <div className="relative h-32 w-32">
              <span className="absolute inset-0 rounded-full border-4 border-white/10" />
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-t-purple-400 border-r-pink-400 animate-spin"
                style={{ animationDuration: '1.1s' }}
              />
              <span
                className="absolute inset-3 rounded-full border-4 border-transparent border-b-fuchsia-300 border-l-indigo-300 animate-spin"
                style={{ animationDuration: '1.7s', animationDirection: 'reverse' }}
              />
              <span className="absolute inset-7 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 opacity-70 blur-md animate-pulse" />
              <span className="absolute inset-0 flex items-center justify-center">
                <Wand2 className="h-10 w-10 text-white drop-shadow-[0_0_10px_rgba(236,72,153,0.8)] animate-pulse" />
              </span>
            </div>

            {/* Title + animated dots */}
            <div className="space-y-1.5">
              <h3 className="bg-gradient-to-r from-purple-200 via-pink-200 to-indigo-200 bg-clip-text text-2xl font-bold text-transparent">
                {isEnhancingTexture ? 'Enhancing Fabric Texture' : 'Applying Magic Edit'}
              </h3>
              <p className="flex items-center justify-center text-sm text-purple-100/80">
                {isEnhancingTexture
                  ? 'Matching the garment fabric to the source swatch'
                  : 'Crafting your design — this can take a few moments'}
                <span className="ml-1 inline-flex">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </p>
            </div>

            {/* Shimmer progress bar */}
            <div className="h-1.5 w-56 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full w-1/2 rounded-full bg-gradient-to-r from-transparent via-pink-400 to-transparent"
                style={{ animation: 'studioShimmer 1.3s ease-in-out infinite' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Fancy full-screen loader for fashion video generation */}
      {isGeneratingFashionVideo && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-gradient-to-br from-slate-900/95 via-indigo-900/90 to-fuchsia-900/90 backdrop-blur-md">
          <style>{`
            @keyframes veoShimmer {
              0% { transform: translateX(-160%); }
              100% { transform: translateX(260%); }
            }
            @keyframes veoFloat {
              0%, 100% { transform: translateY(0); opacity: 0.55; }
              50% { transform: translateY(-10px); opacity: 1; }
            }
            @keyframes veoReel {
              from { transform: rotate(0deg); }
              to { transform: rotate(360deg); }
            }
          `}</style>

          <div className="relative flex flex-col items-center gap-6 px-8 text-center">
            {/* Floating cinematic accents */}
            <Sparkles
              className="absolute -top-6 -left-8 h-5 w-5 text-fuchsia-300"
              style={{ animation: 'veoFloat 2.4s ease-in-out infinite' }}
            />
            <Play
              className="absolute -top-2 right-0 h-4 w-4 text-indigo-200"
              style={{ animation: 'veoFloat 3s ease-in-out infinite', animationDelay: '0.4s' }}
            />
            <Sparkles
              className="absolute top-10 -right-10 h-4 w-4 text-purple-300"
              style={{ animation: 'veoFloat 2.8s ease-in-out infinite', animationDelay: '0.8s' }}
            />

            {/* Animated rings with glowing film core */}
            <div className="relative h-32 w-32">
              <span className="absolute inset-0 rounded-full border-4 border-white/10" />
              <span
                className="absolute inset-0 rounded-full border-4 border-transparent border-t-indigo-400 border-r-fuchsia-400 animate-spin"
                style={{ animationDuration: '1.1s' }}
              />
              <span
                className="absolute inset-3 rounded-full border-4 border-transparent border-b-purple-300 border-l-sky-300 animate-spin"
                style={{ animationDuration: '1.7s', animationDirection: 'reverse' }}
              />
              <span className="absolute inset-7 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 opacity-70 blur-md animate-pulse" />
              <span className="absolute inset-0 flex items-center justify-center">
                <Film
                  className="h-10 w-10 text-white drop-shadow-[0_0_10px_rgba(168,85,247,0.85)]"
                  style={{ animation: 'veoReel 4s linear infinite' }}
                />
              </span>
            </div>

            {/* Title + rotating phase + animated dots */}
            <div className="space-y-1.5">
              <h3 className="bg-gradient-to-r from-indigo-200 via-fuchsia-200 to-purple-200 bg-clip-text text-2xl font-bold text-transparent">
                Creating your fashion video
              </h3>
              <p className="flex items-center justify-center text-sm text-indigo-100/80">
                {FASHION_VIDEO_PHASES[fashionVideoPhase]}
                <span className="ml-1 inline-flex">
                  <span className="animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
                  <span className="animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
                </span>
              </p>
              <p className="text-xs text-indigo-100/70">
                Cinema-quality takes a moment — this can take a few minutes
              </p>
            </div>

            {/* Shimmer progress bar */}
            <div className="h-1.5 w-56 overflow-hidden rounded-full bg-white/10">
              <div
                className="h-full w-1/2 rounded-full bg-gradient-to-r from-transparent via-fuchsia-400 to-transparent"
                style={{ animation: 'veoShimmer 1.3s ease-in-out infinite' }}
              />
            </div>
          </div>
        </div>
      )}
      </div>
  );
};

export default AiStudioView;
